import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LogOut, LayoutDashboard, BookOpen, ShoppingBag, UtensilsCrossed, Users, Inbox, Settings, Eye, EyeOff, Trash2, Edit, Plus, X, Check, ToggleLeft, ToggleRight, AlertCircle, ChevronDown, Upload, Image as ImageIcon, UserPlus, Link as LinkIcon, Copy, FileText, Download, Send, Star, MessageSquare, Mail, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

// ─── Image Upload Helper ──────────────────────────────────────────────────────
function ImageUpload({ value, onChange, authHeaders, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/image`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onChange(r.data.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = ''; // reset
    }
  };

  return (
    <div>
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        {value ? (
          <img src={value} alt="preview" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} />
        ) : (
          <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mid)', border: '1px dashed var(--border)' }}>
            <ImageIcon size={20} />
          </div>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'var(--cream)', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--ink)', border: '1px solid var(--border)' }}>
          <Upload size={14} />
          {uploading ? 'Uploading...' : (value ? 'Replace' : 'Upload')}
          <input type="file" accept="image/*" onChange={handleFile} disabled={uploading} style={{ display: 'none' }} data-testid="image-upload-input" />
        </label>
        {value && (
          <button type="button" onClick={() => onChange('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)', fontSize: '0.82rem' }} data-testid="image-clear-btn">Clear</button>
        )}
      </div>
      <input className="form-input" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="…or paste image URL (https://…)" style={{ fontSize: '0.85rem' }} />
      {err && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 4 }}>{err}</div>}
    </div>
  );
}


// ─── Shared Report Management Card (used by Diet Reviews + Assessment Reports) ──
// Props:
//   detail: review/submission with report_pdf, report_filename, report_uploaded_at, report_sent_at, email
//   uploading: boolean
//   sending: boolean
//   onUpload(file): async — upload selected PDF file
//   onSend(): async — email the uploaded PDF to client
//   onRemove(): async — delete the uploaded PDF and revert status
//   uploadTestId, sendTestId, removeTestId, sentTestId — optional data-testid overrides
function ReportManagementCard({
  detail,
  uploading,
  sending,
  onUpload,
  onSend,
  onRemove,
  uploadTestId = 'rm-upload-label',
  uploadInputTestId = 'rm-upload-input',
  sendTestId = 'rm-send-report',
  removeTestId = 'rm-remove-report-btn',
  sentTestId = 'rm-sent-timestamp',
  cardTestId = 'report-management',
}) {
  if (!detail) return null;
  const hasUpload = Boolean(detail.report_uploaded_at);
  const sentAt = detail.report_sent_at;

  return (
    <div data-testid={cardTestId} style={{ background: '#fff', border: '1px solid #0F4C5C', borderRadius: 14, padding: '18px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: "'Fraunces',serif", color: '#0F4C5C', fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Report Management</div>

      {hasUpload && (
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: '.85rem', color: '#14532D' }}>
            <FileText size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            <strong>{detail.report_filename || 'Report uploaded'}</strong>
            <span style={{ color: '#166534', marginLeft: 8 }}>· uploaded {new Date(detail.report_uploaded_at).toLocaleString()}</span>
          </div>
          <button onClick={onRemove} data-testid={removeTestId}
            style={{ background: '#fff', color: '#991B1B', border: '1px solid #FCA5A5', padding: '6px 12px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={13} /> Remove
          </button>
        </div>
      )}

      {sentAt && (
        <div data-testid={sentTestId} style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '.85rem', color: '#065F46' }}>
          <Check size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Report sent to <strong>{detail.email}</strong> on {new Date(sentAt).toLocaleString()}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label data-testid={uploadTestId}
          style={{ background: '#fff', color: '#0F4C5C', border: '1px solid #0F4C5C', padding: '8px 14px', borderRadius: 50, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Upload size={14} />
          {uploading ? 'Uploading…' : (hasUpload ? 'Replace PDF' : 'Upload Report PDF')}
          <input type="file" accept="application/pdf" data-testid={uploadInputTestId}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f); }}
            disabled={uploading} style={{ display: 'none' }} />
        </label>

        <button
          data-testid={sendTestId}
          onClick={onSend}
          disabled={sending || !hasUpload}
          title={!hasUpload ? 'Upload a PDF report first' : 'Send the report to the client'}
          aria-disabled={sending || !hasUpload}
          style={{
            background: '#0F4C5C', color: '#fff', border: 'none',
            padding: '9px 18px', borderRadius: 50, fontWeight: 600, fontSize: 13,
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: (sending || !hasUpload) ? 0.55 : 1,
            cursor: (sending || !hasUpload) ? 'not-allowed' : 'pointer',
            transition: 'opacity .2s',
          }}>
          <Send size={14} /> {sending ? 'Sending…' : (sentAt ? 'Resend Report' : 'Send Report to Client')}
        </button>
      </div>

      <p style={{ fontSize: '.75rem', color: '#6B7280', marginTop: 10 }}>
        PDF only · max 6 MB. The email is sent with the practice's branded header and the PDF attached.
      </p>
    </div>
  );
}


// ─── Auth Context ──────────────────────────────────────────────────────────────
function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => { localStorage.removeItem('admin_token'); setToken(null); });
    }
  }, [token]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('admin_token', r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
  };
  const logout = () => { localStorage.removeItem('admin_token'); setToken(null); setUser(null); };
  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  return <AuthCtx.Provider value={{ token, user, login, logout, authHeaders }}>{children}</AuthCtx.Provider>;
}

// ─── Admin Login ──────────────────────────────────────────────────────────────
function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await login(form.email, form.password);
      navigate('/admin/dashboard');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login-page" style={{ minHeight: '100vh', background: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '40px', width: '100%', maxWidth: '400px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontFamily: "'Great Vibes', cursive", fontSize: '2rem', color: 'var(--teal)', marginBottom: '4px' }}>Anjum Sohail</div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.4rem', fontWeight: 800, color: 'var(--ink)' }}>Admin Panel</h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--mid)', marginTop: '4px' }}>Secure login for administrators only</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="admin-login-form">
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>Email</label>
            <input data-testid="admin-email-input" type="email" className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="admin@anjumsohail.com" />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input data-testid="admin-password-input" type={showPwd ? 'text' : 'password'} className="form-input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required placeholder="Password" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)' }}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {err && <div data-testid="admin-login-error" style={{ background: 'var(--coral-lt)', color: 'var(--coral)', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <AlertCircle size={16} />{err}
          </div>}
          <button type="submit" disabled={loading} className="btn-teal" style={{ width: '100%', justifyContent: 'center', display: 'flex' }} data-testid="admin-login-submit">
            {loading ? 'Logging in...' : 'Login to Admin Panel'}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Link to="/" style={{ fontSize: '0.82rem', color: 'var(--mid)', textDecoration: 'none' }}>← Back to Website</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function AdminSidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' },
    { path: '/admin/products', icon: <ShoppingBag size={18} />, label: 'Products' },
    { path: '/admin/orders', icon: <Inbox size={18} />, label: 'Orders' },
    { path: '/admin/recipes', icon: <UtensilsCrossed size={18} />, label: 'Recipes' },
    { path: '/admin/clients', icon: <Users size={18} />, label: 'Clients' },
    { path: '/admin/diet-reviews', icon: <Inbox size={18} />, label: 'Diet Reviews' },
    { path: '/admin/assessment-reports', icon: <FileText size={18} />, label: 'Assessment Reports' },
    { path: '/admin/leads', icon: <UserPlus size={18} />, label: 'Leads' },
    { path: '/admin/testimonials', icon: <MessageSquare size={18} />, label: 'Testimonials' },
    { path: '/admin/invoices', icon: <FileText size={18} />, label: 'Invoices' },
    { path: '/admin/settings', icon: <Settings size={18} />, label: 'Settings' },
  ];

  return (
    <div style={{ width: '220px', flexShrink: 0, minHeight: '100vh', background: 'var(--ink)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontFamily: "'Great Vibes', cursive", fontSize: '1.4rem', color: 'var(--coral)', marginBottom: '4px', lineHeight: 1.1 }}>Anjum Sohail</div>
        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600, lineHeight: 1.2 }}>Admin Panel</div>
      </div>
      <div role="navigation" aria-label="Admin navigation" style={{ flex: 1, padding: '8px 0', width: '100%', boxSizing: 'border-box' }}>
        {nav.map(item => (
          <Link key={item.path} to={item.path}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', textDecoration: 'none', transition: 'background 0.2s', fontSize: '0.88rem', fontWeight: 600, color: location.pathname === item.path ? '#fff' : 'rgba(255,255,255,0.55)', background: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent', width: '100%', boxSizing: 'border-box' }}>
            {item.icon}{item.label}
          </Link>
        ))}
      </div>
      <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>{user?.email}</div>
        <Link to="/" target="_blank" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.8rem', marginBottom: '8px' }}>
          <Eye size={14} />View Website
        </Link>
        <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: 0 }} data-testid="admin-logout-btn">
          <LogOut size={14} />Logout
        </button>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard() {
  const { authHeaders } = useAuth();
  const [stats, setStats] = useState({
    active_clients: 0, total_clients: 0, revenue_this_month: 0,
    pending_diet_reviews: 0, new_leads_this_week: 0, orders_last_7_days: 0,
    low_stock_count: 0, weekly_orders: [],
  });

  useEffect(() => {
    axios.get(`${API}/admin/dashboard-stats`, { headers: authHeaders() })
      .then(r => setStats(r.data))
      .catch(() => {});
  }, []);

  const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

  const cards = [
    { label: 'Active Clients', value: stats.active_clients, sub: `${stats.total_clients} total`, color: '#0F4C5C', icon: '👥', path: '/admin/clients' },
    { label: 'Revenue This Month', value: fmtINR(stats.revenue_this_month), sub: 'Paid orders', color: '#7A9D6B', icon: '💰', path: '/admin/orders' },
    { label: 'Pending Diet Reviews', value: stats.pending_diet_reviews, sub: 'Awaiting report', color: '#E07A5F', icon: '📩', path: '/admin/diet-reviews' },
    { label: 'New Leads This Week', value: stats.new_leads_this_week, sub: 'Last 7 days', color: '#C8A054', icon: '✨', path: '/admin/leads' },
    { label: 'Orders Last 7 Days', value: stats.orders_last_7_days, sub: 'All sources', color: '#1A6A7E', icon: '📦', path: '/admin/orders' },
    { label: 'Out of Stock', value: stats.low_stock_count, sub: 'Supplements', color: stats.low_stock_count > 0 ? '#dc2626' : '#9CA3AF', icon: '⚠️', path: '/admin/products' },
  ];

  // Tiny inline SVG line chart for weekly orders
  const weeks = stats.weekly_orders || [];
  const maxV = Math.max(1, ...weeks.map(w => w.count));
  const W = 480, H = 120, pad = 24;
  const dx = weeks.length > 1 ? (W - 2 * pad) / (weeks.length - 1) : 0;
  const points = weeks.map((w, i) => `${pad + i * dx},${H - pad - (w.count / maxV) * (H - 2 * pad)}`).join(' ');

  return (
    <div data-testid="admin-dashboard" style={{ padding: '28px' }}>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.6rem', color: 'var(--ink)', marginBottom: '8px' }}>Dashboard</h1>
      <p style={{ color: 'var(--mid)', marginBottom: '28px', fontSize: '0.9rem' }}>Welcome back, Anjum. Here's a snapshot of your business.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        {cards.map(c => (
          <Link key={c.label} to={c.path} data-testid="dashboard-stat-card" style={{ background: '#fff', borderRadius: 14, padding: '18px 18px 16px', border: '1px solid var(--border)', textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--mid)' }}>{c.label}</div>
              <div style={{ fontSize: '1.1rem' }}>{c.icon}</div>
            </div>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: '1.85rem', color: c.color, lineHeight: 1.1 }}>{c.value}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--muted)', marginTop: 4 }}>{c.sub}</div>
          </Link>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 18 }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', border: '1px solid var(--border)' }} data-testid="weekly-orders-chart">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1rem', color: 'var(--ink)', margin: 0 }}>Orders per week (last 4 weeks)</h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--mid)' }}>Total: {weeks.reduce((a, w) => a + w.count, 0)}</span>
          </div>
          {weeks.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--mid)', fontSize: '0.85rem' }}>No data yet</div>
          ) : (
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
              <polyline fill="none" stroke="#0F4C5C" strokeWidth="2.5" points={points} />
              {weeks.map((w, i) => {
                const x = pad + i * dx;
                const y = H - pad - (w.count / maxV) * (H - 2 * pad);
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r="4" fill="#E07A5F" />
                    <text x={x} y={y - 8} textAnchor="middle" fontSize="11" fill="#0F4C5C" fontWeight="700">{w.count}</text>
                    <text x={x} y={H - 6} textAnchor="middle" fontSize="10" fill="#7e8d95">{w.week_start}</text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', border: '1px solid var(--border)' }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1rem', color: 'var(--ink)', margin: 0, marginBottom: 12 }}>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Link to="/admin/clients" data-testid="quick-add-client" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--teal-lt)', color: 'var(--teal)', padding: '10px 14px', borderRadius: 10, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}><Plus size={14} />Add Client</Link>
            <Link to="/admin/invoices" data-testid="quick-generate-invoice" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--coral-lt)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 10, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}><Plus size={14} />Generate Invoice</Link>
            <Link to="/admin/leads" data-testid="quick-view-leads" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FEF3C7', color: '#92400E', padding: '10px 14px', borderRadius: 10, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>✨ View New Leads</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inline thumbnail uploader (used in row list) ─────────────────────────────
function InlineThumbUpload({ currentUrl, onUploaded, authHeaders, fallback }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/image`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await onUploaded(r.data.url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div
      onClick={() => !busy && inputRef.current?.click()}
      title="Click to upload / replace cover image"
      style={{
        position: 'relative', width: 56, height: 56, borderRadius: 8,
        overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
        border: '1px solid var(--border)', background: 'var(--cream)',
      }}
      data-testid="inline-thumb-upload"
    >
      <img
        src={currentUrl || fallback}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: busy ? 0.4 : 1 }}
      />
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'rgba(0,0,0,0.45)',
        opacity: busy ? 1 : 0, transition: 'opacity 0.15s', color: '#fff',
        fontSize: 10, fontWeight: 700, gap: 4, flexDirection: 'column',
      }}
        onMouseEnter={(e) => { if (!busy) e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { if (!busy) e.currentTarget.style.opacity = 0; }}
      >
        <Upload size={14} />
        {busy ? 'Uploading…' : 'Replace'}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} disabled={busy} style={{ display: 'none' }} />
    </div>
  );
}

// ─── Blogs Manager ────────────────────────────────────────────────────────────
function BlogsManager() {
  const { authHeaders } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: 'Nutrition Basics', image: '', author: 'Anjum Sohail', read_time: '5 min read', published: true, featured: false });

  const CATS = ['PCOS', 'Gut Health', 'Thyroid', 'Weight Loss', 'Blood Sugar', 'Nutrition Basics', 'Pregnancy'];

  const load = async () => {
    const r = await axios.get(`${API}/blogs`);
    setBlogs(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing) {
      await axios.put(`${API}/blogs/${editing.id}`, form, { headers: authHeaders() });
    } else {
      await axios.post(`${API}/blogs`, form, { headers: authHeaders() });
    }
    setShowForm(false); setEditing(null);
    setForm({ title: '', excerpt: '', content: '', category: 'Nutrition Basics', image: '', author: 'Anjum Sohail', read_time: '5 min read', published: true, featured: false });
    load();
  };

  const del = async (id) => {
    if (window.confirm('Delete this blog?')) {
      await axios.delete(`${API}/blogs/${id}`, { headers: authHeaders() });
      load();
    }
  };

  const updateCover = async (blog, newImageUrl) => {
    // Send full blog object back so other fields are preserved
    const payload = { ...blog, image: newImageUrl };
    delete payload._id;
    await axios.put(`${API}/blogs/${blog.id}`, payload, { headers: authHeaders() });
    load();
  };

  const openEdit = (blog) => {
    setEditing(blog);
    setForm({ title: blog.title, excerpt: blog.excerpt, content: blog.content || '', category: blog.category, image: blog.image || '', author: blog.author, read_time: blog.read_time, published: blog.published, featured: blog.featured || false });
    setShowForm(true);
  };

  return (
    <div data-testid="admin-blogs" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Blog Articles ({blogs.length})</h1>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-teal btn-sm" data-testid="add-blog-btn">
          <Plus size={16} />Add Blog
        </button>
      </div>
      <div style={{ background: 'var(--teal-lt)', color: 'var(--teal)', padding: '10px 14px', borderRadius: 10, fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Upload size={14} /> <strong>Tip:</strong>&nbsp;Click the thumbnail next to any blog to upload / replace its cover image instantly.
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid var(--border)', marginBottom: '24px' }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: '20px', color: 'var(--ink)' }}>{editing ? 'Edit Blog' : 'Add New Blog'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Blog title" /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Excerpt</label><textarea className="form-input" value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} placeholder="Short description" /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Content</label><textarea className="form-input" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={4} placeholder="Full article content" /></div>
            <div><label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="form-label">Read Time</label><input className="form-input" value={form.read_time} onChange={e => setForm(f => ({ ...f, read_time: e.target.value }))} placeholder="5 min read" /></div>
            <div style={{ gridColumn: 'span 2' }}><ImageUpload value={form.image} onChange={url => setForm(f => ({ ...f, image: url }))} authHeaders={authHeaders} label="Image" /></div>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', gridColumn: 'span 2' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} />Published
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} />Featured
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={save} className="btn-teal btn-sm" data-testid="save-blog-btn"><Check size={14} />Save Blog</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>Loading...</div> : (
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden' }}>
          {blogs.map((blog, i) => (
            <div key={blog.id} data-testid="blog-row" style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', borderBottom: i < blogs.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
              <InlineThumbUpload
                currentUrl={blog.image || blog.thumbnail_url}
                onUploaded={(url) => updateCover(blog, url)}
                authHeaders={authHeaders}
                fallback="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=80&q=70"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blog.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--mid)' }}>{blog.category} · {blog.published_date} · {blog.read_time}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {blog.featured && <span className="tag" style={{ fontSize: '0.7rem' }}>Featured</span>}
                <span className={`tag ${blog.published ? 'tag-sage' : 'tag-coral'}`} style={{ fontSize: '0.7rem' }}>{blog.published ? 'Published' : 'Draft'}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => openEdit(blog)} style={{ background: 'var(--teal-lt)', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: 'var(--teal)' }} data-testid="edit-blog-btn"><Edit size={14} /></button>
                <button onClick={() => del(blog.id)} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: 'var(--coral)' }} data-testid="delete-blog-btn"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Multi-Image Gallery Upload (max 11) ──────────────────────────────────────
function MultiImageUpload({ value, onChange, authHeaders, max = 11 }) {
  const images = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const slots = max - images.length;
    if (slots <= 0) { setErr(`Maximum ${max} images reached. Remove one to add another.`); e.target.value = ''; return; }
    setErr(''); setUploading(true);
    try {
      const next = [...images];
      for (const file of files.slice(0, slots)) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload/image`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        next.push(r.data.url);
      }
      onChange(next);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAt = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  return (
    <div>
      <label className="form-label">Product Image Gallery ({images.length}/{max})</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
        {images.map((url, idx) => (
          <div key={idx} style={{ position: 'relative', width: 88, height: 88, borderRadius: 10, overflow: 'hidden', border: idx === 0 ? '2px solid var(--teal)' : '1px solid var(--border)' }}>
            <img src={url} alt={`gallery ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {idx === 0 && (
              <div style={{ position: 'absolute', top: 4, left: 4, background: 'var(--teal)', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>COVER</div>
            )}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', background: 'rgba(0,0,0,.55)' }}>
              {idx > 0 && (
                <button type="button" onClick={() => moveUp(idx)} title="Move up" style={{ flex: 1, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '3px 0', fontSize: 10 }} data-testid={`gallery-move-up-${idx}`}>↑</button>
              )}
              <button type="button" onClick={() => removeAt(idx)} title="Remove" style={{ flex: 1, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '3px 0', fontSize: 10 }} data-testid={`gallery-remove-${idx}`}>✕</button>
            </div>
          </div>
        ))}
        {images.length < max && (
          <label style={{ width: 88, height: 88, border: '1px dashed var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--cream)', gap: 4, color: 'var(--mid)' }}>
            <Upload size={18} />
            <span style={{ fontSize: 10, fontWeight: 600 }}>{uploading ? 'Uploading…' : 'Add'}</span>
            <input type="file" accept="image/*" multiple onChange={handleFiles} disabled={uploading} style={{ display: 'none' }} data-testid="gallery-upload-input" />
          </label>
        )}
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: 4 }}>{err}</div>}
      <div style={{ fontSize: 11, color: 'var(--mid)' }}>First image is the cover thumbnail on the public shop page. Click ↑ to reorder.</div>
    </div>
  );
}

// ─── Products Manager ─────────────────────────────────────────────────────────
// ─── PDF Upload (for product guides) ──────────────────────────────────────────
function PDFUpload({ productKey, authHeaders }) {
  const [status, setStatus] = useState({ loading: true, exists: false, url: '', size: 0 });
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    setStatus(s => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/upload/pdf/${productKey}/status`, { headers: authHeaders() });
      setStatus({ loading: false, exists: !!r.data.exists, url: r.data.url || '', size: r.data.size || 0 });
    } catch (e) {
      setStatus({ loading: false, exists: false, url: '', size: 0 });
    }
  }, [productKey, authHeaders]);

  useEffect(() => { if (productKey) refresh(); }, [productKey, refresh]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErr('Only PDF files are allowed'); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setErr('PDF exceeds 20 MB limit'); return;
    }
    setErr(''); setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/upload/pdf/${productKey}`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this PDF? Customers will no longer be able to download it from the thank-you page.')) return;
    try {
      await axios.delete(`${API}/upload/pdf/${productKey}`, { headers: authHeaders() });
      await refresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="pdf-upload" style={{ border: '1.5px dashed var(--border)', borderRadius: 12, padding: 16, background: '#FAFAFA' }}>
      <label className="form-label" style={{ marginBottom: 8 }}>Guide PDF <span style={{ color: 'var(--mid)', fontWeight: 400, fontSize: '.75rem' }}>(saved as /pdfs/{productKey}.pdf · max 20 MB)</span></label>
      {status.loading ? (
        <div style={{ fontSize: '.82rem', color: 'var(--mid)' }}>Checking…</div>
      ) : status.exists ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <a href={status.url} target="_blank" rel="noopener noreferrer" data-testid="pdf-current-link" style={{ background: '#D1FAE5', color: '#065F46', padding: '6px 12px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            📄 {productKey}.pdf ({(status.size / 1024 / 1024).toFixed(2)} MB)
          </a>
          <label style={{ background: 'var(--cream)', color: 'var(--teal)', padding: '6px 12px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>
            <input type="file" accept=".pdf,application/pdf" onChange={handleFile} data-testid="pdf-replace-input" style={{ display: 'none' }} />
            {uploading ? 'Uploading…' : 'Replace PDF'}
          </label>
          <button type="button" onClick={handleDelete} data-testid="pdf-delete-btn" style={{ background: '#FEE2E2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>Remove</button>
        </div>
      ) : (
        <div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--teal)', color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
            <input type="file" accept=".pdf,application/pdf" onChange={handleFile} data-testid="pdf-upload-input" style={{ display: 'none' }} />
            <Upload size={14} />{uploading ? 'Uploading…' : 'Upload PDF'}
          </label>
          <div style={{ fontSize: '.76rem', color: 'var(--mid)', marginTop: 6 }}>No PDF yet. Customers will see the WhatsApp fallback on thank-you page.</div>
        </div>
      )}
      {err && <div style={{ marginTop: 10, padding: 10, background: '#FEE2E2', color: '#991B1B', borderRadius: 8, fontSize: '.82rem' }}>{err}</div>}
    </div>
  );
}

function ProductsManager() {
  const { authHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { name: '', category: 'supplement', tagline: '', description: '', features: [], tags: [], mrp: 0, price: 0, image: '', images: [], buy_link: '', is_digital: false, pdf_key: '', in_stock: true, visible: true, badge: '', manufacturer_name: '', manufacturer_address: '', manufactured_by_fssai: '', hsn_code: '', weight_grams: 0 };
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const r = await axios.get(`${API}/products?include_hidden=true`, { headers: authHeaders() }); setProducts(r.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const imgs = Array.isArray(form.images) ? form.images : [];
    const payload = {
      ...form,
      mrp: Number(form.mrp),
      price: Number(form.price),
      features: typeof form.features === 'string' ? form.features.split('\n').filter(Boolean) : form.features,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
      images: imgs,
      image: imgs[0] || form.image || '',  // sync legacy single field with first gallery image
    };
    if (editing) await axios.put(`${API}/products/${editing.id}`, payload, { headers: authHeaders() });
    else await axios.post(`${API}/products`, payload, { headers: authHeaders() });
    setShowForm(false); setEditing(null); setForm(EMPTY); load();
  };

  const del = async (id) => { if (window.confirm('Delete?')) { await axios.delete(`${API}/products/${id}`, { headers: authHeaders() }); load(); } };

  const toggleStock = async (p) => {
    await axios.put(`${API}/products/${p.id}`, { ...p, in_stock: !p.in_stock }, { headers: authHeaders() });
    load();
  };

  const toggleVisible = async (p) => {
    await axios.put(`${API}/products/${p.id}`, { ...p, visible: !(p.visible !== false) }, { headers: authHeaders() });
    load();
  };

  const openEdit = (p) => {
    setEditing(p);
    const imgs = Array.isArray(p.images) && p.images.length ? p.images : (p.image ? [p.image] : []);
    setForm({ ...p, is_digital: !!p.is_digital, tagline: p.tagline || '', images: imgs, features: p.features?.join('\n') || '', tags: p.tags?.join(', ') || '' });
    setShowForm(true);
  };

  const physicalProducts = products.filter(p => !p.is_digital);
  const digitalProducts = products.filter(p => !!p.is_digital);

  const renderCard = (p) => (
    <div key={p.id} data-testid="product-admin-card" style={{ background: '#fff', borderRadius: '14px', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <img src={(Array.isArray(p.images) && p.images[0]) || p.image || ''} alt={p.name} style={{ width: '100%', height: '140px', objectFit: 'contain', background: '#f8f9fa' }} />
      <div style={{ padding: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)', marginBottom: '4px' }}>{p.name}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--mid)' }}>Rs.{p.price} (MRP Rs.{p.mrp})</div>
            {p.is_digital && <div style={{ fontSize: '0.7rem', color: 'var(--coral)', fontWeight: 700, marginTop: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Digital Guide · /{p.pdf_key}.pdf</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
            <button onClick={() => toggleVisible(p)} title={p.visible !== false ? 'Visible — click to hide' : 'Hidden — click to show'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.visible !== false ? 'var(--teal)' : '#9CA3AF', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', fontWeight: 700 }} data-testid="toggle-visible-btn">
              {p.visible !== false ? <Eye size={16} /> : <EyeOff size={16} />}
              <span>{p.visible !== false ? 'VISIBLE' : 'HIDDEN'}</span>
            </button>
            <button onClick={() => toggleStock(p)} title={p.in_stock ? 'In Stock — click to mark out of stock' : 'Out of Stock — click to mark in stock'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.in_stock ? 'var(--teal)' : 'var(--coral)' }} data-testid="toggle-stock-btn">
              {p.in_stock ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
            </button>
          </div>
        </div>
        <div style={{ fontSize: '0.75rem', marginTop: '6px', fontWeight: 700, color: p.in_stock ? 'var(--teal)' : 'var(--coral)' }}>
          {p.in_stock ? '● In Stock' : '○ Out of Stock'}
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <button onClick={() => openEdit(p)} className="btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }} data-testid="edit-product-btn"><Edit size={13} />Edit</button>
          <button onClick={() => del(p.id)} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', color: 'var(--coral)', fontWeight: 600, fontSize: '0.82rem' }} data-testid="delete-product-btn"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-products" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Products ({products.length})</h1>
        <button onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="btn-teal btn-sm" data-testid="add-product-btn"><Plus size={16} />Add Product</button>
      </div>
      <div style={{ background: 'var(--coral-lt)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 10, fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Upload size={14} /> <strong>Tip:</strong>&nbsp;Choose <em>Physical</em> for supplements (no PDF) or <em>Digital Guide</em> to upload a PDF. PDF filenames are auto-generated — you don't need to type one.
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid var(--border)', marginBottom: '24px' }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: '20px', color: 'var(--ink)' }}>{editing ? 'Edit Product' : 'Add Product'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div>
              <label className="form-label">Product Type *</label>
              <select
                className="form-input"
                value={form.is_digital ? 'digital' : 'physical'}
                onChange={e => {
                  const isDigital = e.target.value === 'digital';
                  setForm(f => ({ ...f, is_digital: isDigital, category: isDigital ? 'Digital Guide' : (f.category && f.category !== 'Digital Guide' ? f.category : 'supplement') }));
                }}
                data-testid="product-type-select"
              >
                <option value="physical">Physical Product (Supplement)</option>
                <option value="digital">Digital Guide (PDF)</option>
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Tagline</label><input className="form-input" value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="e.g., 25 Billion CFU · 10 Probiotic Strains" /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Description</label><textarea className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Features (one per line)</label><textarea className="form-input" value={form.features} onChange={e => setForm(f => ({ ...f, features: e.target.value }))} rows={3} /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Tags (comma-separated)</label><input className="form-input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} /></div>
            <div><label className="form-label">MRP — Printed Price (Rs)</label><input className="form-input" type="number" value={form.mrp} onChange={e => setForm(f => ({ ...f, mrp: e.target.value }))} data-testid="product-mrp-input" placeholder="e.g. 499" /></div>
            <div><label className="form-label">Selling Price (Rs)</label><input className="form-input" type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} data-testid="product-selling-price-input" placeholder="e.g. 299" /></div>
            <div><label className="form-label">Badge</label><input className="form-input" value={form.badge} onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} placeholder="50% OFF" /></div>
            <div><label className="form-label">Razorpay Buy Link</label><input className="form-input" value={form.buy_link} onChange={e => setForm(f => ({ ...f, buy_link: e.target.value }))} placeholder="https://rzp.io/rzp/..." /></div>
            {form.is_digital && (
              <div style={{ gridColumn: 'span 2' }}>
                {editing && form.pdf_key ? (
                  <PDFUpload productKey={form.pdf_key} authHeaders={authHeaders} />
                ) : (
                  <div data-testid="pdf-upload-placeholder" style={{ background: '#FFF7E5', border: '1px dashed #E0A800', borderRadius: 10, padding: '14px 16px', fontSize: '0.85rem', color: '#7C5500' }}>
                    💾 Save the product first — the PDF Upload button will appear here automatically once the digital guide is created.
                  </div>
                )}
              </div>
            )}
            <div style={{ gridColumn: 'span 2' }}><MultiImageUpload value={form.images} onChange={imgs => setForm(f => ({ ...f, images: imgs }))} authHeaders={authHeaders} max={11} /></div>
            <div><label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
              <input type="checkbox" checked={form.in_stock} onChange={e => setForm(f => ({ ...f, in_stock: e.target.checked }))} />In Stock
            </label></div>
            <div><label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
              <input type="checkbox" checked={form.visible !== false} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} data-testid="visible-checkbox" />Visible on public site
            </label></div>
          </div>
          {/* MANUFACTURER DETAILS (shown for any physical product) */}
          {!form.is_digital && (
            <div data-testid="manufacturer-section" style={{ marginTop: 14, padding: 14, background: 'var(--cream)', borderRadius: 10, border: '1px dashed var(--border)' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: 4 }}>Manufacturer Details (optional)</div>
              <p style={{ fontSize: '.74rem', color: 'var(--muted)', marginBottom: 10 }}>For compliance display on supplement order pages. Leave FSSAI blank to inherit from Settings.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input data-testid="manufacturer-name-input" className="form-input" placeholder="Manufacturer Name (e.g. ABC Nutraceuticals Pvt Ltd)" value={form.manufacturer_name || ''} onChange={e => setForm(f => ({ ...f, manufacturer_name: e.target.value }))} />
                <input data-testid="manufacturer-fssai-input" className="form-input" placeholder="Manufactured By FSSAI (auto-fill from Settings if blank)" value={form.manufactured_by_fssai || ''} onChange={e => setForm(f => ({ ...f, manufactured_by_fssai: e.target.value }))} />
              </div>
              <input data-testid="manufacturer-address-input" className="form-input" style={{ marginTop: 10 }} placeholder="Manufacturer Address" value={form.manufacturer_address || ''} onChange={e => setForm(f => ({ ...f, manufacturer_address: e.target.value }))} />
              <div style={{ marginTop: 10 }}>
                <input
                  data-testid="hsn-code-input"
                  className="form-input"
                  placeholder="HSN Code (e.g. 21069099 for vitamins/supplements, 29049900 for magnesium)"
                  value={form.hsn_code || ''}
                  onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value.trim() }))}
                  maxLength={10}
                  inputMode="numeric"
                />
                <p style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: 4 }}>GST classification code for this supplement. Used on auto-generated invoices so the correct HSN appears on every line item.</p>
              </div>
              <div style={{ marginTop: 10 }}>
                <input
                  data-testid="weight-grams-input"
                  className="form-input"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Weight in grams (e.g. 750 for a 750g bottle)"
                  value={form.weight_grams || 0}
                  onChange={e => setForm(f => ({ ...f, weight_grams: parseInt(e.target.value, 10) || 0 }))}
                />
                <p style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: 4 }}>Used by the Copy-for-Shiprocket button to auto-fill total shipping weight (quantity x weight) in kilograms.</p>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={save} className="btn-teal btn-sm" data-testid="save-product-btn"><Check size={14} />Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          </div>
        </div>
      )}

      <div data-testid="physical-products-section">
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.05rem', fontWeight: 700, color: 'var(--ink)', marginTop: 18, marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid var(--border)' }}>
          Physical Products · Supplements <span style={{ fontSize: '0.78rem', color: 'var(--mid)', fontWeight: 500 }}>({physicalProducts.length})</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {physicalProducts.map(renderCard)}
          {physicalProducts.length === 0 && <div style={{ color: 'var(--mid)', fontSize: '0.85rem', padding: '20px 0' }}>No physical products yet.</div>}
        </div>
      </div>

      <div data-testid="digital-products-section">
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.05rem', fontWeight: 700, color: 'var(--ink)', marginTop: 32, marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid var(--border)' }}>
          Digital Products · Guides <span style={{ fontSize: '0.78rem', color: 'var(--mid)', fontWeight: 500 }}>({digitalProducts.length})</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--coral)', fontWeight: 600, marginLeft: 10, letterSpacing: '0.04em' }}>PDF UPLOAD ENABLED</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {digitalProducts.map(renderCard)}
          {digitalProducts.length === 0 && <div style={{ color: 'var(--mid)', fontSize: '0.85rem', padding: '20px 0' }}>No digital guides yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Recipes Manager ──────────────────────────────────────────────────────────
function RecipesManager() {
  const { authHeaders } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { name: '', category: 'PCOS-Friendly', calories: 0, protein: 0, time: '30m', image: '', ingredients: [], method: [], clinical_note: '', featured: false };
  const [form, setForm] = useState(EMPTY);

  const CATS = ['PCOS-Friendly', 'Diabetic-Friendly', 'Weight Loss', 'Thyroid', 'High Protein', 'Gut Health', 'Pregnancy', 'Under 30 min'];

  const load = async () => { const r = await axios.get(`${API}/recipes`); setRecipes(r.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, calories: Number(form.calories), protein: Number(form.protein), ingredients: typeof form.ingredients === 'string' ? form.ingredients.split('\n').filter(Boolean) : form.ingredients, method: typeof form.method === 'string' ? form.method.split('\n').filter(Boolean) : form.method };
    if (editing) await axios.put(`${API}/recipes/${editing.id}`, payload, { headers: authHeaders() });
    else await axios.post(`${API}/recipes`, payload, { headers: authHeaders() });
    setShowForm(false); setEditing(null); setForm(EMPTY); load();
  };

  const del = async (id) => { if (window.confirm('Delete?')) { await axios.delete(`${API}/recipes/${id}`, { headers: authHeaders() }); load(); } };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, ingredients: r.ingredients?.join('\n') || '', method: r.method?.join('\n') || '' }); setShowForm(true); };

  const filtered = recipes.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.category.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-recipes" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Recipes ({recipes.length})</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search recipes..." style={{ padding: '8px 14px', border: '1px solid var(--border)', borderRadius: '8px', fontFamily: "'Instrument Sans', sans-serif", fontSize: '0.85rem', outline: 'none' }} />
          <button onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="btn-teal btn-sm" data-testid="add-recipe-btn"><Plus size={16} />Add Recipe</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid var(--border)', marginBottom: '24px' }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: '20px', color: 'var(--ink)' }}>{editing ? 'Edit Recipe' : 'Add Recipe'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="form-label">Calories (kcal)</label><input className="form-input" type="number" value={form.calories} onChange={e => setForm(f => ({ ...f, calories: e.target.value }))} /></div>
            <div><label className="form-label">Protein (g)</label><input className="form-input" type="number" value={form.protein} onChange={e => setForm(f => ({ ...f, protein: e.target.value }))} /></div>
            <div><label className="form-label">Time</label><input className="form-input" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} placeholder="30m" /></div>
            <div><ImageUpload value={form.image} onChange={url => setForm(f => ({ ...f, image: url }))} authHeaders={authHeaders} label="Recipe Photo" /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Ingredients (one per line)</label><textarea className="form-input" value={form.ingredients} onChange={e => setForm(f => ({ ...f, ingredients: e.target.value }))} rows={4} /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Method (one step per line)</label><textarea className="form-input" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} rows={4} /></div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Clinical Note</label><textarea className="form-input" value={form.clinical_note} onChange={e => setForm(f => ({ ...f, clinical_note: e.target.value }))} rows={2} /></div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={save} className="btn-teal btn-sm" data-testid="save-recipe-btn"><Check size={14} />Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        {filtered.map((recipe, i) => (
          <div key={recipe.id} data-testid="recipe-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <img src={recipe.image || (recipe.slug ? `/images/recipes/${recipe.slug}.jpg` : 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=60')} alt="" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=60'; }} style={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--ink)', marginBottom: '2px' }}>{recipe.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--mid)' }}>{recipe.category} · {recipe.calories}kcal · {recipe.protein}g protein · {recipe.time}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => openEdit(recipe)} style={{ background: 'var(--teal-lt)', border: 'none', borderRadius: '8px', padding: '7px', cursor: 'pointer', color: 'var(--teal)' }} data-testid="edit-recipe-btn"><Edit size={13} /></button>
              <button onClick={() => del(recipe.id)} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: '8px', padding: '7px', cursor: 'pointer', color: 'var(--coral)' }} data-testid="delete-recipe-btn"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Orders Manager ───────────────────────────────────────────────────────────

// Slide-in drawer with full order detail + copy buttons
function OrderDetailDrawer({ order, onClose, onUpdated, derivedType, products }) {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');
  // Inline edit state for product_name.
  const [currentName, setCurrentName]   = useState(order?.product_name || '');
  const [editingName, setEditingName]   = useState(false);
  const [draftName, setDraftName]       = useState(order?.product_name || '');
  const [savingName, setSavingName]     = useState(false);
  const [nameError, setNameError]       = useState('');
  // Convert-to-Client busy flag.
  const [convertingClient, setConvertingClient] = useState(false);
  // Linked draft-invoice surface (B7). Looks up GET /api/invoices/by-order/{id};
  // null = none yet, undefined = still loading. Quietly absent if no invoice.
  const [linkedInvoice, setLinkedInvoice] = useState(undefined);
  // Keep local mirror in sync when the parent swaps the order doc.
  useEffect(() => {
    setCurrentName(order?.product_name || '');
    setDraftName(order?.product_name || '');
    setEditingName(false);
    setNameError('');
  }, [order?.order_id, order?.product_name]);

  // Fetch the linked invoice (if any) whenever a different order is opened.
  useEffect(() => {
    if (!order?.order_id) { setLinkedInvoice(null); return; }
    let cancelled = false;
    setLinkedInvoice(undefined);
    axios.get(`${API}/invoices/by-order/${encodeURIComponent(order.order_id)}`, { headers: authHeaders() })
      .then(r => { if (!cancelled) setLinkedInvoice(r.data); })
      .catch(() => { if (!cancelled) setLinkedInvoice(null); });
    return () => { cancelled = true; };
  }, [order?.order_id]);

  const TEAL = '#0F4C5C';
  const CREAM = '#F7F3EE';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!order) return null;

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
  const fullAddress = order.shipping_address || [order.address_line1, order.address_line2].filter(Boolean).join(', ') || '—';
  const country = order.country || 'India';
  const quantity = order.quantity || 1;
  const unitPrice = order.unit_price || order.amount || 0;
  const category = order.product_category || order.category || '—';

  // ── Build the Shiprocket-friendly export ─────────────────────────────────
  // Empty values render as blank (no dashes) because Shiprocket's CSV import
  // treats literal "-" as data, not as a missing value. Weight uses TOTAL kg
  // (quantity x weight_grams / 1000) since Shiprocket charges by package
  // weight, not per-unit.
  const matchedProduct = (products || []).find(p =>
    (p.id && p.id === order.product_id) ||
    (p.name && (p.name === order.product_name || p.name === currentName))
  );
  const totalWeightKg = (() => {
    if (!matchedProduct || !matchedProduct.weight_grams) return '';
    const grams = Number(matchedProduct.weight_grams) || 0;
    const kg = (grams * quantity) / 1000;
    if (!kg) return '';
    // Strip trailing zeros after the decimal: 0.75 -> "0.75", 1 -> "1".
    return kg.toFixed(3).replace(/\.?0+$/, '');
  })();
  const shiprocketText = [
    `Name: ${order.customer_name || ''}`,
    `Phone: ${order.phone || ''}`,
    `Email: ${order.email || ''}`,
    `Address Line 1: ${order.address_line1 || ''}`,
    `Address Line 2: ${order.address_line2 || ''}`,
    `City: ${order.city || ''}`,
    `State: ${order.state || ''}`,
    `Pincode: ${order.pincode || ''}`,
    `Product: ${currentName || ''}`,
    `Quantity: ${quantity}`,
    `Weight (kg): ${totalWeightKg}`,
    `Price: ${order.amount || 0}`,
    `Order ID: ${order.order_id || ''}`,
  ].join('\n');

  const shippingAddressText = [
    `Name: ${order.customer_name || ''}`,
    `Phone: ${order.phone || ''}`,
    `Email: ${order.email || ''}`,
    `Address Line 1: ${order.address_line1 || ''}`,
    `Address Line 2: ${order.address_line2 || ''}`,
    `City: ${order.city || ''}`,
    `State: ${order.state || ''}`,
    `Pincode: ${order.pincode || ''}`,
  ].join('\n');

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setCopied(key); setTimeout(() => setCopied(''), 2000); } finally { document.body.removeChild(ta); }
    }
  };

  // ── product_name inline-edit save handler ─────────────────────────────────
  const saveProductName = async () => {
    const next = (draftName || '').trim();
    setNameError('');
    if (!next) { setNameError('Cannot be empty'); return; }
    if (next === currentName) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await axios.patch(`${API}/orders/${order.order_id}/product-name`, { product_name: next }, { headers: authHeaders() });
      setCurrentName(next);
      setEditingName(false);
      // Notify parent so list rows refresh without a full reload.
      if (onUpdated) onUpdated({ order_id: order.order_id, product_name: next });
    } catch (e) {
      setNameError(e.response?.data?.detail || 'Could not save - try again');
    } finally {
      setSavingName(false);
    }
  };
  const cancelEditName = () => { setEditingName(false); setDraftName(currentName); setNameError(''); };

  // ── Convert-to-Client handler ─────────────────────────────────────────────
  // Pre-checks for an existing client by email, then navigates to /admin/clients
  // with prefill query params so the existing Add Client form opens populated.
  const handleConvertToClient = async () => {
    const email = (order.email || '').trim();
    if (!email) {
      alert('This order has no email - cannot convert to client. Edit the order to add an email first.');
      return;
    }
    setConvertingClient(true);
    try {
      const r = await axios.get(`${API}/clients`, { params: { search: email }, headers: authHeaders() });
      const existing = (r.data || []).find(c => (c.email || '').toLowerCase() === email.toLowerCase());
      if (existing) {
        alert(`A client with email ${email} already exists (name: ${existing.name || '-'}).`);
        return;
      }
      // No duplicate - navigate with prefill query params. ClientsManager reads
      // them on mount, opens the form, then strips the URL.
      const planType = derivedType === 'Consultation' ? '3-months' : 'one-time';
      const params = new URLSearchParams({
        prefill_name:   order.customer_name || '',
        prefill_email:  email,
        prefill_mobile: order.phone || '',
        prefill_plan:   planType,
      });
      navigate(`/admin/clients?${params.toString()}`);
    } catch (e) {
      alert('Could not check existing clients: ' + (e.response?.data?.detail || e.message));
    } finally {
      setConvertingClient(false);
    }
  };

  const sectionStyle = { background: CREAM, borderRadius: 12, padding: '16px 18px', marginBottom: 14 };
  const sectionTitle = { fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '.95rem', color: TEAL, marginBottom: 10, letterSpacing: '.02em', textTransform: 'uppercase' };
  const fieldRow = { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, padding: '6px 0', fontSize: '.86rem', borderBottom: '1px solid rgba(15,76,92,0.06)' };
  const fieldLabel = { color: 'var(--mid)', fontWeight: 500 };
  const fieldVal = { color: TEAL, fontWeight: 600, wordBreak: 'break-word' };

  const Field = ({ label, value }) => (
    <div style={fieldRow}><span style={fieldLabel}>{label}</span><span style={fieldVal}>{value || '—'}</span></div>
  );

  return (
    <>
      <div
        data-testid="order-drawer-overlay"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,76,92,0.4)', zIndex: 1300, backdropFilter: 'blur(2px)' }}
      />
      <aside
        data-testid="order-detail-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, height: '100vh',
          width: 'min(560px, 100vw)', background: '#fff', boxShadow: '-20px 0 60px rgba(15,76,92,0.18)',
          zIndex: 1301, display: 'flex', flexDirection: 'column',
          animation: 'orderDrawerIn .28s cubic-bezier(.4,0,.2,1)',
        }}
      >
        <style>{`@keyframes orderDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', background: TEAL, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.7rem', letterSpacing: '.14em', textTransform: 'uppercase', opacity: 0.7 }}>Order Detail</div>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.25rem', margin: '2px 0 0' }}>{order.customer_name || 'Customer'}</h2>
            <div style={{ fontSize: '.78rem', opacity: 0.8, marginTop: 2 }}>{order.order_id}</div>
          </div>
          <button data-testid="order-drawer-close" onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#fff' }}>
            <X size={18} />
          </button>
        </div>

        {/* Copy buttons */}
        <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            data-testid="copy-shiprocket-btn"
            onClick={() => copy(shiprocketText, 'shiprocket')}
            className="btn-teal btn-sm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1 }}
          >
            <Copy size={14} /> {copied === 'shiprocket' ? 'Copied ✓' : 'Copy for Shiprocket'}
          </button>
          <button
            data-testid="copy-shipping-address-btn"
            onClick={() => copy(shippingAddressText, 'address')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, background: '#E07A5F', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.84rem' }}
          >
            <Copy size={14} /> {copied === 'address' ? 'Copied ✓' : 'Copy Shipping Address'}
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', background: '#fff' }}>

          <div data-testid="order-section-summary" style={sectionStyle}>
            <div style={sectionTitle}>Order Summary</div>
            <Field label="Order ID" value={order.order_id} />
            <Field label="Order Date" value={formatDate(order.created_at)} />
            <Field label="Payment Status" value={(order.payment_status || '').toUpperCase()} />
            <Field label="Order Status" value={(order.order_status || '').toUpperCase()} />
            <Field label="Razorpay Payment ID" value={order.razorpay_payment_id} />
            {linkedInvoice && linkedInvoice.invoice_number ? (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#F0F7F9', border: '1px solid #CFE2E7', borderRadius: 8 }} data-testid="order-linked-invoice">
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Invoice</div>
                <a href="#/admin/invoices"
                   onClick={(e) => { e.preventDefault(); onClose && onClose(); window.location.hash = '#/admin/invoices'; }}
                   data-testid="order-linked-invoice-link"
                   style={{ color: TEAL, fontWeight: 600, textDecoration: 'none' }}>
                  {(linkedInvoice.workflow_status || 'DRAFT')} Invoice: {linkedInvoice.invoice_number}
                </a>
              </div>
            ) : null}
          </div>

          <div data-testid="order-section-customer" style={sectionStyle}>
            <div style={sectionTitle}>Customer Details</div>
            <Field label="Full Name" value={order.customer_name} />
            <Field label="Email Address" value={order.email} />
            <Field label="Phone Number" value={order.phone} />
            <button
              data-testid="convert-to-client-btn"
              onClick={handleConvertToClient}
              disabled={convertingClient}
              style={{ marginTop: 12, background: TEAL, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: '.82rem', cursor: convertingClient ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            ><UserPlus size={14} /> {convertingClient ? 'Checking...' : 'Convert to Client'}</button>
          </div>

          <div data-testid="order-section-product" style={sectionStyle}>
            <div style={sectionTitle}>Product Details</div>
            {/* Editable product_name row */}
            <div style={{ ...fieldRow, alignItems: 'center' }}>
              <span style={fieldLabel}>Product Name</span>
              {editingName ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      data-testid="product-name-edit-input"
                      autoFocus
                      type="text"
                      value={draftName}
                      onChange={e => setDraftName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveProductName();
                        if (e.key === 'Escape') cancelEditName();
                      }}
                      maxLength={200}
                      style={{ flex: 1, minWidth: 0, padding: '6px 10px', border: `1.5px solid ${TEAL}`, borderRadius: 6, fontSize: '.86rem', color: TEAL, fontWeight: 600 }}
                    />
                    <button
                      data-testid="product-name-save-btn"
                      onClick={saveProductName}
                      disabled={savingName}
                      style={{ background: TEAL, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: '.78rem', fontWeight: 600, cursor: savingName ? 'wait' : 'pointer' }}
                    >{savingName ? 'Saving...' : 'Save'}</button>
                    <button
                      data-testid="product-name-cancel-btn"
                      onClick={cancelEditName}
                      disabled={savingName}
                      style={{ background: 'transparent', color: 'var(--mid)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >Cancel</button>
                  </div>
                  {nameError && <div data-testid="product-name-error" style={{ color: '#c0392b', fontSize: '.74rem' }}>{nameError}</div>}
                </div>
              ) : (
                <span style={{ ...fieldVal, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span>{currentName || '—'}</span>
                  <button
                    data-testid="product-name-edit-btn"
                    onClick={() => { setDraftName(currentName); setEditingName(true); setNameError(''); }}
                    title="Edit product name"
                    style={{ background: 'transparent', border: 'none', color: TEAL, cursor: 'pointer', padding: 2, lineHeight: 0, opacity: 0.7 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                  ><Pencil size={13} /></button>
                </span>
              )}
            </div>
            <Field label="Category" value={category} />
            <Field label="Quantity" value={quantity} />
            <Field label="Unit Price" value={`₹${unitPrice}`} />
            <Field label="Amount Paid" value={`₹${order.amount || 0}`} />
          </div>

          <div data-testid="order-section-shipping" style={sectionStyle}>
            <div style={sectionTitle}>Shipping Address</div>
            <Field label="Address" value={fullAddress} />
            <Field label="City" value={order.city} />
            <Field label="State" value={order.state} />
            <Field label="Pincode" value={order.pincode} />
            <Field label="Country" value={country} />
          </div>

        </div>
      </aside>
    </>
  );
}

function ShippingModal({ order, authHeaders, onClose, onSaved }) {
  const COURIERS = ['Delhivery', 'Bluedart', 'DTDC', 'India Post', 'FedEx', 'Ekart', 'Other'];
  const STATUSES = ['processing', 'shipped', 'in_transit', 'out_for_delivery', 'delivered'];
  const [form, setForm] = useState({
    tracking_number: order.tracking_number || '',
    courier_partner: order.courier_partner || '',
    tracking_url: order.tracking_url || '',
    est_delivery_date: order.est_delivery_date || '',
    shipping_status: order.shipping_status || 'processing',
    send_email: false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/orders/${order.order_id}/shipping`, form, { headers: authHeaders() });
      alert(`Saved!${r.data.email_sent ? ' Shipping email sent to customer.' : (form.send_email ? ' (Email NOT sent — check Resend config.)' : '')}`);
      onSaved();
      onClose();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div onClick={e => e.stopPropagation()} data-testid="shipping-modal" style={{ background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 560 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem', marginBottom: 4 }}>Shipping for {order.order_id}</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>{order.customer_name} · {order.product_name}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label className="form-label">Tracking Number</label><input data-testid="shipping-tracking-number" className="form-input" value={form.tracking_number} onChange={e => setForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
          <div><label className="form-label">Courier Partner</label>
            <select className="form-select" data-testid="shipping-courier" value={form.courier_partner} onChange={e => setForm(f => ({ ...f, courier_partner: e.target.value }))}>
              <option value="">— Select —</option>
              {COURIERS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}><label className="form-label">Tracking URL</label><input data-testid="shipping-tracking-url" className="form-input" value={form.tracking_url} onChange={e => setForm(f => ({ ...f, tracking_url: e.target.value }))} placeholder="https://…" /></div>
          <div><label className="form-label">Estimated Delivery</label><input data-testid="shipping-est-delivery" type="date" className="form-input" value={form.est_delivery_date} onChange={e => setForm(f => ({ ...f, est_delivery_date: e.target.value }))} /></div>
          <div><label className="form-label">Shipping Status</label>
            <select className="form-select" data-testid="shipping-status" value={form.shipping_status} onChange={e => setForm(f => ({ ...f, shipping_status: e.target.value }))}>
              {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer', fontSize: '.88rem' }}>
          <input type="checkbox" checked={form.send_email} onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))} data-testid="shipping-send-email" />
          Send Shipping Update Email to {order.email || '(no email on file)'}
        </label>
        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          <button onClick={save} disabled={saving} className="btn-teal btn-sm" data-testid="save-shipping-btn"><Check size={14} />{saving ? 'Saving…' : 'Save Shipping'}</button>
        </div>
      </div>
    </div>
  );
}

function OrdersManager() {
  const { authHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ order_status: '', payment_status: '', order_type: '', date_from: '', date_to: '' });
  const EMPTY = { customer_name: '', phone: '', email: '', product_name: '', amount: 0, payment_status: 'pending', order_status: 'pending', notes: '' };
  const [form, setForm] = useState(EMPTY);
  const [shippingFor, setShippingFor] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  // ── Order type derivation ──────────────────────────────────────────────────
  // Used to drive the coloured pill on each row and the Type filter dropdown.
  // Priority order matches the Fix 6 spec exactly.
  // NAR (Nutrition Action Report) - identified by fixed price Rs.4999, no
  // diet_review_form_url, and no matching product doc. Exact price match only,
  // no tolerance.
  const NAR_PRICE = 4999;
  const DEFAULT_TYPES = ['Supplement', 'Consultation'];
  const deriveType = (o) => {
    if (o.diet_review_form_url) return 'QDR';
    // Match the order to a product doc. Prefer NAME match (so an inline-edit
    // of product_name immediately re-classifies the row) and fall back to
    // product_id only when the name does not match any product. Name compare
    // is normalised (trim + lowercase) to absorb harmless whitespace/case drift.
    const nm = (o.product_name || '').trim().toLowerCase();
    const matchedByName = nm ? products.find(p => (p.name || '').trim().toLowerCase() === nm) : null;
    const matched = matchedByName || products.find(p => p.id && p.id === o.product_id);
    if ((o.source === 'supplement-order-form') || (matched && matched.is_digital === false)) return 'Supplement';
    if (matched && matched.is_digital === true) return 'Digital';
    if (!matched && Number(o.amount) === NAR_PRICE) return 'NAR';
    if ((Number(o.amount) || 0) >= 1000 && !matched) return 'Consultation';
    return 'Other';
  };
  const TYPE_STYLES = {
    Supplement:   { bg: '#E07A5F', fg: '#fff' },
    Digital:      { bg: '#7AA095', fg: '#fff' },
    QDR:          { bg: '#C8A054', fg: '#fff' },
    Consultation: { bg: '#0F4C5C', fg: '#fff' },
    NAR:          { bg: '#C8A054', fg: '#fff' },
    Other:        { bg: '#9E9E9E', fg: '#fff' },
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (filter.order_status) params.order_status = filter.order_status;
    if (filter.payment_status) params.payment_status = filter.payment_status;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    // Load products + orders in parallel - products are needed to derive Type.
    const [op, pp] = await Promise.all([
      axios.get(`${API}/orders`, { headers: authHeaders(), params }),
      axios.get(`${API}/products`).catch(() => ({ data: [] })),
    ]);
    setOrders(op.data); setProducts(pp.data || []); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Type filter behaviour:
  //   ''       (default)  -> show only Supplement + Consultation
  //   'ALL'              -> show every type
  //   'Supplement' etc.  -> show only that single type
  const visibleOrders = (() => {
    if (filter.order_type === '') return orders.filter(o => DEFAULT_TYPES.includes(deriveType(o)));
    if (filter.order_type === 'ALL') return orders;
    return orders.filter(o => deriveType(o) === filter.order_type);
  })();

  const save = async () => {
    await axios.post(`${API}/orders`, { ...form, amount: Number(form.amount) }, { headers: authHeaders() });
    setShowForm(false); setForm(EMPTY); load();
  };
  const setOrderStatus = async (o, s) => { await axios.put(`${API}/orders/${o.order_id}/status`, { order_status: s }, { headers: authHeaders() }); load(); };
  const setPayStatus = async (o, s) => { await axios.put(`${API}/orders/${o.order_id}/payment-status`, { payment_status: s }, { headers: authHeaders() }); load(); };
  const del = async (id) => { if (window.confirm('Delete order?')) { await axios.delete(`${API}/orders/${id}`, { headers: authHeaders() }); load(); } };

  const STATUS_COLORS = { pending: '#F59E0B', confirmed: '#3B82F6', packed: '#8B5CF6', shipped: '#0EA5E9', delivered: '#10B981', cancelled: '#EF4444' };
  const PAY_COLORS = { pending: '#F59E0B', paid: '#10B981', failed: '#EF4444', refunded: '#6B7280' };

  return (
    <div data-testid="admin-orders" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Orders ({visibleOrders.length} of {orders.length})</h1>
        <button onClick={() => setShowForm(true)} className="btn-teal btn-sm" data-testid="add-order-btn"><Plus size={16} />Add Order</button>
      </div>
      <div style={{ background: 'var(--teal-lt)', color: 'var(--teal)', padding: '10px 14px', borderRadius: 10, fontSize: '0.82rem', marginBottom: '20px' }}>
        <strong>Razorpay webhook:</strong>&nbsp;Configured at <code>/api/razorpay/webhook</code>. Add API keys in <code>backend/.env</code> when ready — paid orders will auto-appear here.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search name / phone / order id…" data-testid="orders-search" style={{ flex: '1 1 200px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem' }} />
        <select value={filter.order_status} onChange={e => { setFilter(f => ({ ...f, order_status: e.target.value })); }} data-testid="filter-order-status" style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem' }}>
          <option value="">All order statuses</option>
          {['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.payment_status} onChange={e => { setFilter(f => ({ ...f, payment_status: e.target.value })); }} data-testid="filter-payment-status" style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem' }}>
          <option value="">All payment statuses</option>
          {['pending', 'paid', 'failed', 'refunded'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.order_type} onChange={e => { setFilter(f => ({ ...f, order_type: e.target.value })); }} data-testid="filter-order-type" style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem' }}>
          <option value="">Supplements and Consultations</option>
          <option value="ALL">All types</option>
          {['Supplement', 'Digital', 'QDR', 'Consultation', 'NAR', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" value={filter.date_from} onChange={e => setFilter(f => ({ ...f, date_from: e.target.value }))} data-testid="orders-date-from" title="From date" style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.82rem' }} />
        <input type="date" value={filter.date_to} onChange={e => setFilter(f => ({ ...f, date_to: e.target.value }))} data-testid="orders-date-to" title="To date" style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.82rem' }} />
        <button onClick={load} className="btn-teal btn-sm" data-testid="apply-filters-btn">Apply</button>
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: 16 }}>Add Manual Order</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label className="form-label">Customer Name *</label><input className="form-input" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} /></div>
            <div><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><label className="form-label">Product *</label><input className="form-input" value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} /></div>
            <div><label className="form-label">Amount (₹)</label><input className="form-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div><label className="form-label">Payment Status</label>
              <select className="form-select" value={form.payment_status} onChange={e => setForm(f => ({ ...f, payment_status: e.target.value }))}>
                {['pending', 'paid', 'failed', 'refunded'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="form-label">Order Status</label>
              <select className="form-select" value={form.order_status} onChange={e => setForm(f => ({ ...f, order_status: e.target.value }))}>
                {['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Notes</label><textarea className="form-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={save} className="btn-teal btn-sm" data-testid="save-order-btn"><Check size={14} />Save</button>
            <button onClick={() => { setShowForm(false); setForm(EMPTY); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div> : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {visibleOrders.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>{
              filter.order_type === ''
                ? 'No Supplement or Consultation orders yet. Switch the type filter to "All types" to see QDR, Digital, NAR and other categories.'
                : filter.order_type === 'ALL'
                  ? 'No orders yet. Razorpay webhook will populate paid orders automatically when configured, or add a manual one above.'
                  : `No orders match type "${filter.order_type}".`
            }</div>
          ) : visibleOrders.map((o, i) => {
            const type = deriveType(o);
            const ts = TYPE_STYLES[type] || TYPE_STYLES.Other;
            return (
            <div key={o.order_id} data-testid="order-row" style={{ padding: '14px 18px', borderBottom: i < visibleOrders.length - 1 ? '1px solid var(--border)' : 'none', display: 'grid', gridTemplateColumns: '1.5fr 2fr auto auto auto auto auto', gap: 12, alignItems: 'center', transition: 'background .15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F7F3EE'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div data-testid="order-row-open" onClick={() => setDetailOrder(o)} style={{ cursor: 'pointer' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>{o.customer_name}</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--mid)' }}>{o.phone} · {o.order_id}</div>
              </div>
              <div onClick={() => setDetailOrder(o)} style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: '0.88rem', color: 'var(--ink)' }}>{o.product_name}</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--mid)' }}>₹{o.amount} · {o.created_at?.slice(0, 10)}{o.client_id && ` · ${o.client_id}`}</div>
                {o.shipping_address && (
                  <div data-testid="order-shipping-address" style={{ fontSize: '0.72rem', color: '#0F4C5C', marginTop: 4, fontStyle: 'italic' }}>📦 {o.shipping_address}</div>
                )}
              </div>
              <span data-testid={`order-type-pill-${o.order_id}`} title={`Order type: ${type}`} style={{ background: ts.bg, color: ts.fg, padding: '4px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{type}</span>
              <select value={o.payment_status} onClick={e => e.stopPropagation()} onChange={e => setPayStatus(o, e.target.value)} data-testid="order-payment-select" style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: (PAY_COLORS[o.payment_status] || '#9CA3AF') + '22', color: PAY_COLORS[o.payment_status] || '#9CA3AF', fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', cursor: 'pointer' }}>
                {(() => {
                  // Per-row constraint: paid orders can only move to refunded.
                  // Refunded is terminal. Pending/failed retain the full list.
                  // The current value is always rendered first so it stays selected.
                  let opts;
                  if (o.payment_status === 'paid')     opts = ['paid', 'refunded'];
                  else if (o.payment_status === 'refunded') opts = ['refunded'];
                  else opts = ['pending', 'paid', 'failed', 'refunded'];
                  return opts.map(s => <option key={s} value={s}>{s}</option>);
                })()}
              </select>
              <select value={o.order_status} onClick={e => e.stopPropagation()} onChange={e => setOrderStatus(o, e.target.value)} data-testid="order-status-select" style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: (STATUS_COLORS[o.order_status] || '#9CA3AF') + '22', color: STATUS_COLORS[o.order_status] || '#9CA3AF', fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', cursor: 'pointer' }}>
                {['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {type === 'Supplement' && (
                <button onClick={(e) => { e.stopPropagation(); setShippingFor(o); }} title="Shipping & Tracking" style={{ background: '#E0F2FE', color: '#0284C7', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: '.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }} data-testid="order-shipping-btn">🚚 Ship</button>
              )}
              <button onClick={(e) => { e.stopPropagation(); del(o.order_id); }} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--coral)' }} data-testid="delete-order-btn"><Trash2 size={13} /></button>
            </div>
            );
          })}
        </div>
      )}
      {shippingFor && <ShippingModal order={shippingFor} authHeaders={authHeaders} onClose={() => setShippingFor(null)} onSaved={load} />}
      {detailOrder && <OrderDetailDrawer order={detailOrder} derivedType={deriveType(detailOrder)} products={products} onClose={() => setDetailOrder(null)} onUpdated={(patch) => {
        // Patch is { order_id, ...changed fields }. Update both the list row and the open drawer copy.
        setOrders(prev => prev.map(o => o.order_id === patch.order_id ? { ...o, ...patch } : o));
        setDetailOrder(prev => prev && prev.order_id === patch.order_id ? { ...prev, ...patch } : prev);
      }} />}
    </div>
  );
}

// ─── Diet Reviews (rebuilt: clean submission list + 7 cream cards + manual PDF upload/send) ──
function DietReviewsManager() {
  const { authHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', date_from: '', date_to: '' });
  const [genForm, setGenForm] = useState({ name: '', email: '', show: false, copyState: '' });
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [uiError, setUiError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [undoToast, setUndoToast] = useState(null); // { ids: [], label: '...' }

  // Status derivation — works on legacy records too (no AI badges)
  const deriveStatus = (r) => {
    if (r?.report_sent_at) return 'sent';
    if (r?.report_pdf || r?.report_uploaded_at) return 'report_ready';
    return 'pending_review';
  };
  const STATUS_BADGE = {
    pending_review: { bg: '#FEF3C7', fg: '#92400E', label: 'Pending Review' },
    report_ready:   { bg: '#DBEAFE', fg: '#1E3A8A', label: 'Report Ready' },
    sent:           { bg: '#DCFCE7', fg: '#14532D', label: 'Sent' },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const r = await axios.get(`${API}/diet-reviews?${params.toString()}`, { headers: authHeaders() });
      let data = Array.isArray(r.data) ? r.data : [];
      // Client-side filter on derived (clean) status
      if (filters.status) data = data.filter(x => deriveStatus(x) === filters.status);
      setReviews(data);
      // Prune selectedIds: drop any IDs no longer in the list
      setSelectedIds(prev => {
        const visible = new Set(data.map(x => x.id));
        const next = new Set();
        prev.forEach(id => { if (visible.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      setReviews([]);
      setUiError(e.response?.data?.detail || e.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filters]);
  useEffect(() => { load(); }, [load]);
  // Lightweight auto-refresh so new public submissions appear without manual reload
  useEffect(() => {
    const t = setInterval(() => { if (!selected) load(); }, 20000);
    return () => clearInterval(t);
  }, [load, selected]);

  const openReview = async (r) => {
    setSelected(r);
    setCopiedAll(false);
    setUiError('');
    try {
      const resp = await axios.get(`${API}/diet-reviews/${r.id}/draft`, { headers: authHeaders() });
      setDetail(resp.data);
    } catch (e) {
      setDetail(r);
      setUiError(e.response?.data?.detail || 'Failed to load submission detail');
    }
  };

  // ── Field section definitions (label, dotted-field-key) ──
  const SECTIONS = [
    {
      title: 'Basic Details',
      fields: [
        ['Name', 'full_name'], ['Age', 'age'], ['Gender', 'gender'], ['Blood Group', 'blood_group'],
        ['City', 'city'], ['Height', 'height'], ['Weight', 'weight'],
        ['Email', 'email'], ['WhatsApp', 'whatsapp'],
      ],
    },
    {
      title: 'Health Goals',
      fields: [['Goals selected', 'goals'], ['Additional context', 'other_goal']],
    },
    {
      title: 'Current Diet',
      fields: [
        ['Diet type', 'diet_type'], ['Meals per day', 'meals_per_day'], ['Meal timings', 'meal_timings'],
        ['Typical weekday diet', 'typical_weekday'], ['Weekend diet', 'weekend_diet'],
        ['Water intake', 'water_intake'], ['Chai / Coffee', 'chai_coffee'],
        ['Cooks at home', 'cooks_at_home'], ['Time available for meals', 'time_for_meals'],
        ['Allergies / Intolerances', 'food_allergies'],
      ],
    },
    {
      title: 'Health & Lifestyle',
      fields: [
        ['Diagnosed conditions', 'conditions'], ['Periods (if applicable)', 'periods'],
        ['Medications', 'medications'], ['Supplements', 'supplements'],
        ['Activity level', 'activity_level'], ['Sleep (hours)', 'sleep_hours'],
        ['Stress level (1-10)', 'stress_level'],
      ],
    },
    {
      title: 'Digestive Health',
      fields: [
        ['Gut symptoms', 'digestive_issues'], ['Foods causing discomfort', 'food_discomfort'],
        ['Bowel frequency', 'bowel_frequency'],
      ],
    },
    {
      title: 'Main Issues',
      fields: [
        ['Top issues to focus on', 'top_issues'], ['Duration', 'duration'],
        ['What has not worked', 'tried_before'], ['Additional notes', 'other_notes'],
      ],
    },
    {
      title: 'Blood Tests',
      fields: [['Recent values', 'blood_test_values'], ['Last full panel date', 'last_blood_panel']],
    },
  ];

  const formatValue = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    if (typeof v === 'object') {
      // meal_timings dict etc.
      const entries = Object.entries(v).filter(([, x]) => x);
      if (!entries.length) return '—';
      return entries.map(([k, x]) => `${k}: ${x}`).join(' · ');
    }
    return String(v);
  };

  const buildPlainTextDump = (d) => {
    if (!d) return '';
    const lines = [];
    lines.push(`Diet Review Submission — ${d.full_name || ''}`);
    lines.push(`Submitted: ${d.submitted_at ? new Date(d.submitted_at).toLocaleString() : '—'}`);
    lines.push('');
    SECTIONS.forEach(sec => {
      lines.push(`── ${sec.title.toUpperCase()} ──`);
      sec.fields.forEach(([label, key]) => {
        lines.push(`${label}: ${formatValue(d[key])}`);
      });
      lines.push('');
    });
    return lines.join('\n');
  };

  const copyAll = async () => {
    const txt = buildPlainTextDump(detail);
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    } catch (e) {
      window.prompt('Copy the submission details:', txt);
    }
  };

  // ── PDF upload ──
  const onPdfPicked = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setUiError('Please choose a PDF file.');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setUiError('PDF must be 6 MB or smaller.');
      return;
    }
    setUiError('');
    setUploading(true);
    try {
      const data_url = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('File read failed'));
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/diet-reviews/${detail.id}/upload-report`,
        { filename: file.name, data_url },
        { headers: authHeaders() });
      // Refresh detail
      const fresh = await axios.get(`${API}/diet-reviews/${detail.id}/draft`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setUiError(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeReport = async () => {
    if (!detail?.id) return;
    if (!window.confirm('Remove the uploaded report? You will need to upload a new PDF before sending.')) return;
    try {
      await axios.delete(`${API}/diet-reviews/${detail.id}/report`, { headers: authHeaders() });
      const fresh = await axios.get(`${API}/diet-reviews/${detail.id}/draft`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setUiError(e.response?.data?.detail || 'Failed to remove report');
    }
  };

  const sendReport = async () => {
    if (!detail?.id) return;
    if (!detail.report_pdf && !detail.report_uploaded_at) {
      setUiError('Upload a PDF before sending.');
      return;
    }
    if (!window.confirm(`Send the report PDF to ${detail.email}? This cannot be undone.`)) return;
    setSending(true);
    setUiError('');
    try {
      await axios.post(`${API}/diet-reviews/${detail.id}/send-report`, {}, { headers: authHeaders() });
      const fresh = await axios.get(`${API}/diet-reviews/${detail.id}/draft`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setUiError(e.response?.data?.detail || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // ── Delete + trash helpers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = reviews.length > 0 && reviews.every(r => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (reviews.length > 0 && reviews.every(r => prev.has(r.id))) return new Set();
      return new Set(reviews.map(r => r.id));
    });
  };

  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const r = await axios.get(`${API}/diet-reviews/trash`, { headers: authHeaders() });
      setTrashItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setUiError(e.response?.data?.detail || 'Failed to load trash');
    } finally {
      setTrashLoading(false);
    }
  }, [authHeaders]);

  const openTrash = async () => {
    setShowTrash(true);
    await loadTrash();
  };

  const restoreOne = async (id) => {
    try {
      await axios.post(`${API}/diet-reviews/${id}/restore`, {}, { headers: authHeaders() });
      await Promise.all([load(), loadTrash()]);
    } catch (e) {
      setUiError(e.response?.data?.detail || 'Restore failed');
    }
  };

  const restoreMany = async (ids) => {
    if (!ids?.length) return;
    try {
      await Promise.all(ids.map(id => axios.post(`${API}/diet-reviews/${id}/restore`, {}, { headers: authHeaders() })));
      await Promise.all([load(), showTrash ? loadTrash() : Promise.resolve()]);
      setUndoToast(null);
    } catch (e) {
      setUiError(e.response?.data?.detail || 'Restore failed');
    }
  };

  const deleteOne = async (id, name) => {
    if (!window.confirm(`Move the submission from ${name || 'this client'} to Trash? You'll have 7 days to restore it.`)) return;
    setDeleting(true);
    setUiError('');
    try {
      await axios.delete(`${API}/diet-reviews/${id}`, { headers: authHeaders() });
      if (selected?.id === id) { setSelected(null); setDetail(null); }
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      await load();
      setUndoToast({ ids: [id], label: `Moved ${name || 'submission'} to Trash` });
      setTimeout(() => setUndoToast(t => (t && t.ids.length === 1 && t.ids[0] === id) ? null : t), 8000);
    } catch (e) {
      setUiError(e.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Move ${count} submission${count === 1 ? '' : 's'} to Trash? You'll have 7 days to restore them.`)) return;
    setDeleting(true);
    setUiError('');
    try {
      const ids = Array.from(selectedIds);
      const r = await axios.post(`${API}/diet-reviews/bulk-delete`, { ids }, { headers: authHeaders() });
      if (selected && ids.includes(selected.id)) { setSelected(null); setDetail(null); }
      setSelectedIds(new Set());
      await load();
      if (r.data?.deleted === 0) setUiError('No submissions were moved to Trash.');
      else {
        setUndoToast({ ids, label: `Moved ${r.data.deleted} submission${r.data.deleted === 1 ? '' : 's'} to Trash` });
        setTimeout(() => setUndoToast(t => (t && t.ids === ids) ? null : t), 8000);
      }
    } catch (e) {
      setUiError(e.response?.data?.detail || e.message || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const generateLink = async () => {
    if (!genForm.name) { alert('Please enter the client name first.'); return; }
    try {
      const r = await axios.post(`${API}/diet-review/token`,
        { name: genForm.name, email: genForm.email || '', hours: 72 },
        { headers: authHeaders() });
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}${r.data.url}`;
      const message = `Hi ${genForm.name}, thank you for your payment! Please fill your diet review form here: ${fullUrl}\nThis link expires in 72 hours. Anjum will send your report within 48 hours.`;
      try {
        await navigator.clipboard.writeText(message);
        setGenForm(g => ({ ...g, copyState: 'WhatsApp message copied to clipboard.' }));
      } catch (e) {
        window.prompt('Copy this WhatsApp message:', message);
        setGenForm(g => ({ ...g, copyState: 'Message ready (copy from prompt)' }));
      }
      setTimeout(() => setGenForm(g => ({ ...g, copyState: '' })), 5000);
    } catch (e) {
      alert('Could not generate link: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── Styles ──
  const TEAL = '#0F4C5C';
  const CREAM = '#F7F3EE';
  const cardStyle = { background: CREAM, borderRadius: 14, padding: '18px 20px', marginBottom: 14, border: '1px solid #E8E2D6' };
  const sectionH = { fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.05rem', color: TEAL, marginBottom: 12, letterSpacing: '0.01em' };
  const fieldLabel = { fontSize: '.72rem', fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 };
  const fieldValue = { fontSize: '.92rem', color: TEAL, fontWeight: 500, lineHeight: 1.55, wordBreak: 'break-word' };

  const renderField = (label, value) => (
    <div key={label} style={{ marginBottom: 10 }}>
      <div style={fieldLabel}>{label}</div>
      <div style={fieldValue}>{formatValue(value)}</div>
    </div>
  );

  return (
    <div data-testid="admin-diet-reviews" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>
          Diet Review Submissions ({reviews.length})
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="reviews-refresh-btn" onClick={load} className="btn-ghost btn-sm" title="Refresh">↻ Refresh</button>
          <button data-testid="reviews-trash-btn" onClick={openTrash} className="btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Show recently deleted submissions">
            <Trash2 size={14} /> Trash
          </button>
          <button data-testid="generate-form-link-btn" onClick={() => setGenForm(g => ({ ...g, show: !g.show }))} className="btn-teal btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <LinkIcon size={14} /> Generate Form Link
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input data-testid="reviews-search" className="form-input" placeholder="Search name, email, phone…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} style={{ flex: 1, minWidth: 200 }} />
        <select data-testid="reviews-status-filter" className="form-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={{ minWidth: 170 }}>
          <option value="">All Status</option>
          <option value="pending_review">Pending Review</option>
          <option value="report_ready">Report Ready</option>
          <option value="sent">Sent</option>
        </select>
        <input data-testid="reviews-date-from" type="date" className="form-input" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} style={{ width: 150 }} title="From date" />
        <input data-testid="reviews-date-to" type="date" className="form-input" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} style={{ width: 150 }} title="To date" />
        {(filters.search || filters.status || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ search: '', status: '', date_from: '', date_to: '' })} className="btn-ghost btn-sm">Clear</button>
        )}
      </div>

      {genForm.show && (
        <div data-testid="generate-form-link-panel" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 18 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1rem', color: 'var(--ink)', marginBottom: 6 }}>Generate a 72-hour Diet Review form link</h3>
          <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 12 }}>Fill the buyer's name (and email if available), then paste the resulting WhatsApp message into your chat with the client.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <label className="form-label">Client name</label>
              <input data-testid="genlink-name" className="form-input" value={genForm.name} onChange={e => setGenForm(g => ({ ...g, name: e.target.value }))} placeholder="Priya Sharma" />
            </div>
            <div>
              <label className="form-label">Email (optional)</label>
              <input data-testid="genlink-email" className="form-input" value={genForm.email} onChange={e => setGenForm(g => ({ ...g, email: e.target.value }))} placeholder="priya@email.com" />
            </div>
            <button data-testid="genlink-create" onClick={generateLink} className="btn-teal btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Copy size={14} /> Create & Copy
            </button>
          </div>
          {genForm.copyState && <div data-testid="genlink-copied" style={{ marginTop: 12, padding: 10, background: '#D1FAE5', color: '#065F46', borderRadius: 8, fontSize: '.85rem', fontWeight: 600 }}>{genForm.copyState}</div>}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '380px 1fr' : '1fr', gap: '16px' }}>
          {/* LEFT — submission list */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
            {/* Bulk action bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: '#FAFAF7', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: reviews.length === 0 ? 'not-allowed' : 'pointer', fontSize: '.78rem', color: 'var(--mid)', fontWeight: 600 }}>
                <input
                  data-testid="reviews-select-all"
                  type="checkbox"
                  disabled={reviews.length === 0}
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                  style={{ width: 16, height: 16, accentColor: '#0F4C5C', cursor: 'inherit' }}
                />
                Select all
              </label>
              {selectedIds.size > 0 && (
                <>
                  <span data-testid="reviews-selected-count" style={{ fontSize: '.78rem', color: '#0F4C5C', fontWeight: 700 }}>{selectedIds.size} selected</span>
                  <button
                    data-testid="reviews-bulk-delete-btn"
                    onClick={bulkDelete}
                    disabled={deleting}
                    className="btn-ghost btn-sm"
                    style={{ marginLeft: 'auto', color: '#991B1B', border: '1px solid #FCA5A5', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: deleting ? 0.6 : 1 }}
                  >
                    <Trash2 size={13} /> {deleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
                  </button>
                </>
              )}
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
            {reviews.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>No submissions match.</div>
            ) : reviews.map((r, i) => {
              const st = deriveStatus(r);
              const badge = STATUS_BADGE[st];
              const isChecked = selectedIds.has(r.id);
              return (
                <div key={r.id} data-testid={`review-row-${r.id}`}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px 12px 16px', borderBottom: i < reviews.length - 1 ? '1px solid var(--border)' : 'none', background: selected?.id === r.id ? 'var(--teal-lt)' : (isChecked ? '#F5F8F9' : '#fff'), transition: 'background 0.15s' }}>
                  <input
                    data-testid={`review-select-${r.id}`}
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => { e.stopPropagation(); toggleSelect(r.id); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: 4, width: 15, height: 15, accentColor: '#0F4C5C', cursor: 'pointer', flexShrink: 0 }}
                    aria-label="Select submission"
                  />
                  <div onClick={() => openReview(r)} style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--ink)', marginBottom: 2 }}>{r.full_name || '—'}</div>
                        <div style={{ fontSize: '0.74rem', color: 'var(--mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: 4 }}>
                          {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                        </div>
                      </div>
                      <span data-testid={`review-status-badge-${r.id}`} style={{ background: badge.bg, color: badge.fg, padding: '3px 9px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{badge.label}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteOne(r.id, r.full_name); }}
                    disabled={deleting}
                    title="Delete this submission"
                    aria-label="Delete submission"
                    style={{ background: 'none', border: 'none', cursor: deleting ? 'wait' : 'pointer', color: '#991B1B', padding: 4, opacity: deleting ? 0.5 : 0.7, flexShrink: 0 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = deleting ? 0.5 : 0.7}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
            </div>
          </div>

          {/* RIGHT — submission detail */}
          {selected && detail && (
            <div data-testid="review-detail" style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--border)', padding: '22px', maxHeight: '82vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.3rem', color: TEAL, marginBottom: 4 }}>{detail.full_name}</h3>
                  <div style={{ fontSize: '.8rem', color: 'var(--mid)' }}>{detail.email} · {detail.whatsapp || detail.phone || '—'}</div>
                  <div style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 2 }}>Submitted: {detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={copyAll} data-testid="copy-all-data-btn" className="btn-teal btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Copy size={14} /> {copiedAll ? 'Copied ✓' : 'Copy All Data'}
                  </button>
                  <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)' }} aria-label="Close"><X size={18} /></button>
                </div>
              </div>

              {uiError && (
                <div data-testid="review-detail-error" style={{ background: '#FEE2E2', color: '#7F1D1D', padding: '10px 14px', borderRadius: 10, marginBottom: 14, fontSize: '.85rem' }}>
                  {uiError}
                </div>
              )}

              {/* Card sections */}
              {SECTIONS.map(sec => (
                <div key={sec.title} data-testid={`section-${sec.title.toLowerCase().replace(/\s|&/g, '-')}`} style={cardStyle}>
                  <div style={sectionH}>{sec.title}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 24px' }}>
                    {sec.fields.map(([label, key]) => renderField(label, detail[key]))}
                  </div>
                </div>
              ))}

              {/* Report Management — shared component */}
              <ReportManagementCard
                detail={detail}
                uploading={uploading}
                sending={sending}
                onUpload={onPdfPicked}
                onSend={sendReport}
                onRemove={removeReport}
                uploadTestId="upload-report-btn"
                uploadInputTestId="upload-report-input"
                sendTestId="send-report-btn"
                removeTestId="remove-report-btn"
                sentTestId="sent-timestamp"
                cardTestId="report-management"
              />
            </div>
          )}
        </div>
      )}

      {/* Undo toast — shown briefly after a soft-delete */}
      {undoToast && (
        <div data-testid="undo-toast" style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: '#0F4C5C', color: '#fff', padding: '12px 18px', borderRadius: 10, boxShadow: '0 8px 32px rgba(15,76,92,.3)', display: 'flex', alignItems: 'center', gap: 14, zIndex: 1200, fontSize: '.88rem' }}>
          <span>{undoToast.label}.</span>
          <button data-testid="undo-toast-btn" onClick={() => restoreMany(undoToast.ids)} style={{ background: '#E07A5F', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
            Undo
          </button>
          <button onClick={() => setUndoToast(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Trash modal — soft-deleted submissions, 7-day undo window */}
      {showTrash && (
        <div data-testid="trash-modal" onClick={() => setShowTrash(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,76,92,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem', color: '#0F4C5C', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Trash2 size={18} /> Trash ({trashItems.length})
                </h3>
                <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: 3 }}>Soft-deleted submissions · auto-purged 7 days after deletion</div>
              </div>
              <button data-testid="trash-close-btn" onClick={() => setShowTrash(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)' }} aria-label="Close"><X size={20} /></button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {trashLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div>
              ) : trashItems.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>
                  <Trash2 size={28} style={{ opacity: .3, marginBottom: 10 }} /><br/>
                  Trash is empty.
                </div>
              ) : trashItems.map((r, i) => {
                const days = r.purge_in_days;
                const urgency = (days != null && days < 2) ? '#991B1B' : (days != null && days < 4) ? '#92400E' : '#166534';
                return (
                  <div key={r.id} data-testid={`trash-row-${r.id}`} style={{ padding: '14px 22px', borderBottom: i < trashItems.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 2 }}>{r.full_name || '—'}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                      <div style={{ fontSize: '.72rem', color: urgency, marginTop: 4, fontWeight: 600 }}>
                        {days != null ? `Auto-purges in ${days} day${days === 1 ? '' : 's'}` : 'Awaiting purge'}
                      </div>
                    </div>
                    <button
                      data-testid={`trash-restore-${r.id}`}
                      onClick={() => restoreOne(r.id)}
                      className="btn-teal btn-sm"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      ↩ Restore
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Health Tags (multi-select with category groups) ──────────────────────────
const HEALTH_TAG_GROUPS = [
  { label: 'General Health', tags: ['General Wellness', 'Weight Loss', 'Weight Gain', 'Sports Nutrition', 'Senior Nutrition (60+)', 'Child and Teen Nutrition'] },
  { label: 'Hormonal and Metabolic', tags: ['PCOS', 'Thyroid', 'Diabetes', 'Pre-Diabetes', 'Insulin Resistance', 'Cholesterol', 'Blood Pressure', 'Fatty Liver', 'Heart Health'] },
  { label: "Women's Health", tags: ['Pregnancy', 'Post-Pregnancy', 'Menopause', 'Hormonal Imbalance'] },
  { label: 'Gut and Immunity', tags: ['Gut Health', 'IBS', 'Food Allergies and Intolerances', 'Low Immunity'] },
  { label: 'Deficiencies', tags: ['Anaemia', 'Vitamin D Deficiency', 'B12 Deficiency', 'Iron Deficiency'] },
  { label: 'Lifestyle', tags: ['Stress and Sleep', 'Skin and Hair', 'Kidney Health', 'Customised Plan'] },
];
const HEALTH_TAG_PILL_BG = '#0F4C5C';

function HealthTagsSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const q = query.trim().toLowerCase();
  const filteredGroups = HEALTH_TAG_GROUPS.map(g => ({
    label: g.label,
    tags: g.tags.filter(t => !q || t.toLowerCase().includes(q)),
  })).filter(g => g.tags.length > 0);

  const add = (tag) => {
    if (selected.includes(tag)) return;
    onChange([...selected, tag]);
    setQuery('');
  };
  const remove = (tag) => onChange(selected.filter(t => t !== tag));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} data-testid="health-tags-select">
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: 42,
          padding: '6px 10px',
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          cursor: 'text',
        }}
      >
        {selected.map(tag => (
          <span
            key={tag}
            data-testid="health-tag-pill"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: HEALTH_TAG_PILL_BG,
              color: '#fff',
              fontSize: '0.74rem',
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 999,
              lineHeight: 1.2,
            }}
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(tag); }}
              aria-label={`Remove ${tag}`}
              data-testid="health-tag-pill-remove"
              style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1, display: 'inline-flex' }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          data-testid="health-tags-search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? 'Search and select health tags...' : ''}
          style={{
            flex: 1,
            minWidth: 140,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: '6px 4px',
            fontSize: '.85rem',
            color: 'var(--ink)',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {open && (
        <div
          data-testid="health-tags-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 12px 40px rgba(15,76,92,.18)',
            maxHeight: 280,
            overflow: 'auto',
            padding: '6px 0',
          }}
        >
          {filteredGroups.length === 0 ? (
            <div style={{ padding: '12px 14px', color: 'var(--mid)', fontSize: '.82rem' }}>No matching tags.</div>
          ) : filteredGroups.map(g => (
            <div key={g.label}>
              <div style={{ padding: '8px 14px 4px', fontSize: '.68rem', fontWeight: 700, color: '#6B7E84', textTransform: 'uppercase', letterSpacing: '.06em' }}>{g.label}</div>
              {g.tags.map(t => {
                const isSelected = selected.includes(t);
                return (
                  <div
                    key={t}
                    onClick={() => add(t)}
                    data-testid="health-tag-option"
                    style={{
                      padding: '7px 14px',
                      cursor: isSelected ? 'default' : 'pointer',
                      color: isSelected ? '#9CA3AF' : 'var(--ink)',
                      fontSize: '.85rem',
                      background: isSelected ? '#F7F3EE' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#F7F3EE'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                  >
                    {isSelected && <Check size={12} color="#0F4C5C" />}
                    {t}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact pill row for displaying health tags on the client list view.
function HealthTagsInline({ tags }) {
  const list = Array.isArray(tags) ? tags : [];
  if (list.length === 0) return null;
  return (
    <div data-testid="client-row-health-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
      {list.map(t => (
        <span
          key={t}
          style={{
            background: HEALTH_TAG_PILL_BG,
            color: '#fff',
            fontSize: '0.66rem',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
            lineHeight: 1.3,
            letterSpacing: '.01em',
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

// ─── Diet Plans Modal (per client) ────────────────────────────────────────────
function DietPlansModal({ client, authHeaders, onClose }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'rich', title: '', content: '', file_url: '', file_name: '', personal_note: '' });
  const [uploading, setUploading] = useState(false);
  const [suggestingNote, setSuggestingNote] = useState(false);
  const [suggestError, setSuggestError] = useState('');

  // Format an ISO timestamp as "DD MMM YYYY at HH:MM" in the viewer's local zone.
  // Returns "" if the value is missing or unparseable.
  const formatEmailSent = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dd = String(d.getDate()).padStart(2, '0');
    const mmm = months[d.getMonth()];
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd} ${mmm} ${yyyy} at ${hh}:${mi}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/v2/clients/${client.client_id}/diet-plans`, { headers: authHeaders() });
    setPlans(r.data); setLoading(false);
  }, [client.client_id, authHeaders]);
  useEffect(() => { load(); }, [load]);

  const computeDefaultTitle = useCallback(() => {
    const firstName = (client?.name || '').trim().split(/\s+/)[0] || 'Client';
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dateStr = `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
    return `Diet Plan - ${firstName} - ${dateStr}`;
  }, [client]);

  const openAdd = () => {
    setForm({ type: 'rich', title: computeDefaultTitle(), content: '', file_url: '', file_name: '', personal_note: '' });
    setSuggestError('');
    setShowAdd(true);
  };

  const suggestNote = async () => {
    setSuggestError('');
    setSuggestingNote(true);
    try {
      const r = await axios.post(
        `${API}/v2/clients/${client.client_id}/suggest-diet-plan-note`,
        { title: form.title || '' },
        { headers: authHeaders() },
      );
      const note = (r.data && r.data.note) || '';
      if (!note) throw new Error('Empty');
      setForm(f => ({ ...f, personal_note: note }));
    } catch (e) {
      setSuggestError('Could not generate a note. Please type one manually.');
    } finally {
      setSuggestingNote(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/upload/diet-plan-file`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, file_url: r.data.url, file_name: r.data.filename, type: 'file' }));
    } catch (er) { alert(er.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const save = async () => {
    if (!form.title) { alert('Title is required'); return; }
    if (form.type === 'file' && !form.file_url) { alert('Upload a file first'); return; }
    if (form.type === 'rich' && !form.content) { alert('Type the diet plan content'); return; }
    const r = await axios.post(`${API}/v2/clients/${client.client_id}/diet-plans`, {
      type: form.type, title: form.title, content: form.content, file_url: form.file_url, file_name: form.file_name, personal_note: form.personal_note,
    }, { headers: authHeaders() });
    if (r.data && r.data.email_warning) {
      alert(r.data.email_warning);
    }
    setForm({ type: 'rich', title: '', content: '', file_url: '', file_name: '', personal_note: '' });
    setShowAdd(false); load();
  };
  const del = async (planId) => { if (window.confirm('Delete this diet plan? This cannot be undone.')) { await axios.delete(`${API}/v2/clients/${client.client_id}/diet-plans/${planId}`, { headers: authHeaders() }); load(); } };

  const [resendingId, setResendingId] = useState(null);
  const resendEmail = async (planId) => {
    const clientEmail = (client?.email || '').trim();
    if (!clientEmail) {
      alert('This client has no email address on file. Add one before resending.');
      return;
    }
    if (!window.confirm(`Resend notification email to ${clientEmail}?`)) return;
    setResendingId(planId);
    try {
      const r = await axios.post(
        `${API}/v2/clients/${client.client_id}/diet-plans/${planId}/resend-email`,
        {},
        { headers: authHeaders() }
      );
      // Update the row's timestamp locally so the caption refreshes immediately.
      const newAt = r.data?.email_sent_at;
      if (newAt) {
        setPlans(prev => prev.map(p => p.id === planId ? { ...p, email_sent_at: newAt, email_sent_to: r.data?.email_sent_to || p.email_sent_to } : p));
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not resend the email. Please try again.');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", margin: 0, fontSize: '1.3rem' }}>Diet Plans · {client.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--mid)' }}><X /></button>
        </div>

        {!showAdd && (
          <button onClick={openAdd} className="btn-teal btn-sm" data-testid="add-diet-plan-btn" style={{ marginBottom: 16 }}><Plus size={14} />Add New Diet Plan</button>
        )}
        {showAdd && (
          <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={() => setForm(f => ({ ...f, type: 'rich' }))} className={form.type === 'rich' ? 'btn-teal btn-sm' : 'btn-ghost btn-sm'} data-testid="plan-type-rich">Rich Text</button>
              <button onClick={() => setForm(f => ({ ...f, type: 'file' }))} className={form.type === 'file' ? 'btn-teal btn-sm' : 'btn-ghost btn-sm'} data-testid="plan-type-file">Upload File</button>
            </div>
            <label className="form-label">Title *</label>
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={computeDefaultTitle()} style={{ marginBottom: 10 }} data-testid="plan-title-input" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label className="form-label" style={{ margin: 0 }}>Personal note to client (optional)</label>
              <button
                type="button"
                onClick={suggestNote}
                disabled={suggestingNote}
                data-testid="suggest-note-btn"
                className="btn-ghost btn-sm"
                style={{ padding: '5px 10px', fontSize: '.74rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {suggestingNote && (
                  <span
                    aria-hidden="true"
                    style={{ width: 12, height: 12, border: '2px solid #C4C9CC', borderTopColor: '#0F4C5C', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }}
                  />
                )}
                {suggestingNote ? 'Generating...' : 'Suggest a Note'}
              </button>
            </div>
            <textarea
              className="form-input"
              data-testid="personal-note-input"
              value={form.personal_note}
              onChange={e => setForm(f => ({ ...f, personal_note: e.target.value }))}
              rows={3}
              placeholder="e.g. Priya, focus on the meal timings in Week 1 before anything else."
              style={{ marginBottom: 10, fontFamily: 'inherit' }}
            />
            {suggestError && (
              <div data-testid="suggest-note-error" style={{ marginTop: -4, marginBottom: 10, fontSize: '.74rem', color: '#C0392B' }}>{suggestError}</div>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            {form.type === 'rich' ? (
              <>
                <label className="form-label">Content *</label>
                <textarea className="form-input" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={10} placeholder="Type the full diet plan here. Plain text or simple markdown supported." style={{ fontFamily: 'inherit' }} data-testid="plan-content-input" />
              </>
            ) : (
              <>
                <label className="form-label">Upload File *</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'var(--teal-lt)', color: 'var(--teal)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                  <Upload size={14} />{uploading ? 'Uploading…' : (form.file_url ? 'Replace file' : 'Choose file')}
                  <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*" onChange={handleFile} disabled={uploading} style={{ display: 'none' }} data-testid="plan-file-input" />
                </label>
                <div style={{ marginTop: 6, fontSize: '0.74rem', color: 'var(--mid)' }}>Accepts PDF, Word (.doc, .docx), Excel (.xls, .xlsx), and images. Max 40 MB.</div>
                {form.file_url && <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--teal)' }}>✓ Uploaded: <a href={form.file_url} target="_blank" rel="noopener noreferrer">{form.file_name || form.file_url}</a></div>}
              </>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button onClick={save} className="btn-teal btn-sm" data-testid="save-plan-btn"><Check size={14} />Save</button>
              <button onClick={() => { setShowAdd(false); setForm({ type: 'rich', title: '', content: '', file_url: '', file_name: '', personal_note: '' }); setSuggestError(''); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
            </div>
          </div>
        )}

        {loading ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div> : plans.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--mid)', background: 'var(--cream)', borderRadius: 10 }}>No diet plans yet for this client.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {plans.map(p => (
              <div key={p.id} data-testid="plan-row" style={{ padding: 14, background: p.is_current ? 'var(--teal-lt)' : 'var(--cream)', borderRadius: 10, border: p.is_current ? '2px solid var(--teal)' : '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>v{p.version}{p.is_current ? ' · CURRENT' : ' · ARCHIVED'}</div>
                    <div style={{ fontWeight: 700, marginTop: 4 }}>{p.title}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--mid)', marginTop: 4 }}>{p.created_at?.slice(0, 10)} · by {p.created_by} · type: {p.type}</div>
                    {p.email_sent_at && (
                      <div data-testid="plan-email-sent" style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>Email sent on {formatEmailSent(p.email_sent_at)}</span>
                        <button
                          data-testid="resend-plan-email-btn"
                          onClick={() => resendEmail(p.id)}
                          disabled={resendingId === p.id}
                          title="Resend this notification email to the client"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px 6px',
                            margin: 0,
                            color: resendingId === p.id ? '#C4C9CC' : '#6B7E84',
                            fontSize: '0.72rem',
                            cursor: resendingId === p.id ? 'wait' : 'pointer',
                            textDecoration: 'underline',
                            fontFamily: 'inherit',
                          }}
                        >
                          {resendingId === p.id ? 'Resending...' : 'Resend Email'}
                        </button>
                      </div>
                    )}
                    {p.type === 'file' && p.file_url && <a href={p.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', color: 'var(--teal)', marginTop: 6, display: 'inline-block' }}>📄 View file</a>}
                    {p.type === 'rich' && p.content && (
                      <details style={{ marginTop: 8, fontSize: '0.85rem' }}>
                        <summary style={{ cursor: 'pointer', color: 'var(--mid)', fontSize: '0.78rem' }}>Show content</summary>
                        <pre style={{ whiteSpace: 'pre-wrap', marginTop: 6, fontFamily: 'inherit', background: '#fff', padding: 10, borderRadius: 6, maxHeight: 220, overflow: 'auto' }}>{p.content}</pre>
                      </details>
                    )}
                  </div>
                  <button onClick={() => del(p.id)} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--coral)' }} data-testid="delete-plan-btn"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subscription Actions Popover ─────────────────────────────────────────────
function SubscriptionActions({ client, authHeaders, onChange }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pause, setPause] = useState({ days: 7, reason: '' });
  const [extend, setExtend] = useState({ days: 30, reason: '' });
  const [adjust, setAdjust] = useState({ plan_expiry: client.plan_expiry || '', reason: '' });

  const call = async (path, body, method = 'post') => {
    setBusy(true);
    try {
      await axios[method](`${API}/v2/clients/${client.client_id}/${path}`, body, { headers: authHeaders() });
      setOpen(null); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {client.subscription_status !== 'paused' ? (
        <button onClick={() => setOpen('pause')} className="btn-ghost btn-sm" data-testid="open-pause-btn">Pause</button>
      ) : (
        <button onClick={() => call('resume', {})} disabled={busy} className="btn-teal btn-sm" data-testid="resume-btn">Resume</button>
      )}
      <button onClick={() => setOpen('extend')} className="btn-ghost btn-sm" data-testid="open-extend-btn">Extend</button>
      <button onClick={() => setOpen('adjust')} className="btn-ghost btn-sm" data-testid="open-adjust-btn">Adjust Expiry</button>

      {open && (
        <div onClick={() => setOpen(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 420 }}>
            <h3 style={{ fontFamily: "'Fraunces', serif", margin: '0 0 14px', fontSize: '1.1rem' }}>
              {open === 'pause' && `Pause plan for ${client.name}`}
              {open === 'extend' && `Extend plan for ${client.name}`}
              {open === 'adjust' && `Adjust expiry for ${client.name}`}
            </h3>
            {open === 'pause' && (<>
              <label className="form-label">Days to pause</label>
              <input className="form-input" type="number" min="1" max="365" value={pause.days} onChange={e => setPause(p => ({ ...p, days: Number(e.target.value) }))} data-testid="pause-days-input" />
              <label className="form-label" style={{ marginTop: 10 }}>Reason</label>
              <input className="form-input" value={pause.reason} onChange={e => setPause(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Travel, Surgery, etc." data-testid="pause-reason-input" />
            </>)}
            {open === 'extend' && (<>
              <label className="form-label">Extra days</label>
              <input className="form-input" type="number" min="1" max="365" value={extend.days} onChange={e => setExtend(p => ({ ...p, days: Number(e.target.value) }))} data-testid="extend-days-input" />
              <label className="form-label" style={{ marginTop: 10 }}>Reason</label>
              <input className="form-input" value={extend.reason} onChange={e => setExtend(p => ({ ...p, reason: e.target.value }))} placeholder="Loyalty, makeup time, etc." />
            </>)}
            {open === 'adjust' && (<>
              <label className="form-label">New Expiry Date (YYYY-MM-DD)</label>
              <input className="form-input" type="date" value={adjust.plan_expiry} onChange={e => setAdjust(p => ({ ...p, plan_expiry: e.target.value }))} data-testid="adjust-date-input" />
              <label className="form-label" style={{ marginTop: 10 }}>Reason</label>
              <input className="form-input" value={adjust.reason} onChange={e => setAdjust(p => ({ ...p, reason: e.target.value }))} />
            </>)}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button disabled={busy} onClick={() => {
                if (open === 'pause') call('pause', pause);
                else if (open === 'extend') call('extend', extend);
                else if (open === 'adjust') call('expiry', adjust, 'put');
              }} className="btn-teal btn-sm" data-testid="confirm-action-btn">{busy ? 'Saving…' : 'Confirm'}</button>
              <button onClick={() => setOpen(null)} className="btn-ghost btn-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Clients Manager (v2) ─────────────────────────────────────────────────────

// Slide-in right side panel showing the full client dossier: details, diet
// plans, reports (QDR + NAR), orders, invoices, and quick action shortcuts.
// Does NOT re-implement any existing flow. Quick actions delegate to the
// existing DietPlansModal, the existing /admin/invoices generator (pre-filled
// via URL params), and the existing edit form (openEdit prop).
function ClientDetailPanel({ client, authHeaders, onClose, onOpenDietPlans, onOpenEdit, onClientUpdated }) {
  const navigate = useNavigate();
  const [related, setRelated] = useState({ diet_reviews: [], assessment_reviews: [], orders: [], invoices: [] });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesDraft, setNotesDraft] = useState(client?.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()} at ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const loadAll = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const [rel, pl] = await Promise.all([
        axios.get(`${API}/v2/clients/${client.client_id}/related`, { headers: authHeaders() }),
        axios.get(`${API}/v2/clients/${client.client_id}/diet-plans`, { headers: authHeaders() }),
      ]);
      setRelated(rel.data);
      setPlans(pl.data);
    } catch (e) {
      // Silent fail to avoid breaking the panel render. Empty arrays already
      // initialised so the UI just shows "No items".
    } finally {
      setLoading(false);
    }
  }, [client, authHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { setNotesDraft(client?.notes || ''); }, [client]);

  // Escape-to-close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const r = await axios.put(`${API}/v2/clients/${client.client_id}`, { notes: notesDraft }, { headers: authHeaders() });
      onClientUpdated && onClientUpdated(r.data);
    } catch (e) {
      alert('Could not save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const resendPlanEmail = async (planId) => {
    const clientEmail = (client?.email || '').trim();
    if (!clientEmail) { alert('This client has no email address on file.'); return; }
    if (!window.confirm(`Resend notification email to ${clientEmail}?`)) return;
    setResendingId(planId);
    try {
      const r = await axios.post(`${API}/v2/clients/${client.client_id}/diet-plans/${planId}/resend-email`, {}, { headers: authHeaders() });
      const newAt = r.data?.email_sent_at;
      if (newAt) {
        setPlans(prev => prev.map(p => p.id === planId ? { ...p, email_sent_at: newAt, email_sent_to: r.data?.email_sent_to || p.email_sent_to } : p));
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not resend the email.');
    } finally {
      setResendingId(null);
    }
  };

  const downloadReport = async (kind, reviewId, filename) => {
    try {
      const url = kind === 'qdr' ? `${API}/diet-reviews/${reviewId}/report` : `${API}/assessment-reviews/${reviewId}/report`;
      const r = await axios.get(url, { headers: authHeaders(), responseType: 'blob' });
      const obj = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = obj; a.download = filename || `${kind}-${reviewId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(obj);
    } catch (e) {
      alert(e.response?.data?.detail || 'Download failed.');
    }
  };

  const goGenerateInvoice = () => {
    const qs = new URLSearchParams({
      new: '1',
      client_name: client.name || '',
      client_email: client.email || '',
      client_phone: client.mobile || '',
    }).toString();
    onClose();
    navigate(`/admin/invoices?${qs}`);
  };

  const STATUS_COLORS = { active: '#10B981', paused: '#F59E0B', expired: '#EF4444' };
  const status = client.effective_status || client.subscription_status || 'active';
  const sc = STATUS_COLORS[status] || '#9CA3AF';

  const SectionHeader = ({ children }) => (
    <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.05rem', color: 'var(--ink)', fontWeight: 700, margin: '22px 0 10px' }}>{children}</div>
  );
  const Pill = ({ children, bg = '#0F4C5C', fg = '#fff' }) => (
    <span style={{ display: 'inline-block', background: bg, color: fg, fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, lineHeight: 1.3, marginRight: 6, marginBottom: 4 }}>{children}</span>
  );
  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--border)', gap: 12 }}>
      <div style={{ fontSize: '.74rem', color: 'var(--mid)', width: 140, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '.88rem', color: 'var(--ink)', flex: 1, wordBreak: 'break-word' }}>{value || <span style={{ color: '#9CA3AF' }}>Not set</span>}</div>
    </div>
  );

  const PLAN_LABELS = {
    'one-time': 'One-Time Consultation',
    'quick-diet-review': 'Quick Diet Review',
    'nutrition-assessment-report': 'Nutrition Assessment Report',
    '1-month': '1 Month',
    '3-months': '3 Months',
    '6-months': '6 Months',
    'annual': 'Annual',
    'customised-plan': 'Customised Plan',
  };

  return (
    <div
      data-testid="client-detail-panel-overlay"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,76,92,.42)', zIndex: 300, backdropFilter: 'blur(2px)' }}
    >
      <div
        data-testid="client-detail-panel"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(520px, 100vw)',
          background: '#fff',
          boxShadow: '-24px 0 60px rgba(15,76,92,.25)',
          overflowY: 'auto',
          animation: 'slideInRight .25s ease-out',
          padding: '22px 24px 32px',
        }}
      >
        <style>{`@keyframes slideInRight { from { transform: translateX(40px); opacity: .6; } to { transform: translateX(0); opacity: 1; } }`}</style>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.5rem', fontWeight: 800, color: 'var(--ink)', lineHeight: 1.2 }}>{client.name}</div>
            <div style={{ marginTop: 4, fontSize: '.74rem', color: 'var(--mid)' }}>
              <code style={{ background: 'var(--cream)', padding: '1px 6px', borderRadius: 4 }}>{client.client_id}</code>
            </div>
          </div>
          <button
            data-testid="close-client-panel-btn"
            onClick={onClose}
            aria-label="Close panel"
            style={{ background: 'var(--cream)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: 'var(--ink)' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Section 1: Client Details */}
        <SectionHeader>Client Details</SectionHeader>
        <Row label="Full Name" value={client.name} />
        <Row label="Email" value={client.email} />
        <Row label="WhatsApp" value={client.mobile} />
        <Row label="Date of Birth" value={fmtDate(client.date_of_birth)} />
        <Row label="Plan Type" value={PLAN_LABELS[client.plan_type] || client.plan_type} />
        <Row label="Plan Start" value={client.plan_start} />
        <Row label="Plan Expiry" value={client.plan_expiry} />
        <div style={{ display: 'flex', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--border)', gap: 12 }}>
          <div style={{ fontSize: '.74rem', color: 'var(--mid)', width: 140, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>Status</div>
          <div style={{ flex: 1 }}>
            <span data-testid="panel-status-badge" style={{ display: 'inline-block', background: sc + '22', color: sc, padding: '4px 12px', borderRadius: 999, fontSize: '.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{status}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)', gap: 12 }}>
          <div style={{ fontSize: '.74rem', color: 'var(--mid)', width: 140, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600, paddingTop: 2 }}>Health Tags</div>
          <div style={{ flex: 1 }}>
            {Array.isArray(client.health_tags) && client.health_tags.length > 0
              ? client.health_tags.map(t => <Pill key={t}>{t}</Pill>)
              : <span style={{ color: '#9CA3AF', fontSize: '.88rem' }}>None</span>}
          </div>
        </div>
        <div style={{ padding: '10px 0' }}>
          <div style={{ fontSize: '.74rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600, marginBottom: 4 }}>Notes</div>
          <textarea
            data-testid="panel-notes-textarea"
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
            rows={3}
            className="form-input"
            placeholder="Internal notes about this client (visible to admin only)."
            style={{ fontFamily: 'inherit' }}
          />
          {notesDraft !== (client?.notes || '') && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button data-testid="panel-save-notes-btn" onClick={saveNotes} disabled={savingNotes} className="btn-teal btn-sm">{savingNotes ? 'Saving...' : 'Save Notes'}</button>
              <button onClick={() => setNotesDraft(client?.notes || '')} disabled={savingNotes} className="btn-ghost btn-sm">Cancel</button>
            </div>
          )}
        </div>

        {/* Section 2: Diet Plans */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22, marginBottom: 10 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: '1.05rem', color: 'var(--ink)', fontWeight: 700 }}>Diet Plans</div>
          <button data-testid="panel-upload-diet-plan-btn" onClick={onOpenDietPlans} className="btn-teal btn-sm"><Plus size={13} />Upload New</button>
        </div>
        {loading ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', fontSize: '.84rem' }}>Loading...</div>
        ) : plans.length === 0 ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', background: 'var(--cream)', borderRadius: 8, fontSize: '.84rem' }}>No diet plans yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plans.map(p => (
              <div key={p.id} data-testid="panel-plan-row" style={{ padding: 10, background: p.is_current ? 'var(--teal-lt)' : 'var(--cream)', borderRadius: 8, border: p.is_current ? '1px solid var(--teal)' : '1px solid var(--border)' }}>
                <div style={{ fontSize: '.7rem', color: 'var(--teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>v{p.version}{p.is_current ? ' · Current' : ''}</div>
                <div style={{ fontWeight: 700, fontSize: '.88rem', marginTop: 2 }}>{p.title}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: 2 }}>{p.created_at?.slice(0, 10)}{p.email_sent_at ? ` · Email sent ${fmtDateTime(p.email_sent_at)}` : ''}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {p.type === 'file' && p.file_url && (
                    <a href={p.file_url} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-sm" style={{ textDecoration: 'none' }}>View File</a>
                  )}
                  <button data-testid="panel-resend-plan-email-btn" onClick={() => resendPlanEmail(p.id)} disabled={resendingId === p.id} className="btn-ghost btn-sm">{resendingId === p.id ? 'Resending...' : 'Resend Email'}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Section 3: Reports */}
        <SectionHeader>Reports</SectionHeader>
        {loading ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', fontSize: '.84rem' }}>Loading...</div>
        ) : (related.diet_reviews.length === 0 && related.assessment_reviews.length === 0) ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', background: 'var(--cream)', borderRadius: 8, fontSize: '.84rem' }}>No reports sent yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {related.diet_reviews.map(r => (
              <div key={'qdr-' + r.id} data-testid="panel-report-row" style={{ padding: 10, background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: '.66rem', fontWeight: 700, color: '#0F4C5C', background: '#A8CDD5', padding: '2px 8px', borderRadius: 999, letterSpacing: '.06em' }}>QDR</span>
                    <div style={{ fontSize: '.82rem', fontWeight: 700, marginTop: 4 }}>Diet Review Report</div>
                    <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: 2 }}>Submitted {fmtDate(r.submitted_at)}{r.report_sent_at ? ` · Sent ${fmtDate(r.report_sent_at)}` : ''}</div>
                  </div>
                  {r.report_filename && (
                    <button onClick={() => downloadReport('qdr', r.id, r.report_filename)} className="btn-ghost btn-sm"><Download size={12} />Download</button>
                  )}
                </div>
              </div>
            ))}
            {related.assessment_reviews.map(r => (
              <div key={'nar-' + r.id} data-testid="panel-report-row" style={{ padding: 10, background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: '.66rem', fontWeight: 700, color: '#fff', background: '#0F4C5C', padding: '2px 8px', borderRadius: 999, letterSpacing: '.06em' }}>NAR</span>
                    <div style={{ fontSize: '.82rem', fontWeight: 700, marginTop: 4 }}>Nutrition Assessment Report</div>
                    <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: 2 }}>Submitted {fmtDate(r.submitted_at)}{r.report_sent_at ? ` · Sent ${fmtDate(r.report_sent_at)}` : ''}</div>
                  </div>
                  {r.report_filename && (
                    <button onClick={() => downloadReport('nar', r.id, r.report_filename)} className="btn-ghost btn-sm"><Download size={12} />Download</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Section 4: Orders and Payments */}
        <SectionHeader>Orders and Payments</SectionHeader>
        {loading ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', fontSize: '.84rem' }}>Loading...</div>
        ) : related.orders.length === 0 ? (
          <div style={{ padding: 14, textAlign: 'center', color: 'var(--mid)', background: 'var(--cream)', borderRadius: 8, fontSize: '.84rem' }}>No orders yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {related.orders.map(o => {
              const paid = (o.payment_status || '').toLowerCase() === 'paid';
              return (
                <div key={o.order_id} data-testid="panel-order-row" style={{ padding: 10, background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '.84rem', fontWeight: 700 }}>{o.product_name || 'Order'}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: 2 }}>
                        <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 4 }}>{o.order_id}</code>
                        {o.created_at ? ` · ${fmtDate(o.created_at)}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '.92rem', fontWeight: 800, color: 'var(--teal)' }}>Rs. {Number(o.amount || 0).toLocaleString('en-IN')}</div>
                      <span style={{ display: 'inline-block', marginTop: 3, fontSize: '.66rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.04em', background: paid ? '#10B98122' : '#F59E0B22', color: paid ? '#10B981' : '#F59E0B' }}>{o.payment_status || 'pending'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Section 5: Quick Actions */}
        <SectionHeader>Quick Actions</SectionHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button data-testid="panel-action-upload-plan" onClick={onOpenDietPlans} className="btn-teal btn-sm" style={{ justifyContent: 'flex-start' }}>
            <BookOpen size={14} />Upload Diet Plan
          </button>
          <button data-testid="panel-action-generate-invoice" onClick={goGenerateInvoice} className="btn-ghost btn-sm" style={{ justifyContent: 'flex-start' }}>
            <FileText size={14} />Generate Invoice
          </button>
          <button data-testid="panel-action-edit-client" onClick={onOpenEdit} className="btn-ghost btn-sm" style={{ justifyContent: 'flex-start' }}>
            <Edit size={14} />Edit Client Details
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkEmailModal({ onClose, authHeaders }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [filter, setFilter] = useState('all');
  const [planType, setPlanType] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { alert('Subject and message body are required'); return; }
    if (!window.confirm('Send this announcement now? This action cannot be undone.')) return;
    setBusy(true); setResult(null);
    try {
      // Convert plain-text newlines to HTML paragraphs/breaks
      const html = body.split(/\n\n+/).map(p => `<p style="margin:0 0 14px;">${p.replace(/\n/g, '<br/>')}</p>`).join('');
      const r = await axios.post(`${API}/admin/clients/bulk-email`, {
        subject, body_html: html, recipient_filter: filter, plan_type: filter === 'by_plan' ? planType : '',
      }, { headers: authHeaders() });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-email-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.3rem', color: 'var(--ink)' }}>📣 Send Announcement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--mid)' }}>×</button>
        </div>
        {result && result.success && (
          <div data-testid="bulk-email-success" style={{ background: '#eaf6ef', color: '#1e6b3e', padding: 12, borderRadius: 8, fontSize: '.85rem', marginBottom: 14 }}>
            ✅ Sent to {result.sent} of {result.total} recipients{result.failed ? ` · ${result.failed} failed` : ''}.
          </div>
        )}
        {result && result.error && (
          <div style={{ background: '#fdf0ed', color: '#c0392b', padding: 12, borderRadius: 8, fontSize: '.85rem', marginBottom: 14 }}>
            ❌ {result.error}
          </div>
        )}
        <label className="form-label">Recipients</label>
        <select className="form-input" data-testid="bulk-email-filter" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All clients</option>
          <option value="active">Active subscribers only</option>
          <option value="by_plan">By plan type</option>
        </select>
        {filter === 'by_plan' && (
          <input className="form-input" data-testid="bulk-email-plan-type" placeholder="e.g. 3-month, annual, one-time" value={planType} onChange={e => setPlanType(e.target.value)} style={{ marginTop: 8 }} />
        )}
        <label className="form-label" style={{ marginTop: 14 }}>Subject</label>
        <input className="form-input" data-testid="bulk-email-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. New PCOS guide is here · 30% off this week" />
        <label className="form-label" style={{ marginTop: 14 }}>Message Body</label>
        <textarea className="form-input" data-testid="bulk-email-body" rows="8" value={body} onChange={e => setBody(e.target.value)} placeholder={"Hello,\n\nI'm excited to share..."} />
        <p style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 4 }}>Plain text. Double line breaks become paragraphs. Emails go via Resend with the standard branded header/footer.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} className="btn-ghost btn-sm">Cancel</button>
          <button onClick={send} disabled={busy} data-testid="bulk-email-send-btn" className="btn-teal btn-sm">{busy ? 'Sending…' : '✉️ Send to recipients'}</button>
        </div>
      </div>
    </div>
  );
}

function ClientsManager() {
  const { authHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dietFor, setDietFor] = useState(null);
  const [panelFor, setPanelFor] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const EMPTY = { name: '', email: '', mobile: '', plan_type: 'one-time', plan_start: '', notes: '', health_tags: [], date_of_birth: '', expiry_reminder_enabled: true };
  const [form, setForm] = useState(EMPTY);

  // Cross-component prefill: when navigated to with ?prefill_name=... query
  // params (e.g. from the OrderDetailDrawer Convert-to-Client flow), open the
  // Add Client form pre-populated, then strip the params from the URL so a
  // page refresh does not re-open the form.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.has('prefill_name') || sp.has('prefill_email')) {
      setForm(f => ({
        ...f,
        name:      sp.get('prefill_name')   || f.name,
        email:     sp.get('prefill_email')  || f.email,
        mobile:    sp.get('prefill_mobile') || f.mobile,
        plan_type: sp.get('prefill_plan')   || f.plan_type,
      }));
      setShowForm(true);
      navigate('/admin/clients', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/v2/clients`, { headers: authHeaders() });
    setClients(r.data); setLoading(false);
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const filteredClients = clients.filter(c => {
    if (statusFilter !== 'all' && (c.effective_status || c.subscription_status) !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.mobile || '').toLowerCase().includes(q);
  });

  const save = async () => {
    if (!form.name) { alert('Name is required'); return; }
    if (!form.plan_start) { alert('Plan Start date is required'); return; }
    if (editing) await axios.put(`${API}/v2/clients/${editing.client_id}`, form, { headers: authHeaders() });
    else await axios.post(`${API}/v2/clients`, form, { headers: authHeaders() });
    setShowForm(false); setEditing(null); setForm(EMPTY); load();
  };
  const del = async (cid) => { if (window.confirm('Delete client and all sessions?')) { await axios.delete(`${API}/v2/clients/${cid}`, { headers: authHeaders() }); load(); } };
  const copyMagicLink = async (c) => {
    if (!c.email) { alert('This client has no email on file. Add an email first.'); return; }
    try {
      const r = await axios.post(`${API}/v2/clients/${c.client_id}/magic-link`, {}, { headers: authHeaders() });
      await navigator.clipboard.writeText(r.data.link);
      alert(`✓ Magic link copied to clipboard!\n\nFor: ${r.data.email}\nExpires in: ${r.data.expires_in_minutes} minutes\n\nSend it to the client manually if email didn't arrive.`);
    } catch (err) {
      alert('Failed to generate magic link: ' + (err.response?.data?.detail || err.message));
    }
  };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name || '', email: c.email || '', mobile: c.mobile || '', plan_type: c.plan_type || 'one-time', plan_start: c.plan_start || '', plan_expiry: c.plan_expiry || '', expiry_reminder_enabled: c.expiry_reminder_enabled !== false, notes: c.notes || '', health_tags: Array.isArray(c.health_tags) ? c.health_tags : [], date_of_birth: c.date_of_birth || '' }); setShowForm(true); };

  const STATUS_COLORS = { active: '#10B981', paused: '#F59E0B', expired: '#EF4444' };
  const PLAN_LABELS = {
    'one-time': 'One-Time Consultation',
    'quick-diet-review': 'Quick Diet Review',
    'nutrition-assessment-report': 'Nutrition Assessment Report',
    '1-month': '1 Month',
    '3-months': '3 Months',
    '6-months': '6 Months',
    'annual': 'Annual',
    'customised-plan': 'Customised Plan',
  };

  // Plan types with a fixed duration: expiry = start + N months, auto-filled
  // and read-only. Plan types in PLAN_TYPES_HIDE_EXPIRY have no expiry concept
  // (One-Time Consultation, Quick Diet Review, Nutrition Assessment Report)
  // so the field is hidden entirely. Anything else falls through to manual
  // entry as a safety net.
  const PLAN_MONTHS = { '1-month': 1, '3-months': 3, '6-months': 6, 'annual': 12 };
  const PLAN_TYPES_HIDE_EXPIRY = new Set(['one-time', 'quick-diet-review', 'nutrition-assessment-report']);

  const addMonthsISO = (iso, months) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    const targetIdx = m - 1 + months;
    const ty = y + Math.floor(targetIdx / 12);
    const tm = ((targetIdx % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
    const cd = Math.min(d, lastDay);
    const pad = n => String(n).padStart(2, '0');
    return `${ty}-${pad(tm + 1)}-${pad(cd)}`;
  };

  const expiryFor = (planType, startDate) => {
    if (PLAN_TYPES_HIDE_EXPIRY.has(planType)) return '';
    const months = PLAN_MONTHS[planType];
    if (months && startDate) return addMonthsISO(startDate, months);
    return null; // null = leave existing value alone (manual-entry fallback)
  };

  const onPlanTypeChange = (newType) => {
    setForm(f => {
      const next = expiryFor(newType, f.plan_start);
      return { ...f, plan_type: newType, plan_expiry: next === null ? (f.plan_expiry || '') : next };
    });
  };
  const onPlanStartChange = (newStart) => {
    setForm(f => {
      const next = expiryFor(f.plan_type, newStart);
      return { ...f, plan_start: newStart, plan_expiry: next === null ? (f.plan_expiry || '') : next };
    });
  };

  return (
    <div data-testid="admin-clients" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Clients ({filteredClients.length}{search || statusFilter !== 'all' ? ` of ${clients.length}` : ''})</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={async () => {
            try {
              const r = await axios.get(`${API}/admin/clients.csv`, { headers: authHeaders(), responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
              const a = document.createElement('a');
              a.href = url; a.download = `clients-${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
            } catch (e) { alert('Export failed: ' + (e.response?.data?.detail || e.message)); }
          }} className="btn-ghost btn-sm" data-testid="export-clients-csv-btn"><Upload size={14} style={{ transform: 'rotate(180deg)' }} />Export CSV</button>
          <button onClick={() => setShowBulkEmail(true)} className="btn-ghost btn-sm" data-testid="send-announcement-btn"><Mail size={14} />Send Announcement</button>
          <button onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="btn-teal btn-sm" data-testid="add-client-btn"><Plus size={16} />Add Client</button>
        </div>
      </div>
      {showBulkEmail && <BulkEmailModal onClose={() => setShowBulkEmail(false)} authHeaders={authHeaders} />}
      <div style={{ background: 'var(--teal-lt)', color: 'var(--teal)', padding: '10px 14px', borderRadius: 10, fontSize: '0.82rem', marginBottom: '14px' }}>
        <strong>Subscriptions:</strong>&nbsp;Pause / Resume / Extend / Adjust expiry per client. Pause auto-extends expiry by paused duration on resume.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input data-testid="clients-search" className="form-input" placeholder="🔍 Search name, email, or phone…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
        <select data-testid="clients-status-filter" className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ minWidth: 160 }}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: 16 }}>{editing ? 'Edit Client' : 'Add Client'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="client-name-input" /></div>
            <div><label className="form-label">Mobile</label><input className="form-input" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="+91 98765 43210" data-testid="client-mobile-input" /></div>
            <div><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="client-email-input" /></div>
            <div><label className="form-label">Plan Type</label>
              <select className="form-select" value={form.plan_type} onChange={e => onPlanTypeChange(e.target.value)} data-testid="client-plan-select">
                {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className="form-label">Plan Start <span style={{ color: '#C0392B' }}>*</span></label><input className="form-input" type="date" data-testid="client-plan-start-input" required value={form.plan_start} onChange={e => onPlanStartChange(e.target.value)} /></div>
            <div><label className="form-label">Date of Birth (optional)</label><input className="form-input" type="date" data-testid="client-dob-input" value={form.date_of_birth || ''} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} /></div>
            {!PLAN_TYPES_HIDE_EXPIRY.has(form.plan_type) && (
              <div><label className="form-label">Plan Expiry {PLAN_MONTHS[form.plan_type] ? <span style={{ fontSize: '.7rem', color: 'var(--mid)', fontWeight: 500 }}>(auto-calculated)</span> : ''}</label>
                <input
                  className="form-input"
                  type="date"
                  data-testid="client-plan-expiry-input"
                  value={form.plan_expiry || ''}
                  readOnly={!!PLAN_MONTHS[form.plan_type]}
                  onChange={e => setForm(f => ({ ...f, plan_expiry: e.target.value }))}
                  style={PLAN_MONTHS[form.plan_type] ? { background: 'var(--cream)', cursor: 'not-allowed' } : undefined}
                />
              </div>
            )}
            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input id="expiry-reminder-toggle" type="checkbox" data-testid="expiry-reminder-toggle" checked={form.expiry_reminder_enabled !== false} onChange={e => setForm(f => ({ ...f, expiry_reminder_enabled: e.target.checked }))} />
              <label htmlFor="expiry-reminder-toggle" style={{ fontSize: '.86rem', color: 'var(--ink)', cursor: 'pointer' }}>Send 7-day plan expiry reminder email automatically</label>
            </div>
            <div style={{ gridColumn: 'span 2' }}><label className="form-label">Notes</label><textarea className="form-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Health Tags</label>
              <HealthTagsSelect value={form.health_tags || []} onChange={(v) => setForm(f => ({ ...f, health_tags: v }))} />
              <div style={{ marginTop: 4, fontSize: '.72rem', color: 'var(--mid)' }}>Search by typing any letter. Click a tag to add it. Click the X on a pill to remove.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={save} className="btn-teal btn-sm" data-testid="save-client-btn"><Check size={14} />Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); setForm(EMPTY); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div> : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {filteredClients.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>{clients.length === 0 ? 'No clients yet.' : 'No clients match your filter.'}</div>
          ) : filteredClients.map((c, i) => {
            const sc = STATUS_COLORS[c.effective_status] || '#9CA3AF';
            return (
              <div key={c.client_id} data-testid="client-row" onClick={() => setPanelFor(c)} style={{ padding: '16px 18px', borderBottom: i < filteredClients.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '40px 2fr 1.5fr 1fr auto', gap: 14, alignItems: 'center' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: sc + '22', color: sc, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Fraunces', serif", fontWeight: 800 }}>
                    {c.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--ink)' }}>{c.name}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--mid)' }}>
                      <code style={{ background: 'var(--cream)', padding: '1px 6px', borderRadius: 4 }}>{c.client_id}</code>
                      {c.mobile && ` · ${c.mobile}`}{c.email && ` · ${c.email}`}
                    </div>
                    <HealthTagsInline tags={c.health_tags} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--ink)' }}>{PLAN_LABELS[c.plan_type] || c.plan_type}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--mid)' }}>{c.plan_start || '?'} → {c.plan_expiry || '?'}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span data-testid="client-status-badge" style={{ display: 'inline-block', background: sc + '22', color: sc, padding: '3px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {c.effective_status}
                    </span>
                    {c.days_remaining != null && c.effective_status === 'active' && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--mid)', marginTop: 4 }}>{c.days_remaining}d left</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button onClick={(e) => { e.stopPropagation(); setDietFor(c); }} className="btn-ghost btn-sm" data-testid="open-diet-plans-btn" title="Diet plans"><BookOpen size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); copyMagicLink(c); }} style={{ background: 'var(--sage-lt, #E8F3EE)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--sage, #2E7D5B)' }} data-testid="copy-magic-link-btn" title="Copy one-time login link"><LinkIcon size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} style={{ background: 'var(--teal-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--teal)' }} data-testid="edit-client-btn"><Edit size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); del(c.client_id); }} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--coral)' }} data-testid="delete-client-btn"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div style={{ marginTop: 10, paddingLeft: 54 }} onClick={e => e.stopPropagation()}>
                  <SubscriptionActions client={c} authHeaders={authHeaders} onChange={load} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dietFor && <DietPlansModal client={dietFor} authHeaders={authHeaders} onClose={() => { setDietFor(null); load(); }} />}
      {panelFor && (
        <ClientDetailPanel
          client={panelFor}
          authHeaders={authHeaders}
          onClose={() => setPanelFor(null)}
          onOpenDietPlans={() => { const c = panelFor; setPanelFor(null); setDietFor(c); }}
          onOpenEdit={() => { const c = panelFor; setPanelFor(null); openEdit(c); }}
          onClientUpdated={(updated) => {
            // Sync the panel's source-of-truth and the list row in place.
            setPanelFor(prev => prev ? { ...prev, ...updated } : prev);
            setClients(prev => prev.map(x => x.client_id === updated.client_id ? { ...x, ...updated } : x));
          }}
        />
      )}
    </div>
  );
}

// ─── Leads Manager ────────────────────────────────────────────────────────────
function LeadsManager() {
  const { authHeaders } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterSource) params.set('source', filterSource);
    if (filterStatus) params.set('status', filterStatus);
    if (search) params.set('search', search);
    try {
      const r = await axios.get(`${API}/leads?${params.toString()}`, { headers: authHeaders() });
      setLeads(r.data || []);
    } finally { setLoading(false); }
  }, [filterSource, filterStatus, search, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await axios.put(`${API}/leads/${id}`, { status }, { headers: authHeaders() });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() });
    load();
  };

  const STATUS_OPTIONS = ['new', 'contacted', 'booked', 'converted', 'closed'];
  const SOURCE_OPTIONS = ['PCOS Quiz', 'Diet Review', 'WhatsApp', 'Other'];
  const statusColor = (s) => ({
    new: { bg: '#FDF0ED', fg: '#B94C2C' },
    contacted: { bg: '#FEF3C7', fg: '#92400E' },
    booked: { bg: '#DBEAFE', fg: '#1E40AF' },
    converted: { bg: '#D1FAE5', fg: '#065F46' },
    closed: { bg: '#E5E7EB', fg: '#4B5563' },
  })[s] || { bg: '#E5E7EB', fg: '#4B5563' };

  return (
    <div data-testid="admin-leads" style={{ padding: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Leads</h1>
          <p style={{ fontSize: '.85rem', color: 'var(--mid)', marginTop: 4 }}>High-intent submissions from the PCOS Quiz and other lead-capture forms. {leads.length} total.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input data-testid="leads-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name or phone…" className="form-input" style={{ width: 220 }} />
          <select data-testid="leads-filter-source" value={filterSource} onChange={e => setFilterSource(e.target.value)} className="form-input" style={{ width: 160 }}>
            <option value="">All sources</option>
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="leads-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input" style={{ width: 140 }}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading leads…</div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)', background: '#fff', borderRadius: 16, border: '1px solid var(--border)' }}>
          No leads yet. When a visitor completes the PCOS Quiz with MODERATE or HIGH risk, their details will appear here.
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Received</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Name</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>WhatsApp</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Email</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Source</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Risk</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</th>
                <th style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--mid)', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.05em' }}></th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => {
                const color = statusColor(l.status);
                const wa = (l.whatsapp || '').replace(/[^0-9+]/g, '');
                return (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 14px', color: 'var(--mid)', fontSize: '.8rem', whiteSpace: 'nowrap' }}>
                      {l.created_at ? new Date(l.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: 'var(--ink)' }}>{l.name}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {l.whatsapp
                        ? <a href={`https://wa.me/${wa.replace(/^\+/, '')}?text=Hi%20${encodeURIComponent(l.name)}%2C%20Anjum%20here`} target="_blank" rel="noopener noreferrer" data-testid={`lead-whatsapp-${l.id}`} style={{ color: '#25D366', fontWeight: 600, textDecoration: 'none' }}>{l.whatsapp}</a>
                        : <span style={{ color: 'var(--mid)' }}>-</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {l.email
                        ? <a href={`mailto:${l.email}`} data-testid={`lead-email-${l.id}`} style={{ color: '#0F4C5C', fontWeight: 600, textDecoration: 'none', userSelect: 'all' }}>{l.email}</a>
                        : <span style={{ color: 'var(--mid)' }}>-</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--mid)' }}>{l.source}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {l.risk_level ? <span style={{ background: l.risk_level === 'HIGH' ? '#FEE2E2' : '#FEF3C7', color: l.risk_level === 'HIGH' ? '#991B1B' : '#92400E', padding: '3px 9px', borderRadius: 50, fontSize: '.72rem', fontWeight: 700 }}>{l.risk_level}{l.score ? ` · ${l.score}` : ''}</span> : <span style={{ color: 'var(--mid)' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={e => setStatus(l.id, e.target.value)} style={{ background: color.bg, color: color.fg, border: 'none', padding: '6px 10px', borderRadius: 6, fontWeight: 700, fontSize: '.78rem', cursor: 'pointer' }}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B94C2C' }}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Settings ─────────────────────────────────────────────────────────────────
const PAYMENT_LINK_ROWS = [
  { key: 'diet_review', label: 'Diet Review', price: 'Rs. 499', note: 'Main paid flagship service.' },
  { key: 'nar', label: 'Nutrition Assessment Report', price: 'Rs. 4,999', note: 'Comprehensive one-time clinical assessment.' },
];
const TRACKING_ROWS = [
  { key: 'gtm_id', label: 'Google Tag Manager Container ID', placeholder: 'GTM-XXXXXXX' },
  { key: 'ga_measurement_id', label: 'Google Analytics Measurement ID', placeholder: 'G-XXXXXXXXXX' },
  { key: 'meta_pixel_id', label: 'Meta Pixel ID', placeholder: '1234567890' },
  { key: 'hotjar_site_id', label: 'Hotjar Site ID', placeholder: '1234567' },
];

function BrandPhotoSlot({ photoKey, label, url, authHeaders, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Exceeds 5 MB'); return; }
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/brand-photo/${photoKey}`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this photo? The default hardcoded hero image will be shown again.')) return;
    try {
      await axios.delete(`${API}/upload/brand-photo/${photoKey}`, { headers: authHeaders() });
      onChange('');
    } catch (e) { setErr(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid={`brand-photo-${photoKey}`} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: '.88rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 10 }}>{label}</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {url ? (
          <img src={url} alt={label} style={{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border)' }} />
        ) : (
          <div style={{ width: 90, height: 90, borderRadius: 10, background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mid)' }}>
            <ImageIcon size={22} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ background: 'var(--teal)', color: '#fff', padding: '7px 14px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Upload size={13} /> {busy ? 'Uploading…' : (url ? 'Replace' : 'Upload')}
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} disabled={busy} style={{ display: 'none' }} data-testid={`brand-photo-input-${photoKey}`} />
          </label>
          {url && (
            <button type="button" onClick={handleRemove} data-testid={`brand-photo-remove-${photoKey}`} style={{ background: 'var(--coral-lt)', color: 'var(--coral)', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>Remove</button>
          )}
        </div>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: '.76rem', marginTop: 8 }}>{err}</div>}
    </div>
  );
}

// ─── Practice Logo Uploader (Invoice PDFs + Transactional Emails) ────────────
function PracticeLogoUploader({ dataUrl, authHeaders, onUploaded, onCleared }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const ref = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { setErr('Logo exceeds 500 KB. Please compress and try again.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      // Convert to base64 data URL on the client (per stored design).
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/admin/settings/practice-logo`, { data_url: dataUrl }, { headers: authHeaders() });
      onUploaded(dataUrl);
      setMsg('✓ Logo updated. New invoices &amp; emails will use this immediately.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the custom logo? The default brand logo will be used again on PDFs and emails.')) return;
    setErr(''); setMsg('');
    try {
      await axios.delete(`${API}/admin/settings/practice-logo`, { headers: authHeaders() });
      onCleared();
      setMsg('✓ Custom logo removed. Default brand logo restored.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr(e.response?.data?.detail || 'Remove failed'); }
  };

  const hasLogo = !!dataUrl;
  return (
    <div data-testid="practice-logo-uploader" style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ width: 200, height: 90, borderRadius: 12, background: hasLogo ? '#0F4C5C' : 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10, border: '1px solid var(--border)' }}>
        {hasLogo ? (
          <img data-testid="practice-logo-preview" src={dataUrl} alt="Practice logo preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <div style={{ fontFamily: "'Great Vibes', cursive", fontSize: 26, color: 'var(--teal)' }}>Anjum Sohail</div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label data-testid="practice-logo-upload-label" style={{ background: 'var(--teal)', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Upload size={14} /> {busy ? 'Uploading…' : (hasLogo ? 'Replace Logo' : 'Upload Logo')}
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleFile} disabled={busy} style={{ display: 'none' }} data-testid="practice-logo-input" />
        </label>
        {hasLogo && (
          <button type="button" onClick={handleRemove} data-testid="practice-logo-remove" style={{ background: 'var(--coral-lt)', color: 'var(--coral)', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' }}>Remove (use default)</button>
        )}
        <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: 2 }}>PNG, JPG, or SVG · max 500 KB</div>
      </div>
      {err && <div data-testid="practice-logo-error" style={{ color: '#dc2626', fontSize: '.78rem', flexBasis: '100%' }}>{err}</div>}
      {msg && <div data-testid="practice-logo-msg" style={{ color: '#16A34A', fontSize: '.78rem', flexBasis: '100%' }} dangerouslySetInnerHTML={{ __html: msg }} />}
    </div>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsManager() {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const PROGRAMMES = ['Weight Loss', 'PCOS', 'Thyroid', 'Diabetes', 'General Nutrition', 'Sports Nutrition', 'Pregnancy Nutrition', 'Gut Health', 'Post-natal Nutrition'];
  const PAGES = ['home', 'about', 'shop', 'pricing'];
  const [activePage, setActivePage] = useState('home');
  const EMPTY = { name: '', location: '', programme: 'General Nutrition', rating: 5, text: '', result: '', photo_url: '', visible: true, display_order: 0, page: 'home', show_on_pages: ['home', 'about', 'shop', 'pricing'] };
  const [form, setForm] = useState(EMPTY);
  const photoRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await axios.get(`${API}/admin/testimonials?page=${activePage}`, { headers: authHeaders() });
    setItems(r.data); setLoading(false);
  }, [authHeaders, activePage]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name?.trim()) { alert('Name required'); return; }
    if (form.text?.length > 300) { alert('Keep text ≤ 300 chars'); return; }
    try {
      const payload = { ...form, rating: Number(form.rating) || 5, display_order: Number(form.display_order) || 0 };
      if (editing) {
        await axios.put(`${API}/admin/testimonials/${editing.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, { headers: authHeaders() });
      }
      setShowForm(false); setEditing(null); setForm(EMPTY);
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await axios.delete(`${API}/admin/testimonials/${id}`, { headers: authHeaders() });
    await load();
  };

  const toggleVisible = async (t) => {
    await axios.put(`${API}/admin/testimonials/${t.id}`, { ...t, visible: !t.visible }, { headers: authHeaders() });
    await load();
  };

  const reorder = async (t, direction) => {
    const list = [...items].sort((a, b) => a.display_order - b.display_order);
    const idx = list.findIndex(x => x.id === t.id);
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= list.length) return;
    const a = list[idx], b = list[swap];
    await Promise.all([
      axios.put(`${API}/admin/testimonials/${a.id}`, { ...a, display_order: b.display_order }, { headers: authHeaders() }),
      axios.put(`${API}/admin/testimonials/${b.id}`, { ...b, display_order: a.display_order }, { headers: authHeaders() }),
    ]);
    await load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/admin/testimonials/${editing.id}/photo`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, photo_url: r.data.url }));
    } catch (e2) { alert('Upload failed: ' + (e2.response?.data?.detail || e2.message)); }
    finally { if (photoRef.current) photoRef.current.value = ''; }
  };

  const openEdit = (t) => { setEditing(t); setForm({ ...t }); setShowForm(true); };

  return (
    <div style={{ padding: 28 }} data-testid="admin-testimonials">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Testimonials</h1>
          <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginTop: 2 }}>Each page has its own independent testimonials. Switch the tabs to manage each page.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm({ ...EMPTY, page: activePage, display_order: items.length + 1 }); setShowForm(true); }} className="btn-teal btn-sm" data-testid="new-testimonial-btn"><Plus size={16} />New {activePage} testimonial</button>
      </div>
      {/* PAGE TABS */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, borderBottom: '1px solid var(--border)' }} data-testid="testimonial-page-tabs">
        {PAGES.map(p => (
          <button
            key={p}
            data-testid={`testim-tab-${p}`}
            onClick={() => setActivePage(p)}
            style={{
              background: activePage === p ? 'var(--teal)' : 'transparent',
              color: activePage === p ? '#fff' : 'var(--mid)',
              border: 'none',
              padding: '10px 18px',
              fontWeight: 700,
              fontSize: '.85rem',
              textTransform: 'capitalize',
              borderRadius: '10px 10px 0 0',
              cursor: 'pointer',
              borderBottom: activePage === p ? '3px solid var(--coral)' : 'none',
            }}>
            {p} ({activePage === p ? items.length : '·'})
          </button>
        ))}
      </div>
      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div> : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {items.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>No testimonials yet.</div> : items.sort((a, b) => a.display_order - b.display_order).map((t, i) => (
            <div key={t.id} data-testid="testimonial-row" style={{ padding: '14px 18px', borderBottom: i < items.length - 1 ? '1px solid var(--border)' : 'none', display: 'grid', gridTemplateColumns: '44px 1.8fr 1fr 1fr auto auto auto', gap: 12, alignItems: 'center' }}>
              {t.photo_url ? (
                <img src={t.photo_url} alt={t.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--teal)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.9rem' }}>{t.name.split(' ').map(p => p[0]).slice(0, 2).join('')}</div>
              )}
              <div>
                <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{t.name} <span style={{ fontWeight: 400, color: 'var(--mid)', fontSize: '.78rem' }}>· {t.location}</span></div>
                <div style={{ fontSize: '.76rem', color: 'var(--mid)', marginTop: 2 }}>{'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)} · {t.programme}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--ink)', marginTop: 3, fontStyle: 'italic', maxWidth: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>"{t.text}"</div>
              </div>
              <span style={{ background: 'var(--coral-lt)', color: 'var(--coral)', padding: '4px 10px', borderRadius: 999, fontSize: '.74rem', fontWeight: 700, textAlign: 'center' }}>{t.result}</span>
              <div style={{ textAlign: 'center', fontSize: '.82rem', color: 'var(--mid)' }}>
                <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--ink)' }}>#{t.display_order}</div>
                <button onClick={() => reorder(t, 'up')} title="Move up" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)', padding: 2 }}>▲</button>
                <button onClick={() => reorder(t, 'down')} title="Move down" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)', padding: 2 }}>▼</button>
              </div>
              <button onClick={() => toggleVisible(t)} data-testid="toggle-visible-btn" title={t.visible ? 'Hide' : 'Show'} style={{ background: t.visible ? '#DCFCE7' : '#FEE2E2', color: t.visible ? '#14532D' : '#7F1D1D', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer' }}>
                {t.visible ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button onClick={() => openEdit(t)} data-testid="edit-testimonial-btn" title="Edit" style={{ background: 'var(--cream)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer' }}><Edit size={13} /></button>
              <button onClick={() => del(t.id)} data-testid="delete-testimonial-btn" title="Delete" style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--coral)' }}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, overflowY: 'auto', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="testimonial-form" style={{ background: '#fff', borderRadius: 16, padding: 28, width: '90%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.3rem', marginBottom: 18 }}>{editing ? 'Edit Testimonial' : 'New Testimonial'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label className="form-label">Client first name + last initial *</label><input data-testid="t-name" className="form-input" placeholder="Priya S." value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div><label className="form-label">Location</label><input data-testid="t-location" className="form-input" placeholder="Bangalore" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></div>
              <div><label className="form-label">Programme</label>
                <select data-testid="t-programme" className="form-select" value={form.programme} onChange={e => setForm(f => ({ ...f, programme: e.target.value }))}>
                  {PROGRAMMES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="form-label">Star rating</label>
                <select data-testid="t-rating" className="form-select" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: Number(e.target.value) }))}>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ★</option>)}
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Testimonial text (max 300 chars) <span style={{ color: form.text.length > 300 ? 'var(--coral)' : 'var(--mid)' }}>· {form.text.length}/300</span></label>
                <textarea data-testid="t-text" className="form-input" rows={4} value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value.slice(0, 300) }))} />
              </div>
              <div style={{ gridColumn: 'span 2' }}><label className="form-label">Result achieved</label><input data-testid="t-result" className="form-input" placeholder="Lost 8kg in 3 months" value={form.result} onChange={e => setForm(f => ({ ...f, result: e.target.value }))} /></div>
              <div>
                <label className="form-label">Display order</label>
                <input data-testid="t-order" type="number" className="form-input" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: Number(e.target.value) }))} />
              </div>
              <div>
                <label className="form-label">Visible on site</label>
                <button type="button" onClick={() => setForm(f => ({ ...f, visible: !f.visible }))} data-testid="t-visible" className="form-input" style={{ textAlign: 'left', cursor: 'pointer', background: form.visible ? '#DCFCE7' : '#FEE2E2', color: form.visible ? '#14532D' : '#7F1D1D', fontWeight: 600 }}>
                  {form.visible ? '✓ Visible' : '✕ Hidden'}
                </button>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Photo (optional, circular crop — max 5 MB)</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} />
                  ) : (
                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--teal)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>{(form.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('')}</div>
                  )}
                  {editing ? (
                    <label style={{ background: 'var(--teal)', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>
                      <Upload size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{form.photo_url ? 'Replace' : 'Upload'}
                      <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} style={{ display: 'none' }} />
                    </label>
                  ) : (
                    <div style={{ fontSize: '.78rem', color: 'var(--mid)' }}>Save first to upload a photo.</div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14, padding: 14, background: 'var(--cream)', borderRadius: 10 }}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: 8 }}>Display Page</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PAGES.map(pg => {
                  const checked = (form.page || 'home') === pg;
                  return (
                    <button
                      key={pg}
                      type="button"
                      data-testid={`testim-page-${pg}`}
                      onClick={() => setForm({ ...form, page: pg, show_on_pages: [pg] })}
                      style={{
                        background: checked ? 'var(--teal)' : '#fff',
                        color: checked ? '#fff' : 'var(--ink)',
                        border: '1.5px solid ' + (checked ? 'var(--teal)' : 'var(--border)'),
                        padding: '7px 14px',
                        borderRadius: 999,
                        fontSize: '.82rem',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                      }}>
                      {pg}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 6 }}>This testimonial only appears on the selected page.</p>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
              <button onClick={save} data-testid="save-testimonial-btn" className="btn-teal btn-sm"><Check size={14} />{editing ? 'Save Changes' : 'Create Testimonial'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Invoices ─────────────────────────────────────────────────────────────────
function InvoicesManager() {
  const { authHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  const EMPTY = {
    client: { name: '', email: '', phone: '' },
    items: [{ description: '', hsn_code: '', quantity: 1, rate: 0, tax_rate: 18, amount: 0 }],
    plan_includes: [''],
    gst: { company_name: '', gstin: '', billing_address: '', city: '', pincode: '', state: '', city_state: '', place_of_supply: 'Karnataka' },
    payment_mode: 'Razorpay',
    transaction_id: '',
    notes: '',
    status: 'UNPAID',
    show_gst: false,
  };
  const [form, setForm] = useState(EMPTY);
  // PDF preview modal state - opens after save (Create OR Edit) and lets the
  // admin download or send the invoice without auto-emailing on save.
  const [preview, setPreview] = useState({ open: false, invNum: '', pdfUrl: '', sending: false });

  // ── Filter bar state (client-side filtering for GST filing) ────────────
  const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' | 'YYYY-MM'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterPaymentMode, setFilterPaymentMode] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWorkflowStatus, setFilterWorkflowStatus] = useState('');

  // Generate last 12 calendar months (current month + 11 previous).
  const monthOptions = useMemo(() => {
    const now = new Date();
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      out.push({ value: `${y}-${m}`, label });
    }
    return out;
  }, []);

  const onMonthChange = (val) => {
    setSelectedMonth(val);
    if (val === 'all') {
      setFromDate('');
      setToDate('');
      return;
    }
    const [y, m] = val.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0); // day 0 of next month = last day of this month
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setFromDate(fmt(first));
    setToDate(fmt(last));
  };

  const clearFilters = () => {
    setSelectedMonth('all');
    setFromDate('');
    setToDate('');
    setFilterPaymentMode('');
    setFilterStatus('');
    setFilterWorkflowStatus('');
  };

  const hasActiveFilters = !!(fromDate || toDate || filterPaymentMode || filterStatus || filterWorkflowStatus);

  // Client-side filter on the fetched invoices array. Uses created_at (UTC).
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (fromDate) {
        const ca = inv.created_at ? new Date(inv.created_at) : null;
        const from = new Date(`${fromDate}T00:00:00Z`);
        if (!ca || ca < from) return false;
      }
      if (toDate) {
        const ca = inv.created_at ? new Date(inv.created_at) : null;
        const to = new Date(`${toDate}T23:59:59.999Z`);
        if (!ca || ca > to) return false;
      }
      if (filterPaymentMode && (inv.payment_mode || '') !== filterPaymentMode) return false;
      if (filterStatus && (inv.status || '').toUpperCase() !== filterStatus.toUpperCase()) return false;
      if (filterWorkflowStatus && (inv.workflow_status || 'DRAFT').toUpperCase() !== filterWorkflowStatus.toUpperCase()) return false;
      return true;
    });
  }, [invoices, fromDate, toDate, filterPaymentMode, filterStatus, filterWorkflowStatus]);

  // GST summary for the currently filtered set.
  const gstSummary = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, total = 0;
    filteredInvoices.forEach((inv) => {
      const c = inv.gst_calc || {};
      taxable += Number(c.taxable || 0);
      cgst += Number(c.cgst || 0);
      sgst += Number(c.sgst || 0);
      igst += Number(c.igst || 0);
      total += Number(c.total || 0);
    });
    return { taxable, cgst, sgst, igst, total, count: filteredInvoices.length };
  }, [filteredInvoices]);

  const exportCSV = async () => {
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.set('from_date', fromDate);
      if (toDate) qs.set('to_date', toDate);
      if (filterPaymentMode) qs.set('payment_mode', filterPaymentMode);
      if (filterStatus) qs.set('status', filterStatus);
      if (filterWorkflowStatus) qs.set('workflow_status', filterWorkflowStatus);
      const url = `${API}/invoices.csv${qs.toString() ? '?' + qs.toString() : ''}`;
      const r = await axios.get(url, { headers: authHeaders(), responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const fnameParts = ['invoices'];
      if (fromDate) fnameParts.push(fromDate);
      if (toDate) fnameParts.push(toDate);
      a.download = fnameParts.join('_') + '.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert('CSV export failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const r = await axios.get(`${API}/invoices${params}`, { headers: authHeaders() });
    setInvoices(r.data); setLoading(false);
  }, [authHeaders, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/settings`).then(r => setSiteSettings(r.data?.data || {})).catch(() => {}); }, []);

  // If we were navigated here from the Client Detail Panel "Generate Invoice"
  // shortcut, pre-fill the search box AND auto-open the new-invoice form with
  // the client details already populated. We do NOT change any submit/PDF
  // logic, the form behaves identically to clicking New Invoice manually.
  const location = useLocation();
  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    if (qp.get('new') === '1') {
      const cname = qp.get('client_name') || '';
      const cemail = qp.get('client_email') || '';
      const cphone = qp.get('client_phone') || '';
      if (cname || cemail || cphone) setSearch(cname || cemail || '');
      setEditing(null);
      setForm(f => ({ ...EMPTY, client: { name: cname, email: cemail, phone: cphone } }));
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Practice contact for live-preview header (pulled from Settings → editable). Fallback to canonical brand defaults.
  const practice = {
    email: siteSettings?.contact?.email || 'info@anjumsohail.com',
    phone: siteSettings?.contact?.phone || '+91 9187542262',
    website: siteSettings?.contact?.website || 'anjumsohail.com',
    address: siteSettings?.contact?.address || 'No 213, 2nd Floor, Ramanashree Arcade, 18 MG Road, Ashok Nagar, Bengaluru 560001',
    gstin: siteSettings?.contact?.gstin || '29FWRPS8543G1ZI',
  };

  const total = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  // GST type auto-derived from billing state. Karnataka (or empty) → intra-state (CGST+SGST), else IGST.
  const stateForGst = (form.gst?.state || form.gst?.place_of_supply || '').toLowerCase().trim();
  const isIntra = stateForGst === '' || stateForGst.includes('karnataka');
  const taxable = total / 1.18;
  const gstTotal = total - taxable;

  const save = async () => {
    const payload = { ...form };
    delete payload.show_gst;
    payload.plan_includes = (payload.plan_includes || []).filter(x => x.trim());
    try {
      let invNum;
      if (editing) {
        await axios.put(`${API}/invoices/${editing.invoice_number}`, payload, { headers: authHeaders() });
        invNum = editing.invoice_number;
      } else {
        const res = await axios.post(`${API}/invoices`, payload, { headers: authHeaders() });
        invNum = res.data?.invoice_number;
      }
      setShowForm(false); setEditing(null); setForm(EMPTY);
      await load();
      // Fetch the PDF as a blob (auth header attached) and open the preview modal.
      // Iframes can't pass Bearer tokens, so we use URL.createObjectURL on the blob.
      if (invNum) {
        try {
          const r = await axios.get(`${API}/invoices/${invNum}/pdf`, { headers: authHeaders(), responseType: 'blob' });
          const pdfUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
          setPreview({ open: true, invNum, pdfUrl, sending: false });
        } catch (e) { /* Preview is best-effort; admin can still hit Download from the list */ }
      }
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const closePreview = () => {
    if (preview.pdfUrl) URL.revokeObjectURL(preview.pdfUrl);
    setPreview({ open: false, invNum: '', pdfUrl: '', sending: false });
  };

  const previewDownload = async () => {
    if (!preview.invNum) return;
    // Trigger a normal browser download from the existing blob URL.
    const a = document.createElement('a'); a.href = preview.pdfUrl; a.download = `${preview.invNum}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    // Mark workflow_status = DOWNLOADED (best-effort; non-blocking).
    try {
      await axios.patch(`${API}/invoices/${preview.invNum}/workflow-status`, { workflow_status: 'DOWNLOADED' }, { headers: authHeaders() });
      await load();
    } catch (e) { /* non-fatal */ }
  };

  const previewSend = async () => {
    if (!preview.invNum) return;
    setPreview(p => ({ ...p, sending: true }));
    try {
      await axios.post(`${API}/invoices/${preview.invNum}/send`, {}, { headers: authHeaders() });
      alert('✓ Invoice sent to client');
      await load();
      closePreview();
    } catch (e) {
      alert('Send failed: ' + (e.response?.data?.detail || e.message));
      setPreview(p => ({ ...p, sending: false }));
    }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    // Seed legacy invoices with defaults for the new HSN/Qty/Tax fields so the
    // controlled inputs render without NaN/undefined warnings.
    const seededItems = (inv.items?.length ? inv.items : EMPTY.items).map(it => ({
      description: it.description || '',
      hsn_code: it.hsn_code || it.sac_code || '',
      quantity: (it.quantity === undefined || it.quantity === null) ? 1 : Number(it.quantity),
      rate: Number(it.rate || 0),
      tax_rate: (it.tax_rate === undefined || it.tax_rate === null) ? 18 : Number(it.tax_rate),
      amount: Number(it.amount || 0),
      // Preserve `duration` if present so we don't lose data on edit (legacy field).
      ...(it.duration ? { duration: it.duration } : {}),
    }));
    setForm({
      client: inv.client || EMPTY.client,
      items: seededItems,
      plan_includes: inv.plan_includes?.length ? inv.plan_includes : [''],
      gst: inv.gst || EMPTY.gst,
      payment_mode: inv.payment_mode || 'Razorpay',
      transaction_id: inv.transaction_id || '',
      notes: inv.notes || '',
      status: (inv.status || 'UNPAID').toUpperCase(),
      show_gst: !!(inv.gst?.gstin || inv.gst?.company_name),
    });
    setShowForm(true);
  };

  const toggleStatus = async (invNum, currentStatus) => {
    const next = (currentStatus || 'UNPAID').toUpperCase() === 'PAID' ? 'UNPAID' : 'PAID';
    try {
      await axios.patch(`${API}/invoices/${invNum}/status`, { status: next }, { headers: authHeaders() });
      await load();
    } catch (e) { alert('Status update failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const resendInvoice = async (invNum) => {
    if (!window.confirm(`Re-send invoice ${invNum} (with PDF) to the client?`)) return;
    try {
      await axios.post(`${API}/invoices/${invNum}/send`, {}, { headers: authHeaders() });
      alert('✓ Invoice re-sent to client');
      await load();
    } catch (e) { alert('Resend failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const downloadPDF = async (invNum) => {
    try {
      const r = await axios.get(`${API}/invoices/${invNum}/pdf`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${invNum}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed'); }
  };

  const sendToClient = async (invNum) => {
    if (!window.confirm(`Email invoice ${invNum} (with PDF) to the client?`)) return;
    try {
      await axios.post(`${API}/invoices/${invNum}/send`, {}, { headers: authHeaders() });
      alert('✓ Invoice sent to client');
      await load();
    } catch (e) { alert('Send failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const del = async (invNum) => {
    if (!window.confirm(`Delete invoice ${invNum}?`)) return;
    await axios.delete(`${API}/invoices/${invNum}`, { headers: authHeaders() });
    await load();
  };

  return (
    <div style={{ padding: 28 }} data-testid="admin-invoices">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>
          Invoices ({hasActiveFilters ? `${filteredInvoices.length} of ${invoices.length}` : invoices.length})
        </h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <input data-testid="invoices-search" className="form-input" placeholder="🔍 Search #, name, email…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 250 }} />
          <button onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="btn-teal btn-sm" data-testid="new-invoice-btn"><Plus size={16} />New Invoice</button>
        </div>
      </div>

      {/* Filter bar for GST filing: month quick-select + manual range + facets + CSV export */}
      <div data-testid="invoice-filter-bar" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr auto', gap: 10, alignItems: 'end', marginBottom: 10 }}>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>Quick month (GST filing)</label>
            <select data-testid="invoice-filter-month" className="form-select" value={selectedMonth} onChange={e => onMonthChange(e.target.value)}>
              <option value="all">All months</option>
              {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div style={{ fontSize: '.7rem', color: 'var(--mid)' }}>Selecting a month auto-fills the date range below.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>From date</label>
            <input data-testid="invoice-filter-from" type="date" className="form-input" value={fromDate} onChange={e => { setFromDate(e.target.value); setSelectedMonth('all'); }} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>To date</label>
            <input data-testid="invoice-filter-to" type="date" className="form-input" value={toDate} onChange={e => { setToDate(e.target.value); setSelectedMonth('all'); }} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>Payment mode</label>
            <select data-testid="invoice-filter-paymode" className="form-select" value={filterPaymentMode} onChange={e => setFilterPaymentMode(e.target.value)}>
              <option value="">All</option>
              {['Razorpay', 'UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Card'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>Status</label>
            <select data-testid="invoice-filter-status" className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Unpaid</option>
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '.72rem' }}>Workflow</label>
            <select data-testid="invoice-filter-workflow" className="form-select" value={filterWorkflowStatus} onChange={e => setFilterWorkflowStatus(e.target.value)}>
              <option value="">All</option>
              <option value="DRAFT">Draft</option>
              <option value="DOWNLOADED">Downloaded</option>
              <option value="SENT">Sent</option>
            </select>
          </div>
          <button data-testid="invoice-export-csv" onClick={exportCSV} className="btn-teal btn-sm" style={{ height: 38 }}>
            <Download size={14} /> Export CSV
          </button>
          <button data-testid="invoice-clear-filters" onClick={clearFilters} className="btn-ghost btn-sm" style={{ height: 38 }} disabled={!hasActiveFilters && selectedMonth === 'all'}>
            <X size={14} /> Clear
          </button>
        </div>
      </div>

      {/* GST Summary card - visible only when filters are active */}
      {hasActiveFilters && (
        <div data-testid="invoice-gst-summary" style={{ background: 'var(--teal)', color: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.05rem' }}>
              GST Summary <span style={{ opacity: .8, fontSize: '.8rem', fontWeight: 500 }}>({gstSummary.count} invoice{gstSummary.count === 1 ? '' : 's'})</span>
            </div>
            <div style={{ opacity: .85, fontSize: '.75rem' }}>
              {fromDate || 'Any'} → {toDate || 'Any'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            <div>
              <div style={{ fontSize: '.66rem', opacity: .8, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Taxable</div>
              <div data-testid="gst-taxable" style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem' }}>Rs. {gstSummary.taxable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: '.66rem', opacity: .8, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>CGST</div>
              <div data-testid="gst-cgst" style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem' }}>Rs. {gstSummary.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: '.66rem', opacity: .8, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>SGST</div>
              <div data-testid="gst-sgst" style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem' }}>Rs. {gstSummary.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: '.66rem', opacity: .8, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>IGST</div>
              <div data-testid="gst-igst" style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem' }}>Rs. {gstSummary.igst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div style={{ fontSize: '.66rem', opacity: .8, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Total</div>
              <div data-testid="gst-total" style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.15rem' }}>Rs. {gstSummary.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>Loading…</div> : (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {filteredInvoices.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--mid)' }}>{hasActiveFilters ? 'No invoices match the current filters.' : 'No invoices yet.'}</div> : filteredInvoices.map((inv, i) => {
            const status = (inv.status || 'UNPAID').toUpperCase();
            const isPaid = status === 'PAID';
            return (
            <div key={inv.invoice_number} data-testid="invoice-row" style={{ padding: '14px 18px', borderBottom: i < filteredInvoices.length - 1 ? '1px solid var(--border)' : 'none', display: 'grid', gridTemplateColumns: '1.1fr 1.7fr .9fr .9fr auto auto auto auto', gap: 10, alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--teal)' }}>{inv.invoice_number}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{inv.invoice_date}</div>
                {(() => {
                  // workflow_status pill: DRAFT / DOWNLOADED / SENT (separate from payment status).
                  const ws = (inv.workflow_status || 'DRAFT').toUpperCase();
                  const wsStyles = {
                    DRAFT: { bg: '#F3F4F6', fg: '#4B5563', dot: '#9CA3AF' },
                    DOWNLOADED: { bg: '#E0F2FE', fg: '#075985', dot: '#0284C7' },
                    SENT: { bg: '#DCFCE7', fg: '#065F46', dot: '#16A34A' },
                  }[ws] || { bg: '#F3F4F6', fg: '#4B5563', dot: '#9CA3AF' };
                  return (
                    <div data-testid={`invoice-workflow-${inv.invoice_number}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, background: wsStyles.bg, color: wsStyles.fg, padding: '2px 8px', borderRadius: 999, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: wsStyles.dot }} />
                      {ws}
                    </div>
                  );
                })()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '.88rem' }}>{inv.client?.name}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{inv.client?.email} · {inv.client?.phone}</div>
              </div>
              <div style={{ fontWeight: 700, color: 'var(--ink)' }}>Rs. {(inv.gst_calc?.total || 0).toLocaleString('en-IN')}</div>
              <button
                onClick={() => toggleStatus(inv.invoice_number, status)}
                data-testid={`invoice-status-toggle-${inv.invoice_number}`}
                title="Click to toggle Paid/Unpaid"
                style={{
                  background: isPaid ? '#DCFCE7' : '#FEF3C7',
                  color: isPaid ? '#065F46' : '#92400E',
                  padding: '5px 12px',
                  borderRadius: 999,
                  fontSize: '.7rem',
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  border: `1px solid ${isPaid ? '#86EFAC' : '#FCD34D'}`,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isPaid ? '#16A34A' : '#D97706' }} />
                {status}
              </button>
              <button onClick={() => downloadPDF(inv.invoice_number)} title="Download PDF" data-testid="invoice-pdf-btn" style={{ background: 'var(--teal-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--teal)' }}><Download size={14} /></button>
              {inv.sent_at ? (
                <button onClick={() => resendInvoice(inv.invoice_number)} title="Re-send to Client" data-testid={`invoice-resend-btn-${inv.invoice_number}`} style={{ background: '#FEF3C7', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: '#B45309', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.72rem', fontWeight: 700 }}><Send size={13} /> Resend</button>
              ) : (
                <button onClick={() => sendToClient(inv.invoice_number)} title="Send to Client" data-testid="invoice-send-btn" style={{ background: '#E0F2FE', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: '#0284C7' }}><Send size={14} /></button>
              )}
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => openEdit(inv)} style={{ background: 'var(--cream)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer' }}><Edit size={13} /></button>
                <button onClick={() => del(inv.invoice_number)} style={{ background: 'var(--coral-lt)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: 'var(--coral)' }}><Trash2 size={13} /></button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, overflowY: 'auto', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="invoice-form" style={{ background: '#fff', borderRadius: 16, padding: 0, width: '95%', maxWidth: 1240, maxHeight: '92vh', overflow: 'hidden', display: 'grid', gridTemplateColumns: 'minmax(420px, 1fr) minmax(380px, 480px)' }}>
            <div style={{ padding: 28, overflowY: 'auto', maxHeight: '92vh' }}>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.3rem', marginBottom: 4 }}>{editing ? `Edit ${editing.invoice_number}` : 'New Invoice'}</h2>
            <p style={{ fontSize: '.8rem', color: 'var(--mid)', marginBottom: 20 }}>GST is 18% inclusive. Live preview on the right updates as you type.</p>

            {/* Client */}
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--teal)', marginBottom: 10 }}>Client</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
              <div><label className="form-label">Name *</label><input data-testid="inv-client-name" className="form-input" value={form.client.name} onChange={e => setForm(f => ({ ...f, client: { ...f.client, name: e.target.value } }))} /></div>
              <div><label className="form-label">Email</label><input data-testid="inv-client-email" className="form-input" value={form.client.email} onChange={e => setForm(f => ({ ...f, client: { ...f.client, email: e.target.value } }))} /></div>
              <div style={{ gridColumn: 'span 2' }}><label className="form-label">Phone</label><input className="form-input" value={form.client.phone} onChange={e => setForm(f => ({ ...f, client: { ...f.client, phone: e.target.value } }))} /></div>
            </div>

            {/* GST Details (collapsible) */}
            <div style={{ marginBottom: 18 }}>
              <button type="button" onClick={() => setForm(f => ({ ...f, show_gst: !f.show_gst }))} data-testid="inv-toggle-gst" style={{ background: 'var(--teal-lt)', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, color: 'var(--teal)', cursor: 'pointer' }}>
                {form.show_gst ? '▼' : '▶'} Add GST Details (Optional — for formal tax invoice)
              </button>
              {form.show_gst && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10, padding: 14, background: 'var(--cream)', borderRadius: 10 }}>
                  <div><label className="form-label">Company Name</label><input className="form-input" value={form.gst.company_name} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, company_name: e.target.value } }))} /></div>
                  <div><label className="form-label">Client GSTIN</label><input data-testid="inv-client-gstin" className="form-input" value={form.gst.gstin} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, gstin: e.target.value } }))} /></div>
                  <div style={{ gridColumn: 'span 2' }}><label className="form-label">Billing Address</label><input className="form-input" value={form.gst.billing_address} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, billing_address: e.target.value } }))} /></div>
                  <div><label className="form-label">City</label><input data-testid="inv-city" className="form-input" value={form.gst.city} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, city: e.target.value } }))} placeholder="Bengaluru" /></div>
                  <div><label className="form-label">Pincode</label><input data-testid="inv-pincode" className="form-input" value={form.gst.pincode} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) } }))} placeholder="560001" inputMode="numeric" maxLength={6} /></div>
                  <div><label className="form-label">State</label><input data-testid="inv-state" className="form-input" value={form.gst.state} onChange={e => setForm(f => ({ ...f, gst: { ...f.gst, state: e.target.value, place_of_supply: e.target.value || 'Karnataka' } }))} placeholder="Karnataka" /></div>
                  <div style={{ gridColumn: 'span 2', fontSize: '.78rem', color: 'var(--mid)', background: '#fff', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <strong>Place of Supply:</strong> {form.gst.state || 'Karnataka'} &nbsp;·&nbsp; <strong>GST:</strong> {isIntra ? 'CGST 9% + SGST 9% (intra-state)' : 'IGST 18% (inter-state)'}
                  </div>
                </div>
              )}
            </div>

            {/* Items */}
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--teal)', marginBottom: 10 }}>Line Items</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.3fr .6fr .9fr .65fr .9fr auto', gap: 8, marginBottom: 6, fontSize: '.7rem', fontWeight: 700, color: 'var(--mid)', letterSpacing: '.05em', textTransform: 'uppercase' }}>
              <div>Description</div>
              <div>HSN/SAC</div>
              <div>Qty</div>
              <div>Rate</div>
              <div>Tax %</div>
              <div>Amount</div>
              <div></div>
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1.3fr .6fr .9fr .65fr .9fr auto', gap: 8, marginBottom: 8 }}>
                <input data-testid={`inv-item-desc-${idx}`} placeholder="Description" className="form-input" value={it.description} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], description: e.target.value }; return { ...f, items }; })} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <input data-testid={`inv-item-hsn-${idx}`} placeholder="HSN/SAC" className="form-input" value={it.hsn_code || ''} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], hsn_code: e.target.value }; return { ...f, items }; })} style={{ flex: 1, minWidth: 0 }} />
                  <select
                    data-testid={`inv-item-hsn-quickfill-${idx}`}
                    value=""
                    onChange={e => {
                      const code = e.target.value;
                      if (!code) return;
                      setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], hsn_code: code }; return { ...f, items }; });
                      e.target.value = '';  // Reset to placeholder so the same select can be re-used.
                    }}
                    className="form-select"
                    title="Quick-fill HSN/SAC code"
                    style={{ width: 38, padding: '0 4px', fontSize: '.7rem', cursor: 'pointer', background: 'var(--teal-lt)', color: 'var(--teal)', borderColor: 'var(--teal-lt)' }}
                  >
                    <option value="">Quick fill code</option>
                    <option value="999311">Nutritional Consultation (SAC) - 999311</option>
                    <option value="999311">Diet Programme (SAC) - 999311</option>
                    <option value="999312">Health Assessment Report (SAC) - 999312</option>
                    <option value="21069099">Vitamin B12 Supplement (HSN) - 21069099</option>
                    <option value="21069099">Probiotic Supplement (HSN) - 21069099</option>
                    <option value="29049900">Magnesium Supplement (HSN) - 29049900</option>
                  </select>
                </div>
                <input data-testid={`inv-item-qty-${idx}`} placeholder="Qty" type="number" min="0" step="1" className="form-input" value={it.quantity ?? 1} onChange={e => setForm(f => {
                  const items = [...f.items];
                  const qty = e.target.value === '' ? 0 : Number(e.target.value);
                  const rate = Number(items[idx].rate) || 0;
                  items[idx] = { ...items[idx], quantity: qty, amount: Number((qty * rate).toFixed(2)) };
                  return { ...f, items };
                })} />
                <input data-testid={`inv-item-rate-${idx}`} placeholder="Rate" type="number" min="0" step="0.01" className="form-input" value={it.rate} onChange={e => setForm(f => {
                  const items = [...f.items];
                  const rate = e.target.value === '' ? 0 : Number(e.target.value);
                  const qty = Number(items[idx].quantity ?? 1) || 0;
                  items[idx] = { ...items[idx], rate, amount: Number((qty * rate).toFixed(2)) };
                  return { ...f, items };
                })} />
                <input data-testid={`inv-item-tax-${idx}`} placeholder="18" type="number" min="0" max="100" step="0.01" className="form-input" value={it.tax_rate ?? 18} onChange={e => setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], tax_rate: e.target.value === '' ? 0 : Number(e.target.value) }; return { ...f, items }; })} />
                <input data-testid={`inv-item-amount-${idx}`} placeholder="Amount" type="number" className="form-input" value={it.amount} readOnly tabIndex={-1} style={{ background: 'var(--cream)', cursor: 'not-allowed', color: 'var(--ink)', fontWeight: 600 }} title="Auto-calculated: Qty x Rate" />
                <button type="button" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} style={{ background: 'var(--coral-lt)', color: 'var(--coral)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}><X size={14} /></button>
              </div>
            ))}
            <button type="button" data-testid="inv-add-item" onClick={() => setForm(f => ({ ...f, items: [...f.items, { description: '', hsn_code: '', quantity: 1, rate: 0, tax_rate: 18, amount: 0 }] }))} className="btn-ghost btn-sm" style={{ marginBottom: 18 }}><Plus size={14} /> Add item</button>

            {/* GST preview */}
            <div style={{ background: 'var(--teal-lt)', borderRadius: 10, padding: 14, fontSize: '.85rem', marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Taxable Amount:</span><strong>Rs. {taxable.toFixed(2)}</strong></div>
              {isIntra ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>CGST @ 9%:</span><strong>Rs. {(gstTotal / 2).toFixed(2)}</strong></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>SGST @ 9%:</span><strong>Rs. {(gstTotal / 2).toFixed(2)}</strong></div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>IGST @ 18%:</span><strong>Rs. {gstTotal.toFixed(2)}</strong></div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}><span><strong>Total:</strong></span><strong data-testid="inv-total">Rs. {total.toFixed(2)}</strong></div>
            </div>

            {/* Plan Includes */}
            <h3 style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--teal)', marginBottom: 10 }}>Plan Includes</h3>
            {form.plan_includes.map((b, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input className="form-input" placeholder="Bullet point" value={b} onChange={e => setForm(f => { const p = [...f.plan_includes]; p[idx] = e.target.value; return { ...f, plan_includes: p }; })} style={{ flex: 1 }} />
                <button type="button" onClick={() => setForm(f => ({ ...f, plan_includes: f.plan_includes.filter((_, i) => i !== idx) }))} style={{ background: 'var(--coral-lt)', color: 'var(--coral)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}><X size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm(f => ({ ...f, plan_includes: [...f.plan_includes, ''] }))} className="btn-ghost btn-sm" style={{ marginBottom: 18 }}><Plus size={14} /> Add bullet</button>

            {/* Payment */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 18 }}>
              <div><label className="form-label">Payment Mode</label>
                <select data-testid="inv-pay-mode" className="form-select" value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))}>
                  {['Razorpay', 'UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Card'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div><label className="form-label">Transaction ID</label><input className="form-input" value={form.transaction_id} onChange={e => setForm(f => ({ ...f, transaction_id: e.target.value }))} /></div>
              <div><label className="form-label">Status</label>
                <select data-testid="inv-status" className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="UNPAID">Unpaid</option>
                  <option value="PAID">Paid</option>
                </select>
              </div>
              <div style={{ gridColumn: 'span 3' }}><label className="form-label">Notes (optional)</label><textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-ghost btn-sm"><X size={14} />Cancel</button>
              <button onClick={save} data-testid="save-invoice-btn" className="btn-teal btn-sm"><Check size={14} />{editing ? 'Save Changes' : 'Create Invoice'}</button>
            </div>
            </div>
            {/* ── LIVE PREVIEW PANE ── */}
            <div data-testid="invoice-live-preview" style={{ background: '#F7F3EE', padding: '20px', overflowY: 'auto', maxHeight: '92vh', borderLeft: '1px solid var(--border)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: 12 }}>📄 Live Preview</div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '22px 24px', boxShadow: '0 6px 20px rgba(15,76,92,.08)', fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0F4C5C', paddingBottom: 14, marginBottom: 14 }}>
                  <div style={{ maxWidth: '52%' }}>
                    {siteSettings?.practice_logo ? (
                      <img data-testid="preview-practice-logo" src={siteSettings.practice_logo} alt="Practice logo" style={{ maxWidth: 180, maxHeight: 48, objectFit: 'contain', display: 'block' }} />
                    ) : (
                      <div data-testid="preview-default-logo" style={{ fontFamily: "'Great Vibes', cursive", fontSize: 30, color: '#0F4C5C', lineHeight: 1 }}>Anjum Sohail</div>
                    )}
                    <div data-testid="preview-registered-address" style={{ fontSize: 8.5, color: '#4A4A4A', marginTop: 8, lineHeight: 1.45 }}>{practice.address}</div>
                    <div data-testid="preview-gstin" style={{ fontSize: 8.5, color: '#4A4A4A', marginTop: 2 }}>GSTIN: <strong style={{ color: '#1A1A1A' }}>{practice.gstin}</strong></div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 9, color: '#4A4A4A', lineHeight: 1.55 }}>
                    <div>{practice.email}</div>
                    <div>{practice.phone}</div>
                    <div>{practice.website}</div>
                  </div>
                </div>
                {/* Meta */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: '#4A4A4A' }}>
                    <div><span style={{ color: '#0F4C5C', fontWeight: 700, fontSize: 9 }}>INVOICE NO.</span> <strong>{editing?.invoice_number || (() => { const d = new Date(); const fyEnd = d.getMonth() >= 3 ? d.getFullYear() + 1 : d.getFullYear(); return `INV-FY${String(fyEnd).slice(-2)}-XXXX`; })()}</strong></div>
                    <div><span style={{ color: '#0F4C5C', fontWeight: 700, fontSize: 9 }}>DATE</span> {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                    <div><span style={{ color: '#0F4C5C', fontWeight: 700, fontSize: 9 }}>MODE</span> {form.payment_mode}</div>
                  </div>
                  <div data-testid="preview-status-badge" style={{ background: form.status === 'PAID' ? '#DCFCE7' : '#FEF3C7', color: form.status === 'PAID' ? '#065F46' : '#92400E', padding: '4px 10px', borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '.04em' }}>● {form.status === 'PAID' ? 'PAID' : 'UNPAID'}</div>
                </div>
                {/* Bill To */}
                <div style={{ display: 'grid', gridTemplateColumns: form.show_gst ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 8, color: '#0F4C5C', fontWeight: 700, letterSpacing: '.1em', marginBottom: 4 }}>BILL TO</div>
                    <div style={{ fontWeight: 700, color: '#1A1A1A' }}>{form.client.name || '—'}</div>
                    <div style={{ color: '#4A4A4A' }}>{form.client.email || '—'}</div>
                    <div style={{ color: '#4A4A4A' }}>{form.client.phone || '—'}</div>
                  </div>
                  {form.show_gst && (form.gst?.gstin || form.gst?.company_name) && (
                    <div>
                      <div style={{ fontSize: 8, color: '#0F4C5C', fontWeight: 700, letterSpacing: '.1em', marginBottom: 4 }}>GST DETAILS</div>
                      <div style={{ fontWeight: 700, color: '#1A1A1A' }}>{form.gst.company_name || '—'}</div>
                      <div style={{ color: '#4A4A4A' }}>{form.gst.gstin || '—'}</div>
                      <div style={{ color: '#4A4A4A' }}>{
                        (form.gst.city || form.gst.pincode || form.gst.state)
                          ? [(`${form.gst.city || ''} ${form.gst.pincode || ''}`).trim(), form.gst.state].filter(Boolean).join(', ')
                          : (form.gst.city_state || '—')
                      }</div>
                    </div>
                  )}
                </div>
                {/* Items */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 9 }}>
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid #0F4C5C' }}>
                      <th style={{ textAlign: 'left', padding: '6px 4px', color: '#0F4C5C', fontSize: 8, letterSpacing: '.06em' }}>DESCRIPTION</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: '#0F4C5C', fontSize: 8 }}>HSN/SAC</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: '#0F4C5C', fontSize: 8 }}>QTY</th>
                      <th style={{ textAlign: 'right', padding: '6px 4px', color: '#0F4C5C', fontSize: 8 }}>RATE</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: '#0F4C5C', fontSize: 8 }}>TAX %</th>
                      <th style={{ textAlign: 'right', padding: '6px 4px', color: '#0F4C5C', fontSize: 8 }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const qtyRaw = (it.quantity === undefined || it.quantity === null) ? 1 : it.quantity;
                      const qtyN = Number(qtyRaw) || 0;
                      const qtyDisp = qtyN === Math.floor(qtyN) ? String(Math.floor(qtyN)) : qtyN.toFixed(2);
                      const taxRaw = (it.tax_rate === undefined || it.tax_rate === null) ? 18 : it.tax_rate;
                      return (
                      <tr key={idx} style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '6px 4px', color: '#1A1A1A' }}>{it.description || '-'}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#4A4A4A' }}>{it.hsn_code || it.sac_code || '999311'}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#4A4A4A' }}>{qtyDisp}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right' }}>Rs. {Number(it.rate || 0).toFixed(2)}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', color: '#4A4A4A' }}>{Number(taxRaw).toFixed(0)}%</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right' }}>Rs. {Number(it.amount || 0).toFixed(2)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Totals */}
                <div style={{ marginLeft: '40%', borderTop: '1px solid #0F4C5C', paddingTop: 8, fontSize: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Taxable Amount</span><span>Rs. {taxable.toFixed(2)}</span></div>
                  {isIntra ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>CGST @ 9%</span><span>Rs. {(gstTotal / 2).toFixed(2)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>SGST @ 9%</span><span>Rs. {(gstTotal / 2).toFixed(2)}</span></div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>IGST @ 18%</span><span>Rs. {gstTotal.toFixed(2)}</span></div>
                  )}
                  <div style={{ background: '#0F4C5C', color: '#fff', padding: '8px 12px', marginTop: 6, borderRadius: 4, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, fontFamily: "'Fraunces', serif" }}>
                    <span>TOTAL</span><span>Rs. {total.toFixed(2)}/-</span>
                  </div>
                </div>
                {/* Plan includes */}
                {form.plan_includes.filter(p => p.trim()).length > 0 && (
                  <div style={{ background: '#E6EEF0', borderRadius: 6, padding: '10px 14px', marginTop: 14, fontSize: 9.5 }}>
                    <div style={{ fontWeight: 700, color: '#0F4C5C', marginBottom: 4 }}>Plan Includes</div>
                    {form.plan_includes.filter(p => p.trim()).map((p, i) => (
                      <div key={i} style={{ color: '#4A4A4A', marginBottom: 2 }}>• {p}</div>
                    ))}
                  </div>
                )}
                {/* Payment Details */}
                <div style={{ background: '#E6EEF0', borderRadius: 6, padding: '10px 14px', marginTop: 10, fontSize: 9 }}>
                  <div style={{ fontWeight: 700, color: '#0F4C5C', marginBottom: 4, fontSize: 9.5 }}>Payment Details</div>
                  <div style={{ color: '#4A4A4A', lineHeight: 1.6 }}>
                    <div>Account: NUTRITIONIST ANJUM SOHAIL</div>
                    <div>Bank: Kotak Mahindra Bank · A/c 9820146454</div>
                    <div>IFSC: KKBK0008045 · UPI: 9820146454</div>
                  </div>
                </div>
                {/* Footer */}
                <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #E5E7EB', color: '#0F4C5C', fontSize: 10, fontStyle: 'italic' }}>
                  Thank you for choosing Anjum Sohail for your health journey.
                </div>
                <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 8, marginTop: 4 }}>
                  This is a computer-generated invoice and does not require a signature.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal - opens after Create/Edit save. Iframe loads via blob URL (no auth needed). */}
      {preview.open && (
        <div data-testid="invoice-preview-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '95%', maxWidth: 980, height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)', background: 'var(--cream)' }}>
              <div>
                <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '1.15rem', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Preview Invoice</h2>
                <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: 2 }}>{preview.invNum} - review before downloading or sending to the client.</div>
              </div>
              <button onClick={closePreview} data-testid="invoice-preview-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--mid)', padding: 6 }} title="Close"><X size={22} /></button>
            </div>
            <div style={{ flex: 1, background: '#525659', overflow: 'hidden' }}>
              {preview.pdfUrl ? (
                <iframe data-testid="invoice-preview-iframe" src={preview.pdfUrl} title={`Invoice ${preview.invNum}`} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <div style={{ color: '#fff', textAlign: 'center', padding: 60 }}>Loading PDF...</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--border)', justifyContent: 'flex-end', background: '#fff' }}>
              <button onClick={closePreview} data-testid="invoice-preview-close-btn" className="btn-ghost btn-sm"><X size={14} /> Close</button>
              <button onClick={previewDownload} data-testid="invoice-preview-download-btn" className="btn-ghost btn-sm" style={{ background: 'var(--teal-lt)', color: 'var(--teal)' }}><Download size={14} /> Download PDF</button>
              <button onClick={previewSend} disabled={preview.sending} data-testid="invoice-preview-send-btn" className="btn-teal btn-sm"><Send size={14} /> {preview.sending ? 'Sending...' : 'Send to Client'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Change Password Card (Settings page) ─────────────────────────────────────
function ChangePasswordCard() {
  const { authHeaders } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });
    if (next.length < 8) { setMsg({ type: 'err', text: 'New password must be at least 8 characters.' }); return; }
    if (next !== confirm) { setMsg({ type: 'err', text: 'New password and confirm do not match.' }); return; }
    if (next === current) { setMsg({ type: 'err', text: 'New password must be different from current.' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeaders() });
      setMsg({ type: 'ok', text: '✓ Password updated. You can keep working — no logout needed.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.detail || 'Failed to update password.' });
    } finally {
      setBusy(false);
    }
  };

  const inp = { ...{ } }; // placeholder for shared inline override (not used)

  return (
    <div data-testid="change-password-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
      <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Change Password</h3>
      <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>
        Updates your admin login password immediately. Your current session stays active — no logout required.
      </p>
      <form onSubmit={submit} style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
        <div>
          <label className="form-label">Current Password</label>
          <input data-testid="current-password-input" className="form-input" type={show ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" required />
        </div>
        <div>
          <label className="form-label">New Password <span style={{ fontWeight: 400, color: 'var(--mid)', fontSize: '.78rem' }}>(min. 8 characters)</span></label>
          <input data-testid="new-password-input" className="form-input" type={show ? 'text' : 'password'} value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password" required />
        </div>
        <div>
          <label className="form-label">Confirm New Password</label>
          <input data-testid="confirm-password-input" className="form-input" type={show ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" required />
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '.84rem', color: 'var(--mid)', cursor: 'pointer' }}>
          <input data-testid="show-password-toggle" type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} /> Show passwords
        </label>
        {msg.text && (
          <div data-testid="change-password-msg" style={{ padding: '10px 14px', borderRadius: 8, fontSize: '.86rem', background: msg.type === 'ok' ? '#DCFCE7' : '#FEE2E2', color: msg.type === 'ok' ? '#15803D' : '#B91C1C', fontWeight: 600 }}>{msg.text}</div>
        )}
        <div>
          <button data-testid="change-password-submit" type="submit" disabled={busy || !current || !next || !confirm} className="btn-teal btn-sm">
            {busy ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SettingsManager() {
  const { authHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Email preview modal state
  const [emailPreview, setEmailPreview] = useState(null); // { html, hasLogo, template }
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewErr, setEmailPreviewErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const openEmailPreview = async (template = 'diet_review') => {
    setEmailPreviewLoading(true);
    setEmailPreviewErr('');
    setEmailPreview({ html: '', hasLogo: false, template });
    try {
      const r = await axios.get(`${API}/admin/settings/email-preview`, {
        params: { template },
        headers: authHeaders(),
      });
      setEmailPreview({ html: r.data.html || '', hasLogo: !!r.data.has_logo, template });
    } catch (e) {
      setEmailPreviewErr(e.response?.data?.detail || e.message || 'Failed to load preview');
      setEmailPreview(null);
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  const toggleSection = (key) => {
    setSettings(s => ({ ...s, data: { ...s.data, sections_visible: { ...s.data?.sections_visible, [key]: !s.data?.sections_visible?.[key] } } }));
  };

  const updateNested = (parent, key, val) => {
    setSettings(s => ({ ...s, data: { ...s.data, [parent]: { ...(s.data?.[parent] || {}), [key]: val } } }));
  };
  const updateScalar = (key, val) => {
    setSettings(s => ({ ...s, data: { ...s.data, [key]: val } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { data: settings.data }, { headers: authHeaders() });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  if (!settings) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--mid)' }}>Loading settings...</div>;

  const sections = settings.data?.sections_visible || {};
  const rzpLinks = settings.data?.razorpay_links || {};
  const rzpKeys = settings.data?.razorpay_keys || {};
  const tracking = settings.data?.tracking || {};
  const placeholderCount = PAYMENT_LINK_ROWS.filter(r => (rzpLinks[r.key] || '').includes('anjum-')).length;

  return (
    <div data-testid="admin-settings" style={{ padding: '28px', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--ink)' }}>Website Settings</h1>
        <button onClick={save} disabled={saving} className="btn-teal btn-sm" data-testid="save-settings-btn">
          {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <ChangePasswordCard />

      {placeholderCount > 0 && (
        <div data-testid="razorpay-todo-banner" style={{ background: '#FEF9C3', border: '1.5px solid #EAB308', borderRadius: 12, padding: '14px 18px', marginBottom: 22, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertCircle size={22} color="#A16207" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.9rem', color: '#713F12' }}>
            <strong>⚠️ {placeholderCount} Razorpay payment link{placeholderCount === 1 ? '' : 's'} still use a placeholder URL.</strong> Before going live, replace each <code style={{ background: '#FEF3C7', padding: '1px 5px', borderRadius: 4 }}>rzp.io/l/anjum-*</code> placeholder with your real Razorpay payment link under <em>Payment Links</em> below.
          </div>
        </div>
      )}

      {/* PAYMENT LINKS */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Payment Links <span style={{ fontSize: '.78rem', color: 'var(--mid)', fontWeight: 500 }}>(Razorpay — services only)</span></h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>Service-level payment links. Digital-guide buy links are now managed individually under <strong>Admin → Products → Edit → Razorpay Buy Link</strong>. Every CTA on the site with <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>data-rzp-key</code> is automatically swapped to the URL below on page load.</p>
        <div style={{ display: 'grid', gap: 10 }}>
          {PAYMENT_LINK_ROWS.map(row => (
            <div key={row.key} style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '.88rem', fontWeight: 600, color: 'var(--ink)' }}>{row.label}</div>
                <div style={{ fontSize: '.76rem', color: 'var(--mid)' }}>{row.price}{row.note ? ` · ${row.note}` : ''}</div>
              </div>
              <input data-testid={`payment-link-${row.key}`} className="form-input" value={rzpLinks[row.key] || ''} placeholder={`https://rzp.io/l/anjum-${row.key.replace(/_/g, '-')}`} onChange={e => updateNested('razorpay_links', row.key, e.target.value)} />
              <a data-testid={`payment-link-test-${row.key}`} href={rzpLinks[row.key] || '#'} target="_blank" rel="noopener noreferrer" style={{ background: 'var(--cream)', color: 'var(--teal)', padding: '8px 14px', borderRadius: 8, fontSize: '.78rem', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}><LinkIcon size={13} /> Test</a>
            </div>
          ))}
        </div>
      </div>

      {/* RAZORPAY KEYS */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Pricing</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>These prices read dynamically into the public site (e.g. <code>/diet-review.html</code>). Changes are live immediately — no redeployment needed.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div><label className="form-label">Diet Review Price (₹)</label><input data-testid="diet-review-price-input" type="number" min="0" className="form-input" value={settings.data?.pricing?.diet_review_price ?? 499} onChange={e => updateNested('pricing', 'diet_review_price', parseInt(e.target.value || '0', 10))} /></div>
          <div><label className="form-label">Diet Review MRP (₹, strikethrough)</label><input data-testid="diet-review-mrp-input" type="number" min="0" className="form-input" value={settings.data?.pricing?.diet_review_mrp ?? 999} onChange={e => updateNested('pricing', 'diet_review_mrp', parseInt(e.target.value || '0', 10))} /></div>
          <div><label className="form-label">Currency Prefix</label><input data-testid="diet-review-currency-input" className="form-input" value={settings.data?.pricing?.diet_review_currency ?? 'Rs.'} onChange={e => updateNested('pricing', 'diet_review_currency', e.target.value)} /></div>
        </div>
      </div>

      {/* RAZORPAY KEYS */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Razorpay Credentials</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>Needed only if you enable webhook-based auto-order creation. Get these from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--coral)' }}>Razorpay Dashboard → Settings → API Keys</a>.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div><label className="form-label">Razorpay Key ID (live)</label><input data-testid="razorpay-key-id" className="form-input" value={rzpKeys.key_id || ''} placeholder="rzp_live_xxxxxxxx" onChange={e => updateNested('razorpay_keys', 'key_id', e.target.value)} /></div>
          <div><label className="form-label">Razorpay Key Secret</label><input data-testid="razorpay-key-secret" className="form-input" type="password" value={rzpKeys.key_secret || ''} placeholder="(keep secret)" onChange={e => updateNested('razorpay_keys', 'key_secret', e.target.value)} /></div>
          <div><label className="form-label">Razorpay Webhook Secret</label><input data-testid="razorpay-webhook-secret" className="form-input" type="password" value={rzpKeys.webhook_secret || ''} placeholder="(keep secret)" onChange={e => updateNested('razorpay_keys', 'webhook_secret', e.target.value)} /></div>
        </div>
      </div>

      {/* PRACTICE LOGO UPLOAD — used in invoice PDFs & all transactional emails */}
      <div data-testid="settings-practice-logo" style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)' }}>Practice Logo <span style={{ fontSize: '.78rem', color: 'var(--mid)', fontWeight: 500 }}>(invoice PDFs &amp; transactional emails)</span></h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="email-preview-diet-btn"
              onClick={() => openEmailPreview('diet_review')}
              className="btn-ghost btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Preview the diet review report email exactly as a client would see it"
            >
              <Eye size={14} /> Preview Diet Review Email
            </button>
            <button
              type="button"
              data-testid="email-preview-invoice-btn"
              onClick={() => openEmailPreview('invoice')}
              className="btn-ghost btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Preview the invoice email exactly as a client would see it"
            >
              <Eye size={14} /> Preview Invoice Email
            </button>
          </div>
        </div>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 16 }}>Upload a custom logo (PNG/JPG recommended, max 500 KB) to replace the default cursive script on invoice PDFs, invoice emails, diet review reports, and cart-recovery emails. Leave empty to keep the default brand logo. Does not affect the public website nav.</p>
        <PracticeLogoUploader
          dataUrl={settings.data?.practice_logo || ''}
          authHeaders={authHeaders}
          onUploaded={(url) => updateScalar('practice_logo', url)}
          onCleared={() => updateScalar('practice_logo', '')}
        />
      </div>

      {/* LEGAL & COMPLIANCE — TWO FSSAI FIELDS */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Legal & Compliance</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>FSSAI license numbers shown on the public site, supplement order pages, and PDFs. Each field is independently editable and togglable.</p>
        {/* MARKETED BY */}
        <div style={{ borderLeft: '3px solid var(--coral)', paddingLeft: 16, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Marketed By FSSAI License No.</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: 'var(--mid)' }} data-testid="marketed-by-fssai-toggle">
              <input type="checkbox" checked={!!settings.data?.compliance?.marketed_by_fssai_visible} onChange={e => updateNested('compliance', 'marketed_by_fssai_visible', e.target.checked)} />
              {settings.data?.compliance?.marketed_by_fssai_visible ? 'Visible' : 'Hidden'}
            </label>
          </div>
          <p style={{ fontSize: '.74rem', color: 'var(--muted)', marginBottom: 6 }}>Anjum Sohail's own FSSAI license for marketing supplements.</p>
          <input data-testid="marketed-by-fssai" className="form-input" value={settings.data?.compliance?.marketed_by_fssai_no || ''} placeholder="11224999000600" onChange={e => updateNested('compliance', 'marketed_by_fssai_no', e.target.value)} />
        </div>
        {/* MANUFACTURED BY */}
        <div style={{ borderLeft: '3px solid var(--teal)', paddingLeft: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Manufactured By FSSAI License No.</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: 'var(--mid)' }} data-testid="manufactured-by-fssai-toggle">
              <input type="checkbox" checked={!!settings.data?.compliance?.manufactured_by_fssai_visible} onChange={e => updateNested('compliance', 'manufactured_by_fssai_visible', e.target.checked)} />
              {settings.data?.compliance?.manufactured_by_fssai_visible ? 'Visible' : 'Hidden'}
            </label>
          </div>
          <p style={{ fontSize: '.74rem', color: 'var(--muted)', marginBottom: 8 }}>Manufacturer's FSSAI license — update when manufacturer changes.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
            <input data-testid="manufactured-by-name" className="form-input" placeholder="Manufacturer name (e.g. ABC Nutraceuticals Pvt Ltd)" value={settings.data?.compliance?.manufactured_by_name || ''} onChange={e => updateNested('compliance', 'manufactured_by_name', e.target.value)} />
            <input data-testid="manufactured-by-address" className="form-input" placeholder="Manufacturer address" value={settings.data?.compliance?.manufactured_by_address || ''} onChange={e => updateNested('compliance', 'manufactured_by_address', e.target.value)} />
          </div>
          <input data-testid="manufactured-by-fssai" className="form-input" placeholder="Manufacturer FSSAI license no." value={settings.data?.compliance?.manufactured_by_fssai_no || ''} onChange={e => updateNested('compliance', 'manufactured_by_fssai_no', e.target.value)} />
        </div>
      </div>

      {/* CONTACT */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Contact & Delivery</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 16 }}>Registered address &amp; GSTIN below appear on every invoice (PDF + preview) and are legally required on a GST invoice.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {['whatsapp', 'email', 'phone', 'website', 'address', 'gstin'].map(field => (
            <div key={field} style={{ gridColumn: field === 'address' ? 'span 2' : undefined }}>
              <label className="form-label" style={{ textTransform: 'capitalize' }}>{field === 'gstin' ? 'GSTIN (15-char)' : field === 'address' ? 'Registered Address (shown on invoices)' : field}</label>
              <input data-testid={`contact-${field}`} className="form-input" value={settings.data?.contact?.[field] || ''} placeholder={
                field === 'address' ? 'No 213, 2nd Floor, Ramanashree Arcade, 18 MG Road, Ashok Nagar, Bengaluru 560001'
                  : field === 'gstin' ? '29FWRPS8543G1ZI'
                  : field === 'website' ? 'anjumsohail.com'
                  : ''
              } onChange={e => updateNested('contact', field, e.target.value)} />
            </div>
          ))}
          <div>
            <label className="form-label">Diet Review report email recipient</label>
            <input data-testid="diet-review-email" className="form-input" value={settings.data?.diet_review_email || ''} placeholder="info@anjumsohail.com" onChange={e => updateScalar('diet_review_email', e.target.value)} />
          </div>
          <div>
            <label className="form-label">WhatsApp number for CTAs</label>
            <input data-testid="whatsapp-number" className="form-input" value={settings.data?.whatsapp_number || ''} placeholder="+919187542262" onChange={e => updateScalar('whatsapp_number', e.target.value)} />
          </div>
        </div>
      </div>

      {/* TRACKING */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Analytics &amp; Tracking</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>Tracking IDs are stored securely. These are displayed here for reference; currently each HTML page has its own hard-coded IDs. Setting these values here will enable dynamic injection (P1 task).</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {TRACKING_ROWS.map(row => (
            <div key={row.key}>
              <label className="form-label">{row.label}</label>
              <input data-testid={`tracking-${row.key}`} className="form-input" value={tracking[row.key] || ''} placeholder={row.placeholder} onChange={e => updateNested('tracking', row.key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* BRAND PHOTOS (Anjum's Photo — Home Hero + About) */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 20 }} data-testid="settings-brand-photos">
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 6 }}>Anjum's Photos</h3>
        <p style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: 18 }}>Upload/replace the Homepage Hero Photo and About Page Photo. JPG/PNG/WebP, max 5 MB. Live site updates instantly.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {[{ key: 'home_hero', label: 'Homepage Hero Photo' }, { key: 'about', label: 'About Page Photo' }].map(p => (
            <BrandPhotoSlot key={p.key} photoKey={p.key} label={p.label} url={settings.data?.brand_photos?.[p.key] || ''} authHeaders={authHeaders} onChange={(url) => updateNested('brand_photos', p.key, url)} />
          ))}
        </div>
      </div>

      {/* HOMEPAGE SECTIONS */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid var(--border)' }}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: 'var(--ink)', marginBottom: 16 }}>Homepage Section Visibility</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {Object.entries(sections).map(([key, val]) => (
            <div key={key} data-testid={`section-toggle-${key}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 10, background: val ? 'var(--teal-lt)' : '#fafafa' }}>
              <span style={{ fontSize: '.88rem', fontWeight: 600, color: 'var(--ink)', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
              <button onClick={() => toggleSection(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: val ? 'var(--teal)' : 'var(--muted)' }}>
                {val ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Email Preview Modal — renders the real branded email HTML in an iframe */}
      {emailPreview !== null && (
        <div
          data-testid="email-preview-modal"
          onClick={() => setEmailPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,76,92,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: '1.1rem', color: '#0F4C5C', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Eye size={16} /> Email Preview — {emailPreview.template === 'invoice' ? 'Invoice' : 'Diet Review Report'}
                </h3>
                <div style={{ fontSize: '.74rem', color: 'var(--mid)', marginTop: 3 }}>
                  This is what clients will see in Gmail · Outlook · iCloud Mail. {emailPreview.hasLogo
                    ? <span style={{ color: '#166534', fontWeight: 600 }}>Logo is loaded from the public endpoint ✓</span>
                    : <span style={{ color: '#92400E', fontWeight: 600 }}>No logo uploaded — cursive HTML fallback shown.</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  data-testid="email-preview-switch"
                  onClick={() => openEmailPreview(emailPreview.template === 'invoice' ? 'diet_review' : 'invoice')}
                  className="btn-ghost btn-sm"
                  title="Switch template"
                >
                  Show {emailPreview.template === 'invoice' ? 'Diet Review' : 'Invoice'} instead
                </button>
                <button
                  data-testid="email-preview-close"
                  onClick={() => setEmailPreview(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)' }}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, background: '#F7F3EE', overflow: 'auto', minHeight: 400 }}>
              {emailPreviewLoading ? (
                <div style={{ padding: 60, textAlign: 'center', color: 'var(--mid)' }}>Loading preview…</div>
              ) : emailPreviewErr ? (
                <div style={{ padding: 24, color: '#7F1D1D', background: '#FEE2E2', margin: 16, borderRadius: 10, fontSize: '.9rem' }}>{emailPreviewErr}</div>
              ) : (
                <iframe
                  data-testid="email-preview-iframe"
                  title="Email preview"
                  srcDoc={emailPreview.html}
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: '64vh', border: 'none', background: '#F7F3EE', display: 'block' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin Protected Route ────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/admin" replace />;
  return children;
}

// ─── Admin Layout ─────────────────────────────────────────────────────────────
// ─── Admin Notification Bell ──────────────────────────────────────────────────
function NotificationBell() {
  const { authHeaders } = useAuth();
  const [counts, setCounts] = useState({ leads: 0, diet_reviews: 0, orders_24h: 0, clients_24h: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/notification-counts`, { headers: authHeaders() });
      setCounts(r.data || {});
    } catch (e) { /* silent */ }
  }, [authHeaders]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(id); document.removeEventListener('mousedown', onClick); };
  }, [refresh]);

  const go = (path) => { setOpen(false); navigate(path); };
  const hasAny = counts.total > 0;
  const items = [
    { key: 'leads', label: 'New leads', count: counts.leads, path: '/admin/leads', testid: 'bell-leads' },
    { key: 'diet_reviews', label: 'Unanswered diet reviews', count: counts.diet_reviews, path: '/admin/diet-reviews', testid: 'bell-diet-reviews' },
    { key: 'orders_24h', label: 'New orders (24h)', count: counts.orders_24h, path: '/admin/orders', testid: 'bell-orders' },
    { key: 'clients_24h', label: 'New clients (24h)', count: counts.clients_24h, path: '/admin/clients', testid: 'bell-clients' },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button data-testid="notification-bell" aria-label="Notifications" onClick={() => setOpen(o => !o)} style={{ background: hasAny ? '#FEE2E2' : '#F3F4F6', border: 'none', width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'background .15s' }}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={hasAny ? '#DC2626' : '#4B5563'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>
        {hasAny && (
          <span data-testid="notification-badge" style={{ position: 'absolute', top: -2, right: -2, background: '#DC2626', color: '#fff', fontSize: '.64rem', fontWeight: 800, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', lineHeight: 1 }}>{counts.total > 99 ? '99+' : counts.total}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" style={{ position: 'absolute', right: 0, top: 48, width: 300, background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,.15)', border: '1px solid #E5E7EB', overflow: 'hidden', zIndex: 1000 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
            <strong style={{ fontSize: '.9rem', color: '#111827' }}>Notifications</strong>
            <div style={{ fontSize: '.72rem', color: '#6B7280', marginTop: 2 }}>Auto-refreshes every 60 seconds</div>
          </div>
          {items.map(it => (
            <button key={it.key} data-testid={it.testid} onClick={() => go(it.path)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px 16px', background: 'none', border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: '.86rem', color: '#111827', fontWeight: it.count > 0 ? 600 : 400 }}>{it.label}</span>
              <span style={{ background: it.count > 0 ? '#FEE2E2' : '#F3F4F6', color: it.count > 0 ? '#991B1B' : '#6B7280', padding: '2px 9px', borderRadius: 50, fontSize: '.75rem', fontWeight: 700, minWidth: 26, textAlign: 'center' }}>{it.count}</span>
            </button>
          ))}
          {counts.total === 0 && (
            <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: '.82rem', color: '#6B7280' }}>🌿 All caught up. Nothing pending.</div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminLayout({ children }) {
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <AdminSidebar />
      <main style={{ flex: 1, background: '#f8f9fa', overflowY: 'auto' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '10px 28px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <NotificationBell />
        </div>
        {children}
      </main>
    </div>
  );
}

// ─── Nutrition Assessment Reports (intake-form submissions + report management) ──
function AssessmentReportsManager() {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', email: '' });
  const [manualResult, setManualResult] = useState(null);
  const [error, setError] = useState('');
  // Trash state (matches Diet Reviews pattern — 7-day undo window)
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const deriveStatus = (r) => {
    if (r?.report_sent_at) return 'sent';
    if (r?.report_pdf || r?.report_uploaded_at) return 'report_ready';
    if (r?.submitted_at && r?.full_name) return 'pending_review';
    return 'form_sent';
  };
  const STATUS_BADGE = {
    form_sent:      { bg: '#FEF3C7', fg: '#92400E', label: 'Form Sent' },
    pending_review: { bg: '#DBEAFE', fg: '#1E3A8A', label: 'Pending Review' },
    report_ready:   { bg: '#E0E7FF', fg: '#3730A3', label: 'Report Ready' },
    sent:           { bg: '#DCFCE7', fg: '#14532D', label: 'Report Sent' },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assessment-reviews`, { headers: authHeaders() });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setItems([]);
      setError(e.response?.data?.detail || e.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => { if (!selected) load(); }, 30000); return () => clearInterval(t); }, [load, selected]);

  const filtered = items.filter((r) => {
    if (statusFilter && deriveStatus(r) !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [r.full_name, r.email, r.whatsapp].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const openItem = async (r) => {
    setSelected(r);
    setCopied(false);
    setError('');
    try {
      const resp = await axios.get(`${API}/assessment-reviews/${r.id}`, { headers: authHeaders() });
      setDetail(resp.data);
    } catch (e) {
      setDetail(r);
      setError(e.response?.data?.detail || 'Failed to load detail');
    }
  };

  const SECTIONS = [
    { title: 'Basic Details', fields: [
      ['Name','full_name'], ['Age','age'], ['Gender','gender'], ['Blood Group','blood_group'],
      ['Email','email'], ['WhatsApp','whatsapp'], ['City','city'],
      ['Height','height'], ['Weight','weight'], ['Waist','waist'], ['Hip','hip'],
      ['Waist-to-Height','waist_to_height'], ['Body Fat %','body_fat_pct'],
      ['Weight Trend (6 mo)','weight_trend'],
    ]},
    { title: 'Health Goals', fields: [
      ['Goals selected','goals'], ['Other goal / context','other_goal'],
    ]},
    { title: 'Current Diet', fields: [
      ['Diet type','diet_type'], ['Meals per day','meals_per_day'], ['Meal timings','meal_timings'],
      ['Typical weekday diet','typical_weekday'], ['Weekend diet','weekend_diet'],
      ['Water intake','water_intake'], ['Chai / Coffee','chai_coffee'],
      ['Cooks at home','cooks_at_home'], ['Time for meals','time_for_meals'],
      ['Food allergies / intolerances','food_allergies'],
    ]},
    { title: 'Health & Medical History', fields: [
      ['Diagnosed conditions','conditions'], ['Other conditions','conditions_other'],
      ['Current medications','medications'], ['Current supplements','supplements'],
      ['Family history','family_history'],
      ['Activity level','activity_level'], ['Sleep (hours)','sleep_hours'], ['Stress level (1-10)','stress_level'],
      ['Previous programmes / diets','previous_programmes'],
    ]},
    { title: 'Hormonal Health', fields: [
      ['Thyroid symptoms','thyroid_symptoms'],
      ['Energy pattern','energy_pattern'],
      ['Sleep quality','sleep_quality'],
      ['Unexplained weight changes','unexplained_weight_changes'],
      ['Recent stress events','recent_stress_events'],
      // Female-only
      ['Menstrual regularity','menstrual_regularity'],
      ['Cycle length (days)','cycle_length_days'],
      ['Period symptoms','period_symptoms'],
      ['PCOS symptoms','pcos_symptoms'],
      ['Pregnancy / breastfeeding','pregnancy_status'],
      ['Menopause status','menopause_status'],
      ['Hot flashes / night sweats','hot_flashes'],
      // Male 40+
      ['Male symptoms (40+)','male_symptoms'],
      ['Prostate health','prostate_health'],
      // All-gender optional
      ['Libido change (vs 2 yrs ago)','libido_change'],
    ]},
    { title: 'Digestive Health', fields: [
      ['Gut symptoms','digestive_issues'], ['Foods causing discomfort','food_discomfort'],
      ['Bowel frequency','bowel_frequency'], ['IBS / IBD history','ibs_history'],
    ]},
    { title: 'Bloodwork', fields: [
      ['Last full panel','last_blood_panel'],
      ['Abnormal markers (flagged by doctor)','abnormal_markers'],
      ['Marker-by-marker values','blood_markers'],
      ['Additional notes','blood_test_values'],
      ['Lab report PDF uploaded','has_lab_file'],
      ['Lab report filename','lab_filename'],
    ]},
    { title: 'Main Concerns', fields: [
      ['Top issues to focus on','top_issues'], ['Issue duration','issue_duration'],
      ['What has NOT worked','tried_before'], ['Other notes','other_notes'],
      ['Lifestyle factors','lifestyle_factors'],
    ]},
    { title: 'Supplement History', fields: [
      ['Supplements stopped','supplements_stopped'],
      ['Reasons for stopping','supplement_stop_reasons'],
      ['Adverse reactions','adverse_reactions'],
      ['Highest spend on supplements','supplements_spending'],
    ]},
    { title: 'Medical History Timeline', fields: [
      ['Health history narrative','health_history_narrative'],
      ['Surgeries / hospitalisations','surgeries_hospitalisations'],
      ['Ongoing specialist consults','ongoing_specialists'],
      ['Upcoming tests / procedures','upcoming_tests'],
    ]},
    { title: 'Goals in Detail', fields: [
      ['Primary goal (one line)','primary_goal_one_line'],
      ['Success in 90 days','success_in_90_days'],
      ['Blockers so far','blockers_so_far'],
      ['Under specialist / GP','under_specialist'],
      ['Doctor advice given','doctor_advice_given'],
    ]},
  ];

  const formatValue = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    if (typeof v === 'object') {
      // blood_markers structured dict: { thyroid: [{marker,value,unit,date}, ...] }
      const isMarkers = Object.values(v).every((x) => Array.isArray(x));
      if (isMarkers) {
        const lines = [];
        Object.entries(v).forEach(([panel, rows]) => {
          if (!rows || !rows.length) return;
          lines.push(`${panel}: ` + rows.map(r => `${r.marker}=${r.value || '?'} ${r.unit || ''}${r.date ? ' (' + r.date + ')' : ''}`).join('; '));
        });
        return lines.length ? lines.join('\n') : '—';
      }
      return Object.entries(v).filter(([,vv]) => vv).map(([k,vv]) => `${k}: ${vv}`).join('  ·  ') || '—';
    }
    return String(v);
  };

  const copyAll = () => {
    if (!detail) return;
    const lines = ['NUTRITION ASSESSMENT INTAKE — for Claude analysis', '─'.repeat(60), ''];
    SECTIONS.forEach((sec) => {
      lines.push(`### ${sec.title}`);
      sec.fields.forEach(([label, key]) => {
        lines.push(`${label}: ${formatValue(detail[key])}`);
      });
      lines.push('');
    });
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const uploadReport = async (file) => {
    if (!file || !detail) return;
    if (file.type !== 'application/pdf') { setError('Please upload a PDF file'); return; }
    setUploading(true);
    setError('');
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/assessment-reviews/${detail.id}/upload-report`,
        { pdf_data_url: dataUrl, filename: file.name },
        { headers: authHeaders() });
      const fresh = await axios.get(`${API}/assessment-reviews/${detail.id}`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const sendReport = async () => {
    if (!detail) return;
    if (!detail.report_pdf && !detail.report_uploaded_at) {
      setError('Upload a PDF before sending.');
      return;
    }
    if (!window.confirm(`Send the uploaded report PDF to ${detail.email}? This cannot be undone.`)) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/assessment-reviews/${detail.id}/send-report`, {}, { headers: authHeaders() });
      const fresh = await axios.get(`${API}/assessment-reviews/${detail.id}`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const removeReport = async () => {
    if (!detail?.id) return;
    if (!window.confirm('Remove the uploaded report? You will need to upload a new PDF before sending.')) return;
    try {
      await axios.delete(`${API}/assessment-reviews/${detail.id}/report`, { headers: authHeaders() });
      const fresh = await axios.get(`${API}/assessment-reviews/${detail.id}`, { headers: authHeaders() });
      setDetail(fresh.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove report');
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this submission? It will be soft-deleted and recoverable from the database.')) return;
    await axios.delete(`${API}/assessment-reviews/${id}`, { headers: authHeaders() });
    if (selected?.id === id) { setSelected(null); setDetail(null); }
    await load();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(x => x.id)));
  };
  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} submission(s)? They will be soft-deleted.`)) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/assessment-reviews/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: authHeaders() });
      setSelectedIds(new Set());
      if (selected && selectedIds.has(selected.id)) { setSelected(null); setDetail(null); }
      await load();
    } finally {
      setDeleting(false);
    }
  };

  const submitManualToken = async () => {
    setManualResult(null);
    if (!manualForm.email) { setError('Email is required'); return; }
    try {
      const r = await axios.post(`${API}/assessment-reviews/manual-token`,
        { ...manualForm, send_email: true },
        { headers: authHeaders() });
      setManualResult(r.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Token generation failed');
    }
  };

  // Trash handlers
  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const r = await axios.get(`${API}/assessment-reviews/trash`, { headers: authHeaders() });
      setTrashItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setTrashItems([]);
      setError(e.response?.data?.detail || 'Failed to load trash');
    } finally {
      setTrashLoading(false);
    }
  }, [authHeaders]);
  const openTrash = async () => { setShowTrash(true); await loadTrash(); };
  const restoreOne = async (id) => {
    try {
      await axios.post(`${API}/assessment-reviews/${id}/restore`, {}, { headers: authHeaders() });
      await Promise.all([load(), loadTrash()]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Restore failed');
    }
  };

  // Card styles (cream/teal palette)
  const cardStyle = { background: '#F7F3EE', borderRadius: 14, padding: '20px 22px', marginBottom: 18, border: '1px solid #D0E4E8' };
  const sectionTitle = { fontFamily: "'Fraunces',serif", color: '#0F4C5C', fontSize: 18, fontWeight: 700, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #D0E4E8' };
  const fieldLabel = { fontSize: 11, fontWeight: 700, color: '#7A8A8E', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 3 };
  const fieldValue = { fontSize: 14, color: '#0A3545', fontWeight: 500, lineHeight: 1.55, whiteSpace: 'pre-wrap' };

  return (
    <div data-testid="admin-assessment-reports" style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, color: '#0F4C5C', margin: 0 }}>Assessment Reports</h1>
          <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>Rs. 4,999 Nutrition Assessment Report submissions</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button data-testid="ar-trash-btn" onClick={openTrash}
            style={{ background: '#fff', color: '#0F4C5C', border: '1px solid #D0E4E8', padding: '10px 16px', borderRadius: 50, fontWeight: 600, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={14} /> Trash
          </button>
          <button data-testid="ar-manual-token-btn" onClick={() => { setManualOpen(true); setManualResult(null); setManualForm({ name:'', email:'' }); }}
            style={{ background: '#0F4C5C', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 50, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            + Generate Form Link
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.6fr)', gap: 20, alignItems: 'start' }}>
        {/* LIST */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <div style={{ padding: 14, borderBottom: '1px solid #E5E7EB', background: '#F9FAFB', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input data-testid="ar-search-input" placeholder="Search name / email / phone" value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 140, padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }} />
            <select data-testid="ar-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 12 }}>
              <option value="">All status</option>
              <option value="pending_review">Pending Review</option>
              <option value="report_ready">Report Ready</option>
              <option value="sent">Report Sent</option>
            </select>
          </div>

          <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F3F4F6', background: '#fff' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" data-testid="ar-select-all" checked={filtered.length > 0 && selectedIds.size === filtered.length} onChange={toggleAll} />
              Select all ({filtered.length})
            </label>
            {selectedIds.size > 0 && (
              <button data-testid="ar-bulk-delete" onClick={bulkDelete} disabled={deleting}
                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {deleting ? 'Deleting...' : `Delete ${selectedIds.size}`}
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>No submissions yet. New bookings will appear here once Razorpay payment is captured and the client submits the intake form.</div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              {filtered.map((r) => {
                const st = STATUS_BADGE[deriveStatus(r)] || STATUS_BADGE.form_sent;
                const isActive = selected?.id === r.id;
                return (
                  <div key={r.id} data-testid={`ar-item-${r.id}`}
                    style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', background: isActive ? '#FDF0ED' : '#fff', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <input type="checkbox" data-testid={`ar-select-${r.id}`} checked={selectedIds.has(r.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(r.id)} style={{ marginTop: 4 }} />
                    <div style={{ flex: 1, minWidth: 0 }} onClick={() => openItem(r)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                        <strong style={{ color: '#0F4C5C', fontSize: 14 }}>{r.full_name || '(unnamed)'}</strong>
                        <span style={{ background: st.bg, color: st.fg, fontSize: 10.5, padding: '2px 8px', borderRadius: 50, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{st.label}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}</div>
                    </div>
                    <button data-testid={`ar-delete-${r.id}`} onClick={(e) => { e.stopPropagation(); deleteOne(r.id); }}
                      style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 4 }} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* DETAIL */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', padding: 22, minHeight: 400 }}>
          {!detail ? (
            <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '60px 20px', fontSize: 14 }}>
              Select a submission to view full intake details, copy data for Claude, upload the report PDF and email it to the client.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, color: '#0F4C5C', margin: 0 }}>{detail.full_name}</h2>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>{detail.email} · {detail.whatsapp}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Payment ID: {detail.payment_id || '(manual)'} · Submitted: {detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : '—'}</div>
                </div>
                <button data-testid="ar-copy-all" onClick={copyAll}
                  style={{ background: copied ? '#7AA095' : '#0F4C5C', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 50, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  {copied ? '✓ Copied' : 'Copy All Data'}
                </button>
              </div>

              {/* Report Management — shared component reused from Diet Reviews */}
              <ReportManagementCard
                detail={detail}
                uploading={uploading}
                sending={sending}
                onUpload={uploadReport}
                onSend={sendReport}
                onRemove={removeReport}
                uploadTestId="ar-upload-label"
                uploadInputTestId="ar-upload-input"
                sendTestId="ar-send-report"
                removeTestId="ar-remove-report-btn"
                sentTestId="ar-sent-timestamp"
                cardTestId="report-management"
              />

              {/* Client-uploaded lab report (from the intake form) */}
              {detail.has_lab_file && (
                <div data-testid="ar-lab-file-card" style={{ background: '#FBF5EA', border: '1px solid #C8A054', borderRadius: 12, padding: '14px 18px', marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, color: '#7A6940' }}>
                    <FileText size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    <strong>Client uploaded a lab report:</strong> {detail.lab_filename || 'lab-report'}
                  </div>
                  <a data-testid="ar-lab-file-download"
                    href={`${API}/assessment-reviews/${detail.id}/lab-file`}
                    onClick={(e) => {
                      // Open download with auth header — use fetch + blob to honour bearer token
                      e.preventDefault();
                      axios.get(`${API}/assessment-reviews/${detail.id}/lab-file`, { headers: authHeaders(), responseType: 'blob' })
                        .then(r => {
                          const blob = r.data;
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = detail.lab_filename || 'lab-report'; a.click();
                          setTimeout(() => window.URL.revokeObjectURL(url), 1500);
                        }).catch(() => setError('Failed to download lab report'));
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0F4C5C', color: '#fff', padding: '8px 16px', borderRadius: 50, fontWeight: 600, fontSize: 12.5, textDecoration: 'none' }}>
                    <Download size={13} /> Download Lab Report
                  </a>
                </div>
              )}

              {/* Sections */}
              {SECTIONS.map((sec) => (
                <div key={sec.title} style={cardStyle} data-testid={`ar-section-${sec.title.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div style={sectionTitle}>{sec.title}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px 24px' }}>
                    {sec.fields.map(([label, key]) => (
                      <div key={key}>
                        <div style={fieldLabel}>{label}</div>
                        <div style={fieldValue}>{formatValue(detail[key])}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Manual token modal */}
      {manualOpen && (
        <div onClick={() => setManualOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,53,69,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, padding: 28, maxWidth: 480, width: '100%' }}>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: '#0F4C5C', marginBottom: 10 }}>Generate Intake Form Link</h3>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 18 }}>Use this when the Razorpay webhook missed an event or you accepted payment outside Razorpay. A 7-day token will be created and the form link emailed to the client.</p>
            {!manualResult ? (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#0F4C5C' }}>Client Name</label>
                <input data-testid="ar-manual-name" value={manualForm.name} onChange={e => setManualForm({ ...manualForm, name: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, margin: '6px 0 14px' }} />
                <label style={{ fontSize: 12, fontWeight: 600, color: '#0F4C5C' }}>Client Email *</label>
                <input data-testid="ar-manual-email" type="email" value={manualForm.email} onChange={e => setManualForm({ ...manualForm, email: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, margin: '6px 0 18px' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button onClick={() => setManualOpen(false)} style={{ background: '#fff', color: '#4B5563', border: '1px solid #D1D5DB', padding: '10px 18px', borderRadius: 50, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button data-testid="ar-manual-submit" onClick={submitManualToken} style={{ background: '#E07A5F', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: 50, fontWeight: 600, cursor: 'pointer' }}>Generate &amp; Email</button>
                </div>
              </>
            ) : (
              <div>
                <div style={{ background: '#DCFCE7', color: '#14532D', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
                  ✓ Token created and email sent to <strong>{manualForm.email}</strong>.
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Form link (valid 7 days):</div>
                <input readOnly value={manualResult.url} style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 12, fontFamily: 'monospace' }} />
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <button onClick={() => setManualOpen(false)} style={{ background: '#0F4C5C', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: 50, fontWeight: 600, cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Trash modal — soft-deleted submissions inside 7-day undo window */}
      {showTrash && (
        <div onClick={() => setShowTrash(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,53,69,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 620, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 22px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Trash2 size={18} style={{ color: '#0F4C5C' }} />
                <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, color: '#0F4C5C', margin: 0 }}>Trash <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({trashItems.length})</span></h3>
              </div>
              <button data-testid="ar-trash-close" onClick={() => setShowTrash(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 6 }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '10px 22px', background: '#FBF5EA', borderBottom: '1px solid #E5E7EB', fontSize: 12, color: '#7A6940' }}>
              Soft-deleted submissions are kept for <strong>7 days</strong>, then automatically purged.
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {trashLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading...</div>
              ) : trashItems.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6B7280', fontSize: 13 }}>Trash is empty.</div>
              ) : (
                trashItems.map((r, i) => (
                  <div key={r.id} data-testid={`ar-trash-row-${r.id}`}
                    style={{ padding: '14px 22px', borderBottom: i < trashItems.length - 1 ? '1px solid #F3F4F6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: '#0F4C5C', fontWeight: 600, fontSize: 14 }}>{r.full_name || '(unnamed)'}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{r.email}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                        Deleted {r.deleted_at ? new Date(r.deleted_at).toLocaleString() : '—'} · purge in {r.purge_in_days ?? '—'} day{(r.purge_in_days === 1) ? '' : 's'}
                      </div>
                    </div>
                    <button data-testid={`ar-trash-restore-${r.id}`} onClick={() => restoreOne(r.id)}
                      style={{ background: '#E07A5F', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 50, fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
                      Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin App ────────────────────────────────────────────────────────────
export default function Admin() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="/" element={<AdminLogin />} />
        <Route path="/dashboard" element={<RequireAuth><AdminLayout><Dashboard /></AdminLayout></RequireAuth>} />
        <Route path="/blogs" element={<RequireAuth><AdminLayout><BlogsManager /></AdminLayout></RequireAuth>} />
        <Route path="/products" element={<RequireAuth><AdminLayout><ProductsManager /></AdminLayout></RequireAuth>} />
        <Route path="/recipes" element={<RequireAuth><AdminLayout><RecipesManager /></AdminLayout></RequireAuth>} />
        <Route path="/clients" element={<RequireAuth><AdminLayout><ClientsManager /></AdminLayout></RequireAuth>} />
        <Route path="/diet-reviews" element={<RequireAuth><AdminLayout><DietReviewsManager /></AdminLayout></RequireAuth>} />
        <Route path="/assessment-reports" element={<RequireAuth><AdminLayout><AssessmentReportsManager /></AdminLayout></RequireAuth>} />
        <Route path="/leads" element={<RequireAuth><AdminLayout><LeadsManager /></AdminLayout></RequireAuth>} />
        <Route path="/testimonials" element={<RequireAuth><AdminLayout><TestimonialsManager /></AdminLayout></RequireAuth>} />
        <Route path="/invoices" element={<RequireAuth><AdminLayout><InvoicesManager /></AdminLayout></RequireAuth>} />
        <Route path="/orders" element={<RequireAuth><AdminLayout><OrdersManager /></AdminLayout></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><AdminLayout><SettingsManager /></AdminLayout></RequireAuth>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}
